import './PsychologistDashboard.css';
import { PublicProfileFields, cleanPublicDetails } from '../components/PublicProfileDetails';
import PsychologistPublicLink from '../components/PsychologistPublicLink';
import { FreeAccessBanner } from '../components/PsychologistAccess';
import { analysisDisplayText } from '../utils/analysisDisplayText';
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import jsPDF from 'jspdf';
import { API, AuthContext } from '../App';
import { getClientPhaseProgress } from '../utils/clientPhaseProgress';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Brain, LogOut, Users, FileText, Lock, MessageSquare, Trash2, Settings, Loader2, AlertCircle, Info, MessageCircle, Eye, Save, CalendarCheck, FolderOpen, ChevronDown, XCircle, Clock, Bell, CheckCircle, Video, BookOpen, Volume2, VolumeX, StopCircle, Download, FileSearch, X, Network, PlusCircle, FolderPlus } from 'lucide-react';
import NotificationModal from '../components/NotificationModal';
import { useNotification } from '../hooks/useNotification';
import Pagination from '../components/Pagination';
import AccountSettings from '../components/AccountSettings';
import ConsentModal from '../components/ConsentModal';
import VideoCallConsentModal from '../components/VideoCallConsentModal';
import JitsiMeetVideoCallComponent from '../components/JitsiMeetVideoCallComponent';
import WebRTCVideoCall from '../components/WebRTCVideoCall';
import DPDPAVideoCallConsent from '../components/DPDPAVideoCallConsent';
import ReportsLibrary from '../components/ReportsLibrary';

import { withDoctorTitle } from '../utils/doctorTitle';
import { subscribeInsightInvalidation } from '../utils/insightInvalidation';
const Phase3ExtraWordsManager = ({ sessions = [] }) => {
  const [words, setWords] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedSessionId, setSelectedSessionId] = useState('');
  const [locked, setLocked] = useState(false);
  const [newWord, setNewWord] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const loadSequence = useRef(0);
  const selectionRef = useRef({ clientId: '', sessionId: '' });

  const clients = Array.from(new Map(
    sessions
      .filter(session => session?.client_id)
      .map(session => [session.client_id, { id: session.client_id, name: session.client_name || 'Unknown Client' }])
  ).values()).sort((a, b) => a.name.localeCompare(b.name));

  const showMsg = (text, type = 'success') => {
    setMessageType(type);
    setMessage(text);
    setTimeout(() => setMessage(''), 4000);
  };

  const handleClientChange = (clientId) => {
    ++loadSequence.current;
    selectionRef.current = { clientId, sessionId: '' };
    setSelectedClientId(clientId);
    setSelectedSessionId('');
    setWords([]);
    setLocked(false);
    setNewWord('');
    setMessage('');
  };

  const handleSessionChange = async (sessionId) => {
    const requestId = ++loadSequence.current;
    selectionRef.current = { clientId: selectedClientId, sessionId };
    setSelectedSessionId(sessionId);
    setWords([]);
    setLocked(false);
    setNewWord('');
    setMessage('');
    if (!sessionId || !selectedClientId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/psychologist/phase3-extra-words`, { params: { client_id: selectedClientId, session_id: sessionId } });
      if (requestId === loadSequence.current) {
        setWords(res.data?.words || []);
        setLocked(Boolean(res.data?.locked));
      }
    } catch (e) {
      if (requestId === loadSequence.current) showMsg(e?.response?.data?.detail || 'Failed to load extra words', 'error');
    } finally {
      if (requestId === loadSequence.current) setLoading(false);
    }
  };

  const saveWords = async (updated) => {
    const clientId = selectedClientId;
    const sessionId = selectedSessionId;
    if (!clientId || !sessionId) {
      showMsg('Select a client and a specific analysis/assessment first.', 'error');
      return;
    }
    try {
      setSaving(true);
      const res = await axios.put(`${API}/psychologist/phase3-extra-words`, { client_id: clientId, session_id: sessionId, words: updated });
      if (selectionRef.current.clientId === clientId && selectionRef.current.sessionId === sessionId) {
        setWords(res.data?.words || []);
        showMsg('Saved successfully', 'success');
      }
    } catch (e) {
      if (selectionRef.current.clientId === clientId && selectionRef.current.sessionId === sessionId) {
        showMsg(e?.response?.data?.detail || 'Failed to save', 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = () => {
    if (!selectedClientId || !selectedSessionId) {
      showMsg('Select a client and a specific analysis/assessment first.', 'error');
      return;
    }
    const trimmed = newWord.trim();
    if (!trimmed) return;
    if (words.some(w => w.toLowerCase() === trimmed.toLowerCase())) {
      showMsg('This word is already in your list', 'error');
      return;
    }
    const updated = [...words, trimmed];
    setNewWord('');
    saveWords(updated);
  };

  const handleRemove = (idx) => {
    const updated = words.filter((_, i) => i !== idx);
    saveWords(updated);
  };

  const selectedClient = clients.find(client => client.id === selectedClientId);
  const clientSessions = sessions.filter(session => session?.client_id === selectedClientId);
  const selectedSession = clientSessions.find(session => session.id === selectedSessionId);
  const sessionLabel = (session) => {
    const rawDate = session.scheduled_at || session.created_at;
    const date = rawDate ? new Date(rawDate).toLocaleDateString() : 'Date unavailable';
    return `${date} — ${session.status || 'unknown status'} — ${String(session.id).slice(0, 8)}`;
  };

  return (
    <Card className="mt-4" data-testid="phase3-extra-words-manager">
      <CardHeader>
        <CardTitle>Add Domain Specific Words</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-900">
          The 20 fixed core Phase 3 words are always used. Additional words apply only to the selected client's specific INSIGHT analysis/assessment and never carry forward to another assessment.
        </div>

        <div className="mb-4">
          <Label htmlFor="phase3-extra-words-client">Select Client:</Label>
          <select
            id="phase3-extra-words-client"
            value={selectedClientId}
            onChange={(e) => handleClientChange(e.target.value)}
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded bg-white text-sm"
            data-testid="phase3-extra-words-client-select"
            disabled={saving}
          >
            <option value="">Select a client</option>
            {clients.map(client => <option key={client.id} value={client.id}>{client.name}</option>)}
          </select>
        </div>

        <div className="mb-4">
          <Label htmlFor="phase3-extra-words-session">Analysis/Assessment:</Label>
          <select
            id="phase3-extra-words-session"
            value={selectedSessionId}
            onChange={(e) => handleSessionChange(e.target.value)}
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded bg-white text-sm"
            data-testid="phase3-extra-words-session-select"
            disabled={saving || !selectedClientId}
          >
            <option value="">Select an analysis/assessment</option>
            {clientSessions.map(session => <option key={session.id} value={session.id}>{sessionLabel(session)}</option>)}
          </select>
        </div>

        {selectedClient && selectedSession ? (
          <div className="mb-3 text-sm font-medium text-gray-800">Additional Phase 3 Words for: {selectedClient.name} — {sessionLabel(selectedSession)}</div>
        ) : (
          <div className="mb-3 text-sm text-amber-700">Select a client and a specific analysis/assessment before assigning words.</div>
        )}
        {locked && <div className="mb-3 text-sm text-blue-800 bg-blue-50 border border-blue-200 rounded p-2">Phase 3 has started. Its administered word set is now a read-only historical snapshot.</div>}

        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={newWord}
            onChange={(e) => setNewWord(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
            placeholder="Enter a new word and press Add"
            className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
            data-testid="phase3-extra-word-input"
            disabled={saving || loading || locked || !selectedSessionId}
          />
          <Button
            onClick={handleAdd}
            disabled={saving || loading || locked || !selectedSessionId || !newWord.trim()}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="phase3-extra-word-add-btn"
          >
            Add Word
          </Button>
        </div>

        {message && (
          <div className={`mb-3 p-2 rounded text-sm ${messageType === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>{message}</div>
        )}

        <div className="border rounded overflow-hidden">
          <table className="w-full text-sm" data-testid="phase3-extra-words-table">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left border-b w-16">#</th>
                <th className="px-3 py-2 text-left border-b">Word</th>
                <th className="px-3 py-2 text-right border-b w-24">Action</th>
              </tr>
            </thead>
            <tbody>
              {(loading || words.length === 0) && (
                <tr><td colSpan={3} className="px-3 py-6 text-center text-gray-500">{loading ? 'Loading…' : selectedSessionId ? 'No extra words yet. Phase 3 will run with the fixed 20 words only.' : 'Select an analysis/assessment to view assigned words.'}</td></tr>
              )}
              {words.map((w, idx) => (
                <tr key={idx} className="border-b last:border-b-0">
                  <td className="px-3 py-2 text-gray-600">{21 + idx}</td>
                  <td className="px-3 py-2 font-medium">{w}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => handleRemove(idx)}
                      disabled={saving || loading || locked || !selectedSessionId}
                      className="text-red-600 hover:text-red-800 text-xs font-medium"
                      data-testid={`phase3-extra-word-remove-${idx}`}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};


const PsychologistDashboard = () => {
  const { notification, showSuccess, showError, showInfo, showWarning, closeNotification } = useNotification();
  const { user, logout } = useContext(AuthContext);
  
  // Helper function to get minimum datetime (current time + 1 hour)
  const getMinDateTime = () => {
    const now = new Date();
    now.setHours(now.getHours() + 1); // Minimum 1 hour from now
    // Round up to nearest minute to avoid timezone issues
    now.setSeconds(0);
    now.setMilliseconds(0);
    return now.toISOString().slice(0, 16); // Format: YYYY-MM-DDTHH:mm
  };
  
  // Consent Modal State
  const [showConsentModal, setShowConsentModal] = useState(false);
  
  const [currentClients, setCurrentClients] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [users, setUsers] = useState([]);
  const [assessmentValidityDays, setAssessmentValidityDays] = useState(90); // Default to 90, will be fetched from admin settings
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackCategory, setFeedbackCategory] = useState('');
  const [feedbackRating, setFeedbackRating] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [questionsPage, setQuestionsPage] = useState(1);
  const [questionsItemsPerPage, setQuestionsItemsPerPage] = useState(5);
  const [unansweredCount, setUnansweredCount] = useState(0);
  const [replyMessage, setReplyMessage] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [replyAttachments, setReplyAttachments] = useState({});
  const [uploadingFile, setUploadingFile] = useState(false);
  const [viewThreadDialog, setViewThreadDialog] = useState(false);
  const [viewingThread, setViewingThread] = useState(null);
  const [showGridView, setShowGridView] = useState(true);
  const [editingClientQuestion, setEditingClientQuestion] = useState(null);
  const [editClientQuestionSubject, setEditClientQuestionSubject] = useState('');
  const [editClientQuestionMessage, setEditClientQuestionMessage] = useState('');
  const [showEditClientQuestionDialog, setShowEditClientQuestionDialog] = useState(false);
  const [updatingClientQuestion, setUpdatingClientQuestion] = useState(false);
  const [showReportSettingsModal, setShowReportSettingsModal] = useState(false);
  const [selectedSessionForSettings, setSelectedSessionForSettings] = useState(null);
  const [reportVisibility, setReportVisibility] = useState({
    show_exploring_client_feelings: true,
    show_emotional_balance_snapshot: true,
    show_strengths_and_coping_guide: true,
    show_deep_dive_inner_world: true,
    show_ai_summary_report: true,
    show_metaphorical_client_portrait: true
  });
  
  // Features - Session Selection
  const [selectedSessionForFeatures, setSelectedSessionForFeatures] = useState(null);
  const [phase5Data, setPhase5Data] = useState(null);
  const [loadingPhase5Data, setLoadingPhase5Data] = useState(false);
  const [phase5SubMenu, setPhase5SubMenu] = useState('custom-reports'); // Sub-menu state
  const [showPhase5Dropdown, setShowPhase5Dropdown] = useState(false); // Dropdown visibility state
  
  // Admin-controlled report visibility. Missing key = enabled.
  const [adminReportVisibility, setAdminReportVisibility] = useState({});
  const fetchAdminReportVisibility = React.useCallback(async () => {
      try {
        const token = sessionStorage.getItem('token');
        const res = await axios.get(`${API}/report-visibility`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAdminReportVisibility(res.data?.report_visibility || {});
      } catch (e) {
        console.warn('Failed to load report visibility, defaulting to all-enabled', e);
        setAdminReportVisibility({});
      }
  }, []);
  useEffect(() => { fetchAdminReportVisibility(); }, [fetchAdminReportVisibility]);
  useEffect(() => subscribeInsightInvalidation((change) => {
    if (change.type === 'report_visibility_changed') fetchAdminReportVisibility();
    if (change.type === 'client_feedback_changed') fetchClientFeedback();
    if (change.sessionId && change.sessionId === String(selectedSessionForFeatures)) fetchPhase5DataForSession(selectedSessionForFeatures);
  }), [selectedSessionForFeatures, fetchAdminReportVisibility]);
  const isReportEnabled = (key) => adminReportVisibility[key] !== false;
  // Reports exposed by the standalone Features dropdown (Phase 5 owns Book/Movie).
  // If ALL of them are disabled, the entire Features button + "Work with Session"
  // button should be hidden — there's nothing left for the psychologist to do there.
  const FEATURE_TAB_KEYS = ['thematic_threads','persistent_patterns'];
  const anyFeatureEnabled = FEATURE_TAB_KEYS.some(k => isReportEnabled(k));
  
  // Custom Reports states
  const [showCustomReportModal, setShowCustomReportModal] = useState(false);
  const [customReportName, setCustomReportName] = useState('');
  const [customReportFocus, setCustomReportFocus] = useState('');
  const [generatingCustomReport, setGeneratingCustomReport] = useState(false);
  const [selectedSessionForCustomReport, setSelectedSessionForCustomReport] = useState(null);
  const [customReports, setCustomReports] = useState([]);
  
  // Ask Buddy states
  const [aiQuery, setAiQuery] = useState('');
  const [queryLoading, setQueryLoading] = useState(false);
  const [queryFile, setQueryFile] = useState(null);
  const [includeFileInPrompt, setIncludeFileInPrompt] = useState(false);
  const [currentQueryPage, setCurrentQueryPage] = useState(1);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const queriesPerPage = 5;
  
  // Book Recommendations states
  const [generatingBooks, setGeneratingBooks] = useState(false);
  const [bookRecommendations, setBookRecommendations] = useState(null);
  const [bookRecsShared, setBookRecsShared] = useState(false);
  const [selectedBookForModal, setSelectedBookForModal] = useState(null);
  const [showBookModal, setShowBookModal] = useState(false);
  
  // Movie Recommendations states
  const [generatingMovies, setGeneratingMovies] = useState(false);
  const [movieRecommendations, setMovieRecommendations] = useState(null);
  const [movieRecsShared, setMovieRecsShared] = useState(false);
  const [selectedMovieForModal, setSelectedMovieForModal] = useState(null);
  const [showMovieModal, setShowMovieModal] = useState(false);
  
  // APA Case Parallels & Research Framework states
  const [generatingReport, setGeneratingReport] = useState(false);
  
  // Thematic Threads Over Time Report states
  const [showThematicThreadsModal, setShowThematicThreadsModal] = useState(false);
  const [thematicThreadsClients, setThematicThreadsClients] = useState([]);
  const [selectedThematicClient, setSelectedThematicClient] = useState('');
  const [selectedTheme, setSelectedTheme] = useState('');
  const [generatingThematicReport, setGeneratingThematicReport] = useState(false);
  const [thematicReportContent, setThematicReportContent] = useState('');
  const [showThematicReportResult, setShowThematicReportResult] = useState(false);

  // Persistent Patterns Spotlight Report states
  const [showPersistentPatternsModal, setShowPersistentPatternsModal] = useState(false);
  const [selectedPersistentPattern, setSelectedPersistentPattern] = useState('');
  const [generatingPersistentReport, setGeneratingPersistentReport] = useState(false);
  const [persistentReportContent, setPersistentReportContent] = useState('');
  const [showPersistentReportResult, setShowPersistentReportResult] = useState(false);
  
  const [myFeedback, setMyFeedback] = useState([]);
  const [loadingMyFeedback, setLoadingMyFeedback] = useState(false);
  const [showFeedbackDetailModal, setShowFeedbackDetailModal] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [showDeleteFeedbackModal, setShowDeleteFeedbackModal] = useState(false);
  const [deletingFeedback, setDeletingFeedback] = useState(false);
  const [feedbackPage, setFeedbackPage] = useState(1);
  const [feedbackItemsPerPage, setFeedbackItemsPerPage] = useState(10);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [feedbackToDelete, setFeedbackToDelete] = useState(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showEndChatModal, setShowEndChatModal] = useState(false);
  const [endingChat, setEndingChat] = useState(false);
  const [chatToEnd, setChatToEnd] = useState(null);


  // Modal notification system (replaces toast messages)
  const [notificationModal, setNotificationModal] = useState({
    show: false,
    type: 'info', // 'success', 'error', 'info'
    message: ''
  });

  const showModalNotification = (type, message) => {
    setNotificationModal({ show: true, type, message });
    
    // Auto-dismiss success and info modals after 3 seconds (errors stay until manually closed)
    if (type === 'success' || type === 'info') {
      setTimeout(() => {
        setNotificationModal(prev => {
          // Only close if it's still the same message (prevent closing wrong modal)
          if (prev.message === message && prev.show) {
            return { show: false, type: 'info', message: '' };
          }
          return prev;
        });
      }, 3000);
    }
  };

  const closeModalNotification = () => {
    setNotificationModal({ show: false, type: 'info', message: '' });
  };

  
  // Settings state
  const [settings, setSettings] = useState(null);
  const [wordsText, setWordsText] = useState('');
  
  // Consultation requests state
  const [consultations, setConsultations] = useState([]);
  const statusRequestSequence = useRef({ sessions: 0, consultations: 0 });
  const dashboardMounted = useRef(true);
  
  // Client history state
  const [showClientHistoryModal, setShowClientHistoryModal] = useState(false);
  const [clientHistory, setClientHistory] = useState([]);
  const [loadingClientHistory, setLoadingClientHistory] = useState(false);
  const [selectedClientForHistory, setSelectedClientForHistory] = useState(null);
  const [loadingConsultations, setLoadingConsultations] = useState(false);
  const [processingConsultation, setProcessingConsultation] = useState(null);
  const [consultationDate, setConsultationDate] = useState('');
  const [showApprovalWarning, setShowApprovalWarning] = useState(false);
  const [pendingApprovalConsultation, setPendingApprovalConsultation] = useState(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleConsultation, setRescheduleConsultation] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelConsultation, setCancelConsultation] = useState(null);
  const [cancellationReason, setCancellationReason] = useState('');
  
  // Pagination for consultations
  const [pendingConsultationsPage, setPendingConsultationsPage] = useState(1);
  const [pendingConsultationsPerPage, setPendingConsultationsPerPage] = useState(5);
  const [scheduledConsultationsPage, setScheduledConsultationsPage] = useState(1);
  const [scheduledConsultationsPerPage, setScheduledConsultationsPerPage] = useState(5);
  const [completedConsultationsPage, setCompletedConsultationsPage] = useState(1);
  const [completedConsultationsPerPage, setCompletedConsultationsPerPage] = useState(5);
  const [activeSessionsPage, setActiveSessionsPage] = useState(1);
  const [activeSessionsPerPage, setActiveSessionsPerPage] = useState(5);
  const [completedSessionsPage, setCompletedSessionsPage] = useState(1);
  const [completedSessionsPerPage, setCompletedSessionsPerPage] = useState(5);
  
  // Video Call state
  const [showVideoCallConsent, setShowVideoCallConsent] = useState(false);
  const [selectedVideoConsultation, setSelectedVideoConsultation] = useState(null);
  const [activeVideoCall, setActiveVideoCall] = useState(null);
  const [initiatingCall, setInitiatingCall] = useState(false);
  const [activeTab, setActiveTab] = useState('consultations');
  const [showVideoConsentModal, setShowVideoConsentModal] = useState(false);
  const [pendingVideoCallConsultation, setPendingVideoCallConsultation] = useState(null);
  // Transcription modal removed


  
  // Account dialog state
  const [showAccount, setShowAccount] = useState(false);
  
  // Client feedback management (admin rights)
  const [clientFeedback, setClientFeedback] = useState([]);
  const [loadingClientFeedback, setLoadingClientFeedback] = useState(false);
  const [selectedClientFeedback, setSelectedClientFeedback] = useState(null);
  const [showClientFeedbackModal, setShowClientFeedbackModal] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyingToFeedback, setReplyingToFeedback] = useState(false);
  const [clientFeedbackPage, setClientFeedbackPage] = useState(1);
  const [clientFeedbackItemsPerPage, setClientFeedbackItemsPerPage] = useState(5);
  
  // Edit message state
  const [editingMessage, setEditingMessage] = useState(null); // {questionId, messageIndex, currentMessage}
  const [editMessageText, setEditMessageText] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  
  // Profile editing state
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileBio, setProfileBio] = useState('');
  const [profileExperience, setProfileExperience] = useState('');
  const [profileQualifications, setProfileQualifications] = useState('');
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [updatingProfile, setUpdatingProfile] = useState(false);
  
  const navigate = useNavigate();

  // Persistent Patterns for Spotlight Analysis
  const persistentPatterns = [
    "Financial Agency vs. Dependency",
    "Autonomy vs. Connection",
    "Self-Worth & External Validation",
    "Career Identity Development",
    "Emotional Regulation Evolution",
    "Attachment & Relationship Patterns",
    "Perfectionism vs. Self-Compassion",
    "Avoidance vs. Engagement",
    "Control & Uncertainty Tolerance",
    "Trust & Vulnerability Capacity",
    "Internal vs. External Locus of Control",
    "Meaning-Making & Purpose",
    "Grief & Loss Processing",
    "Identity Formation & Exploration",
    "Conflict Resolution Maturity",
    "Boundaries & Self-Protection",
    "Achievement Orientation vs. Contentment",
    "Past Orientation vs. Future Focus",
    "Social Comparison & Authenticity",
    "Responsibility & Self-Blame Patterns"
  ];

  // Comprehensive Psychological Themes for Thematic Threads Report
  const psychologicalThemes = [
    "Family Relationships",
    "Romantic Relationships",
    "Friendships & Social Connections",
    "Parent-Child Dynamics",
    "Sibling Relationships",
    "Career & Professional Identity",
    "Academic Performance & Learning",
    "Self-Worth & Self-Esteem",
    "Body Image & Physical Self",
    "Identity & Sense of Self",
    "Purpose & Meaning in Life",
    "Anxiety & Worry Patterns",
    "Depression & Mood",
    "Anger & Frustration",
    "Fear & Phobias",
    "Shame & Guilt",
    "Grief & Loss",
    "Trauma & Past Experiences",
    "Emotional Regulation",
    "Stress & Overwhelm",
    "Coping Mechanisms & Resilience",
    "Avoidance Patterns",
    "Control & Perfectionism",
    "Trust & Vulnerability",
    "Boundaries & Assertiveness",
    "Conflict Resolution",
    "Communication Patterns",
    "Loneliness & Isolation",
    "Belonging & Community",
    "Cultural Identity",
    "Spirituality & Faith",
    "Values & Moral Beliefs",
    "Decision-Making & Choices",
    "Future Orientation & Hope",
    "Past Orientation & Regret",
    "Present Awareness & Mindfulness",
    "Achievement & Success",
    "Failure & Disappointment",
    "Change & Transitions",
    "Stability & Security",
    "Independence & Autonomy",
    "Dependence & Support-Seeking",
    "Power & Authority",
    "Submission & Compliance",
    "Creativity & Self-Expression",
    "Intellectual Curiosity",
    "Financial Concerns",
    "Health & Wellness",
    "Substance Use & Addiction",
    "Sleep & Rest Patterns",
    "Intimacy & Connection",
    "Sexuality & Desire",
    "Authenticity & Masks",
    "Comparison & Competition",
    "Envy & Jealousy",
    "Gratitude & Appreciation",
    "Forgiveness & Resentment",
    "Responsibility & Obligation",
    "Freedom & Constraint",
    "Growth & Stagnation",
    "Safety & Danger",
    "Optimism & Pessimism"
  ];

  useEffect(() => {
    // Check if user has given consent
    if (!user?.consent_accepted) {
      setShowConsentModal(true);
    }
    
    // Only fetch data if psychologist is approved
    if (user?.approval_status === 'approved') {
      fetchSessions();
      fetchUsers();
      fetchQuestions();
      fetchSessions();
      fetchUnansweredCount();
      fetchMyFeedback();
      fetchSettings();
      fetchConsultations();
      fetchClientFeedback();
      fetchUnreadNotificationCount();
      fetchAssessmentValidityDays();
    }
  }, [user?.approval_status]);
  
  // Poll for new notifications every 30 seconds
  useEffect(() => {
    if (user?.approval_status === 'approved') {
      const interval = setInterval(() => {
        fetchUnreadNotificationCount();
        fetchNotifications(); // Fetch actual notifications, not just count
      }, 30000); // 30 seconds
      
      return () => clearInterval(interval);
    }
  }, [user?.approval_status]);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`);
      setUsers(res.data);
    } catch (error) {
      // Silently fail if not admin
      console.log('Could not fetch users');
    }
  };

  const fetchSessions = async ({ background = false } = {}) => {
    const requestId = ++statusRequestSequence.current.sessions;
    try {
      const [res, clients] = await Promise.all([axios.get(`${API}/sessions`), axios.get(`${API}/psychologist/current-clients`)]);
      if (dashboardMounted.current && requestId === statusRequestSequence.current.sessions) {
        setSessions(res.data);
        setCurrentClients(clients.data);
      }
    } catch (error) {
      if (!background) showError('Failed to load sessions');
    }
  };

  const fetchAssessmentValidityDays = async () => {
    try {
      const res = await axios.get(`${API}/assessment-validity-days`);
      if (res.data && res.data.assessment_validity_days) {
        setAssessmentValidityDays(res.data.assessment_validity_days);
        console.log('✅ Analysis validity days loaded:', res.data.assessment_validity_days);
      }
    } catch (error) {
      console.error('Failed to fetch analysis validity days:', error);
      console.log('Using default analysis validity: 90 days');
      // Keep default 90 days if fetch fails
    }
  };

  const fetchConsultations = async ({ background = false } = {}) => {
    const requestId = ++statusRequestSequence.current.consultations;
    if (!background) setLoadingConsultations(true);
    try {
      const res = await axios.get(`${API}/consultations`);
      if (dashboardMounted.current && requestId === statusRequestSequence.current.consultations) {
        setConsultations(res.data);
        console.log('✅ Fetched consultations:', res.data.length);
      }
    } catch (error) {
      console.error('❌ Failed to load consultations:', error);
      if (!background) showError('Failed to load consultations: ' + (error.response?.data?.detail || error.message));
    } finally {
      if (!background && dashboardMounted.current && requestId === statusRequestSequence.current.consultations) {
        setLoadingConsultations(false);
      }
    }
  };

  // Refresh dashboard/session status only; active phase editing data is never read here.
  useEffect(() => {
    dashboardMounted.current = true;
    if (user?.approval_status !== 'approved' || !user?.id || !sessionStorage.getItem('token')) return undefined;

    const refreshDashboardStatus = () => {
      if (!dashboardMounted.current || !sessionStorage.getItem('token')) return;
      fetchSessions({ background: true });
      fetchConsultations({ background: true });
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') refreshDashboardStatus();
    };
    const interval = window.setInterval(refreshDashboardStatus, 12000);
    window.addEventListener('focus', refreshDashboardStatus);
    document.addEventListener('visibilitychange', refreshWhenVisible);

    return () => {
      dashboardMounted.current = false;
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshDashboardStatus);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [user?.id, user?.approval_status]);

  // Fetch client history
  const fetchClientHistory = async (clientId, clientName) => {
    setLoadingClientHistory(true);
    setSelectedClientForHistory(clientName);
    try {
      const res = await axios.get(`${API}/sessions/history/client/${clientId}`);
      
      // Check if there are any historical sessions
      if (res.data.length === 0) {
        showError('No previous sessions found. This is the client\'s first session.');
        setClientHistory([]);
        setLoadingClientHistory(false);
        return;
      }
      
      setClientHistory(res.data);
      setShowClientHistoryModal(true);
      console.log('✅ Fetched client history:', res.data.length, 'sessions');
    } catch (error) {
      console.error('❌ Failed to load client history:', error);
      showError('Failed to load client history: ' + (error.response?.data?.detail || error.message));
      setClientHistory([]);
    } finally {
      setLoadingClientHistory(false);
    }
  };

  // Fetch Phase 5 data for selected session
  const fetchPhase5DataForSession = async (sessionId) => {
    if (!sessionId) return;
    
    setLoadingPhase5Data(true);
    try {
      const res = await axios.get(`${API}/sessions/${sessionId}/phase5`);
      console.log('[DASHBOARD] Phase 5 data loaded:', res.data);
      console.log('[DASHBOARD] Custom reports in response:', res.data.custom_reports);
      console.log('[DASHBOARD] Custom reports count:', res.data.custom_reports?.length || 0);
      
      setPhase5Data(res.data);
      
      // Set individual feature states
      setCustomReports(res.data.custom_reports || []);
      console.log('[DASHBOARD] State updated with custom reports:', res.data.custom_reports?.length || 0);
      setBookRecommendations(res.data.book_recommendations || null);
      setBookRecsShared(res.data.book_recommendations_shared_with_client || false);
      setMovieRecommendations(res.data.movie_recommendations || null);
      setMovieRecsShared(res.data.movie_recommendations_shared_with_client || false);
    } catch (error) {
      console.error('[DASHBOARD] Failed to load Phase 5 data:', error);
      showError('Failed to load Phase 5 data');
      setPhase5Data(null);
    } finally {
      setLoadingPhase5Data(false);
    }
  };

  // Check if selected session is completed (read-only mode)
  // Read-only activates in ONLY THREE specific cases:
  // 1. Psychologist explicitly marks session as complete
  // 2. Psychologist cancels the consultation
  // 3. Client cancels the consultation
  // NOTE: consultation status "completed" (video call done) does NOT trigger read-only
  const isSessionCompleted = () => {
    if (!selectedSessionForFeatures) {
      console.log('[READ-ONLY CHECK] No session selected - returning FALSE');
      return false;
    }
    
    // Check consultation data (from consultations array)
    const consultation = consultations.find(c => c.session_id === selectedSessionForFeatures);
    
    // Check session data (from sessions array)
    const session = sessions.find(s => s.id === selectedSessionForFeatures);
    
    console.log('[READ-ONLY CHECK] ========================================');
    console.log('[READ-ONLY CHECK] Session ID:', selectedSessionForFeatures);
    console.log('[READ-ONLY CHECK] Found consultation:', !!consultation);
    console.log('[READ-ONLY CHECK] Consultation status:', consultation?.status);
    console.log('[READ-ONLY CHECK] Consultation cancelled_by:', consultation?.cancelled_by);
    console.log('[READ-ONLY CHECK] Found session:', !!session);
    console.log('[READ-ONLY CHECK] Session marked_complete:', session?.marked_complete);
    console.log('[READ-ONLY CHECK] Session status:', session?.status);
    
    // Case 1: Psychologist explicitly marked session complete
    // Check both marked_complete flag and status field
    const psychologistMarkedComplete = (
      session?.marked_complete === true || 
      session?.status === 'session_completed' ||
      session?.status === 'marked_complete'
    );
    
    // Case 2 & 3: Consultation was cancelled (by either party)
    // ONLY if status is 'cancelled' - NOT 'completed'
    const isCancelled = consultation?.status === 'cancelled';
    
    console.log('[READ-ONLY CHECK] Psychologist marked complete:', psychologistMarkedComplete);
    console.log('[READ-ONLY CHECK] Is cancelled:', isCancelled);
    console.log('[READ-ONLY CHECK] Final result (show read-only):', psychologistMarkedComplete || isCancelled);
    console.log('[READ-ONLY CHECK] ========================================');
    
    // Return true ONLY if session marked complete OR cancelled
    // consultation.status === 'completed' does NOT trigger this
    return psychologistMarkedComplete || isCancelled;
  };

  // Handler for session selection change
  const handleSessionSelect = async (sessionId) => {
    setSelectedSessionForFeatures(sessionId);
    if (sessionId) {
      await fetchPhase5DataForSession(sessionId);
    } else {
      setPhase5Data(null);
    }
  };

  // Custom Report Handlers
  const handleGenerateCustomReportDashboard = async () => {
    if (!customReportName.trim()) {
      showModalNotification('error', 'Please enter an Analysis name');
      return;
    }
    if (!customReportFocus.trim()) {
      showModalNotification('error', 'Please describe what you want in the Analysis');
      return;
    }
    if (!selectedSessionForFeatures) {
      showModalNotification('error', 'Please select a session first');
      return;
    }

    setGeneratingCustomReport(true);
    
    try {
      await axios.post(
        `${API}/sessions/${selectedSessionForFeatures}/phase5/generate-custom-report`,
        {
          report_name: customReportName.trim(),
          custom_focus: customReportFocus.trim()
        }
      );
      
      console.log('[CUSTOM REPORT] Generation successful, refreshing data...');
      showModalNotification('success', 'Custom analysis generated successfully! Check the AI Generated Analysis section in Phase 5.');
      setCustomReportName('');
      setCustomReportFocus('');
      
      // Refresh Phase 5 data to show new report
      await fetchPhase5DataForSession(selectedSessionForFeatures);
      await fetchSessions(); // Refresh session data to update consultation cards
      
      console.log('[CUSTOM REPORT] Data refreshed. phase5Data should now include new report.');
    } catch (error) {
      console.error('Custom report error:', error);
      showModalNotification('error', error.response?.data?.detail || 'Failed to generate custom analysis');
    } finally {
      setGeneratingCustomReport(false);
    }
  };

  // Ask Buddy Handlers
  const handleAskBuddySubmit = async () => {
    if (!aiQuery.trim()) {
      showModalNotification('error', 'Please enter a question');
      return;
    }
    if (!selectedSessionForFeatures) {
      showModalNotification('error', 'Please select a session first');
      return;
    }
    
    setQueryLoading(true);
    
    try {
      let fileContent = null;
      let fileName = null;
      
      if (queryFile && includeFileInPrompt) {
        const reader = new FileReader();
        fileContent = await new Promise((resolve, reject) => {
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = reject;
          reader.readAsText(queryFile);
        });
        fileName = queryFile.name;
      }
      
      await axios.post(`${API}/sessions/${selectedSessionForFeatures}/phase5/query`, {
        query: aiQuery,
        file_content: fileContent,
        file_name: fileName,
        include_file: includeFileInPrompt
      });
      
      setAiQuery('');
      setQueryFile(null);
      setIncludeFileInPrompt(false);
      showModalNotification('success', 'AI responded to your query! Check the AI Generated Analysis section in Phase 5 to view the response.');
      await fetchPhase5DataForSession(selectedSessionForFeatures);
      await fetchSessions(); // Refresh session data
    } catch (error) {
      showModalNotification('error', error.response?.data?.detail || 'Query failed');
    } finally {
      setQueryLoading(false);
    }
  };

  // Book Recommendations Handlers
  const handleGenerateBookRecommendations = async () => {
    if (!selectedSessionForFeatures) {
      showError('Please select a session first');
      return;
    }
    
    setGeneratingBooks(true);
    try {
      // Generating book recommendations (silent)
      const response = await axios.post(
        `${API}/sessions/${selectedSessionForFeatures}/phase5/generate-book-recommendations`,
        {},
        { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } }
      );
      
      const recsData = response.data.recommendations || response.data;
      setBookRecommendations(recsData);
      showModalNotification('success', 'Book recommendations generated successfully!');
      await fetchPhase5DataForSession(selectedSessionForFeatures);
      await fetchSessions(); // Refresh session data
    } catch (error) {
      console.error('Error generating book recommendations:', error);
      showModalNotification('error', error.response?.data?.detail || 'Failed to generate book recommendations');
    } finally {
      setGeneratingBooks(false);
    }
  };

  const handleToggleShareBooks = async () => {
    if (!selectedSessionForFeatures) return;
    
    const newShareState = !bookRecsShared;
    try {
      await axios.patch(
        `${API}/sessions/${selectedSessionForFeatures}/phase5/book-recommendations/share?share=${newShareState}`,
        {},
        { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } }
      );
      
      setBookRecsShared(newShareState);
      showModalNotification(
        'success', 
        newShareState 
          ? '✅ Book recommendations shared with client successfully!' 
          : '✅ Book recommendations unshared successfully'
      );
    } catch (error) {
      console.error('Failed to update sharing status');
      showModalNotification('error', 'Failed to update book recommendations sharing status');
    }
  };

  // Movie Recommendations Handlers
  const handleGenerateMovieRecommendations = async () => {
    if (!selectedSessionForFeatures) {
      showError('Please select a session first');
      return;
    }
    
    setGeneratingMovies(true);
    try {
      // Generating movie recommendations (silent)
      const response = await axios.post(
        `${API}/sessions/${selectedSessionForFeatures}/phase5/generate-movie-recommendations`,
        {},
        { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } }
      );
      
      const recsData = response.data.recommendations || response.data;
      setMovieRecommendations(recsData);
      showModalNotification('success', 'Movie recommendations generated successfully!');
      await fetchPhase5DataForSession(selectedSessionForFeatures);
      await fetchSessions(); // Refresh session data
    } catch (error) {
      console.error('Error generating movie recommendations:', error);
      showModalNotification('error', error.response?.data?.detail || 'Failed to generate movie recommendations');
    } finally {
      setGeneratingMovies(false);
    }
  };

  const handleToggleShareMovies = async () => {
    if (!selectedSessionForFeatures) return;
    
    const newShareState = !movieRecsShared;
    try {
      await axios.patch(
        `${API}/sessions/${selectedSessionForFeatures}/phase5/movie-recommendations/share?share=${newShareState}`,
        {},
        { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } }
      );
      
      setMovieRecsShared(newShareState);
      showModalNotification(
        'success',
        newShareState 
          ? '✅ Movie recommendations shared with client successfully!' 
          : '✅ Movie recommendations unshared successfully'
      );
    } catch (error) {
      console.error('Failed to update sharing status');
      showModalNotification('error', 'Failed to update movie recommendations sharing status');
    }
  };

  // Ask Buddy Query Handlers
  const handleReviewQuery = async (queryIndex) => {
    if (!selectedSessionForFeatures) return;
    
    try {
      await axios.post(
        `${API}/sessions/${selectedSessionForFeatures}/phase5/queries/${queryIndex}/review`,
        {},
        { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } }
      );
      // Query marked as reviewed (silent)
      await fetchPhase5DataForSession(selectedSessionForFeatures);
    } catch (error) {
      console.error('Failed to mark query as reviewed:', error);
      // Silent error
    }
  };

  const handleReviewCustomReport = async (reportIndex) => {
    if (!selectedSessionForFeatures) return;
    
    try {
      await axios.post(
        `${API}/sessions/${selectedSessionForFeatures}/phase5/custom-reports/${reportIndex}/review`,
        {},
        { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } }
      );
      await fetchPhase5DataForSession(selectedSessionForFeatures);
    } catch (error) {
      console.error('Failed to mark custom report as reviewed:', error);
    }
  };

  const handleDeleteCustomReport = async (reportIndex) => {
    if (!selectedSessionForFeatures) return;

    try {
      const token = sessionStorage.getItem('token');
      await axios.delete(
        `${API}/sessions/${selectedSessionForFeatures}/phase5/custom-reports/${reportIndex}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      await fetchPhase5DataForSession(selectedSessionForFeatures);
    } catch (error) {
      console.error('Failed to delete custom report:', error);
    }
  };

  const handleToggleCustomReportVisibility = async (reportIndex, currentVisibility) => {
    if (!selectedSessionForFeatures) return;

    try {
      const token = sessionStorage.getItem('token');
      await axios.post(
        `${API}/sessions/${selectedSessionForFeatures}/phase5/custom-reports/${reportIndex}/toggle-visibility`,
        { visible_to_client: !currentVisibility },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      await fetchPhase5DataForSession(selectedSessionForFeatures);
    } catch (error) {
      console.error('Failed to toggle report visibility:', error);
    }
  };

  // Thematic Threads Over Time Report Handlers
  const handleOpenThematicThreadsModal = () => {
    // Get the active session's client
    if (!selectedSessionForFeatures) {
      showModalNotification('info', 'Please activate a client session first');
      return;
    }

    // Find the active session to get client info
    const activeSession = sessions.find(s => s.id === selectedSessionForFeatures);
    if (!activeSession) {
      showModalNotification('error', 'Active session not found');
      return;
    }

    // Fetch all completed sessions for this specific client
    const clientSessions = sessions.filter(session => 
      session.client_id === activeSession.client_id && 
      (session.status === 'completed' || session.status === 'expired')
    );

    if (clientSessions.length < 2) {
      showModalNotification('info', `This client needs at least 2 completed sessions for longitudinal analysis. Currently has ${clientSessions.length} completed session(s).`);
      return;
    }
    
    // Pre-select the active session's client
    setSelectedThematicClient(activeSession.client_id);
    setSelectedTheme('');
    setThematicReportContent('');
    setShowThematicReportResult(false);
    setShowThematicThreadsModal(true);
  };

  const handleGenerateThematicThreads = async () => {
    if (!selectedThematicClient || !selectedTheme) {
      showError('Please select both a client and a theme');
      return;
    }

    try {
      setGeneratingThematicReport(true);
      
      const response = await axios.post(
        `${API}/sessions/thematic-threads-report`,
        {
          client_id: selectedThematicClient,
          theme_name: selectedTheme
        },
        {
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setThematicReportContent(response.data.report_content);
      setShowThematicReportResult(true);
      showSuccess(`Thematic Threads Analysis generated successfully! Analyzed ${response.data.sessions_analyzed} sessions.`);
      
    } catch (error) {
      console.error('Failed to generate thematic threads report:', error);
      if (error.response?.data?.detail) {
        showError(error.response.data.detail);
      } else {
        showError('Failed to generate thematic threads Analysis');
      }
    } finally {
      setGeneratingThematicReport(false);
    }
  };

  const handleCloseThematicThreadsModal = () => {
    setShowThematicThreadsModal(false);
    setSelectedThematicClient('');
    setSelectedTheme('');
    setThematicReportContent('');
    setShowThematicReportResult(false);
  };

  // Persistent Patterns Spotlight Handlers
  const handleOpenPersistentPatternsModal = () => {
    if (!selectedSessionForFeatures) {
      showModalNotification('info', 'Please activate a client session first');
      return;
    }

    const activeSession = sessions.find(s => s.id === selectedSessionForFeatures);
    if (!activeSession) {
      showModalNotification('error', 'Active session not found');
      return;
    }

    const clientSessions = sessions.filter(session => 
      session.client_id === activeSession.client_id && 
      (session.status === 'completed' || session.status === 'expired')
    );

    if (clientSessions.length < 2) {
      showModalNotification('info', `This client needs at least 2 completed sessions for pattern analysis. Currently has ${clientSessions.length} completed session(s).`);
      return;
    }
    
    setSelectedPersistentPattern('');
    setPersistentReportContent('');
    setShowPersistentReportResult(false);
    setShowPersistentPatternsModal(true);
  };

  const handleGeneratePersistentPatterns = async () => {
    if (!selectedPersistentPattern) {
      showError('Please select a persistent pattern');
      return;
    }

    const activeSession = sessions.find(s => s.id === selectedSessionForFeatures);
    if (!activeSession) {
      showError('Active session not found');
      return;
    }

    try {
      setGeneratingPersistentReport(true);
      
      const response = await axios.post(
        `${API}/sessions/persistent-patterns-spotlight`,
        {
          client_id: activeSession.client_id,
          pattern_name: selectedPersistentPattern
        },
        {
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setPersistentReportContent(response.data.report_content);
      setShowPersistentReportResult(true);
      showSuccess(`Persistent Patterns Analysis generated successfully! Analyzed ${response.data.sessions_analyzed} sessions.`);
      
    } catch (error) {
      console.error('Failed to generate persistent patterns report:', error);
      if (error.response?.data?.detail) {
        showError(error.response.data.detail);
      } else {
        showError('Failed to generate persistent patterns Analysis');
      }
    } finally {
      setGeneratingPersistentReport(false);
    }
  };

  const handleClosePersistentPatternsModal = () => {
    setShowPersistentPatternsModal(false);
    setSelectedPersistentPattern('');
    setPersistentReportContent('');
    setShowPersistentReportResult(false);
  };

  const handleDownloadPersistentPatternsPDF = async () => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 14.4;
      const maxWidth = pageWidth - 2 * margin;
      let yPosition = 5;

      const clientName = sessions.find(s => s.id === selectedSessionForFeatures)?.client_name || 'Client';
      const patternName = selectedPersistentPattern;

      // Logo
      try {
        const logoImg = new Image();
        logoImg.src = '/insight-logo.png';
        await new Promise((resolve, reject) => {
          logoImg.onload = resolve;
          logoImg.onerror = reject;
          setTimeout(reject, 2000);
        });
        
        const logoWidth = 25;
        const logoHeight = (logoImg.height / logoImg.width) * logoWidth;
        const logoX = pageWidth - logoWidth - margin;
        doc.addImage(logoImg, 'PNG', logoX, yPosition, logoWidth, logoHeight);
        yPosition += logoHeight + 8;
      } catch (logoError) {
        yPosition = margin;
      }

      const checkNewPage = (requiredSpace = 20) => {
        if (yPosition + requiredSpace > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
        }
      };

      const cleanText = (text) => {
        if (!text) return '';
        let cleaned = text;
        cleaned = cleaned.replace(/[\u2014\u2013]/g, '-');
        cleaned = cleaned.replace(/-{3,}/g, ', ');
        cleaned = cleaned.replace(/\)\}/g, '');
        cleaned = cleaned.replace(/(\w)\s+'s\b/g, "$1's");
        cleaned = cleaned.replace(/_/g, '');
        cleaned = cleaned.replace(/[^\w\s.,!?:;'"()\[\]\/\n\-<>\u2265\u2264%&@#$*+=]/g, '');
        cleaned = cleaned.replace(/_/g, '');
        return cleaned.trim();
      };

      const getHeadingLevel = (line) => {
        const trimmedLine = line.trim();
        const words = trimmedLine.split(/\s+/);
        
        if (/^[A-Z][^:]*:$/.test(trimmedLine) && words.length >= 1 && words.length <= 5) {
          return { level: 3, isHeading: true };
        }
        
        if (trimmedLine.includes('═══') || 
            trimmedLine.includes('PERSISTENT PATTERN:') || 
            trimmedLine.includes('PATTERN INTERPRETATION') ||
            trimmedLine.includes('RECOMMENDED CONTINUED FOCUS') ||
            trimmedLine.includes('SESSION') ||
            (trimmedLine.length < 50 && trimmedLine === trimmedLine.toUpperCase() && trimmedLine.length > 3)) {
          return { level: 1, isHeading: true };
        }
        
        const isCapitalized = /^[A-Z]/.test(trimmedLine);
        const noEndPunctuation = !/[.!?;,:]$/.test(trimmedLine);
        const notAllCaps = trimmedLine !== trimmedLine.toUpperCase();
        const hasSomeCapitals = /[A-Z].*[A-Z]/.test(trimmedLine);
        
        if (isCapitalized && noEndPunctuation && notAllCaps && words.length >= 2 && words.length <= 10 && hasSomeCapitals) {
          return { level: 2, isHeading: true };
        }
        
        return { level: 0, isHeading: false };
      };

      // Title
      doc.setFont('times', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(255, 0, 0);
      doc.text('PERSISTENT PATTERNS SPOTLIGHT', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 8;

      // Client and Psychologist info
      doc.setFont('times', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      const psychologistName = user?.name || 'Psychologist';
      doc.text(`Client: ${clientName}`, margin, yPosition);
      yPosition += 6;
      doc.text(`Psychologist: ${psychologistName}`, margin, yPosition);
      yPosition += 6;
      doc.text(`Pattern: ${patternName}`, margin, yPosition);
      yPosition += 6;
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, margin, yPosition);
      yPosition += 10;

      // Process content
      let content = persistentReportContent
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"');

      const rawContent = cleanText(content);
      const contentBlocks = rawContent.split('\n\n');

      contentBlocks.forEach((block) => {
        if (!block.trim()) return;
        
        const lines = block.split('\n').filter(line => line.trim());
        
        lines.forEach((line) => {
          const cleanedLine = line.trim();
          if (!cleanedLine) return;
          
          const headingInfo = getHeadingLevel(cleanedLine);
          
          if (headingInfo.isHeading) {
            checkNewPage(5);
            doc.setFont('times', 'bold');
            
            if (headingInfo.level === 1) {
              doc.setFontSize(11);
              doc.setTextColor(255, 0, 0);
            } else if (headingInfo.level === 2) {
              doc.setFontSize(11);
              doc.setTextColor(0, 128, 0);
            } else if (headingInfo.level === 3) {
              doc.setFontSize(11);
              doc.setTextColor(0, 0, 0);
            }
            
            const headingLines = doc.splitTextToSize(cleanedLine, maxWidth);
            headingLines.forEach(hLine => {
              checkNewPage();
              doc.text(hLine, margin, yPosition, { 
                align: 'justify',
                maxWidth: maxWidth
              });
              yPosition += 5.5;
            });
            
          } else {
            checkNewPage(5);
            doc.setFont('times', 'bold');
            doc.setFontSize(10);
            doc.setTextColor(0, 0, 255);
            
            const paragraphLines = doc.splitTextToSize(cleanedLine, maxWidth);
            paragraphLines.forEach((pLine) => {
              checkNewPage();
              doc.text(pLine, margin, yPosition, { 
                align: 'justify',
                maxWidth: maxWidth
              });
              yPosition += 5.5;
            });
          }
        });
      });

      // Footer
      const totalPages = doc.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.setFont('times', 'normal');
        doc.setTextColor(100, 100, 100);
        
        const pageText = `Page ${i} of ${totalPages}`;
        const reportTitle = `Persistent Patterns: ${patternName}`.substring(0, 40);
        
        doc.text(
          `${reportTitle} | ${pageText}`,
          pageWidth / 2,
          pageHeight - 8,
          { align: 'center' }
        );
      }

      const timestamp = new Date().toISOString().split('T')[0].replace(/-/g, '');
      const cleanPattern = cleanText(patternName).replace(/\s+/g, '_').substring(0, 30);
      const fileName = `Persistent_Patterns_${cleanPattern}_${timestamp}.pdf`;
      doc.save(fileName);
      
      showSuccess('Professional Analysis PDF downloaded successfully!');
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      showError('Failed to generate PDF. Please try again.');
    }
  };

  const handleDownloadThematicThreadsPDF = async () => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 14.4; // 0.2 inches
      const maxWidth = pageWidth - 2 * margin;
      let yPosition = 5;

      // Get client name and theme
      const clientName = sessions.find(s => s.id === selectedSessionForFeatures)?.client_name || 'Client';
      const themeName = selectedTheme;

      // Load and add INSIGHT logo
      try {
        const logoImg = new Image();
        logoImg.src = '/insight-logo.png';
        await new Promise((resolve, reject) => {
          logoImg.onload = resolve;
          logoImg.onerror = reject;
          setTimeout(reject, 2000);
        });
        
        const logoWidth = 25;
        const logoHeight = (logoImg.height / logoImg.width) * logoWidth;
        const logoX = pageWidth - logoWidth - margin;
        doc.addImage(logoImg, 'PNG', logoX, yPosition, logoWidth, logoHeight);
        yPosition += logoHeight + 8;
      } catch (logoError) {
        console.log('Logo not loaded, continuing without logo');
        yPosition = margin;
      }

      const checkNewPage = (requiredSpace = 20) => {
        if (yPosition + requiredSpace > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
        }
      };

      // Helper function to clean text
      const cleanText = (text) => {
        if (!text) return '';
        let cleaned = text;
        cleaned = cleaned.replace(/[\u2014\u2013]/g, '-');
        cleaned = cleaned.replace(/-{3,}/g, ', ');
        cleaned = cleaned.replace(/\)\}/g, '');
        cleaned = cleaned.replace(/(\w)\s+'s\b/g, "$1's");
        cleaned = cleaned.replace(/_/g, '');
        cleaned = cleaned.replace(/[^\w\s.,!?:;'"()\[\]\/\n\-<>\u2265\u2264%&@#$*+=]/g, '');
        cleaned = cleaned.replace(/_/g, '');
        return cleaned.trim();
      };

      // Helper function to detect heading level
      const getHeadingLevel = (line) => {
        const trimmedLine = line.trim();
        const words = trimmedLine.split(/\s+/);
        
        // Level 3: Ends with colon
        if (/^[A-Z][^:]*:$/.test(trimmedLine) && words.length >= 1 && words.length <= 5) {
          return { level: 3, isHeading: true };
        }
        
        // Level 1: Main headings - ALL CAPS or contains "SESSION"
        if (trimmedLine.includes('═══') || 
            trimmedLine.includes('THEME:') || 
            trimmedLine.includes('SESSION') ||
            (trimmedLine.length < 50 && trimmedLine === trimmedLine.toUpperCase() && trimmedLine.length > 3)) {
          return { level: 1, isHeading: true };
        }
        
        // Level 2: Sub-headings
        const isCapitalized = /^[A-Z]/.test(trimmedLine);
        const noEndPunctuation = !/[.!?;,:]$/.test(trimmedLine);
        const notAllCaps = trimmedLine !== trimmedLine.toUpperCase();
        const hasSomeCapitals = /[A-Z].*[A-Z]/.test(trimmedLine);
        
        if (isCapitalized && noEndPunctuation && notAllCaps && words.length >= 2 && words.length <= 10 && hasSomeCapitals) {
          return { level: 2, isHeading: true };
        }
        
        return { level: 0, isHeading: false };
      };

      // Title - RED, BOLD, Times New Roman, Size 14
      doc.setFont('times', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(255, 0, 0);
      doc.text('THEMATIC THREADS OVER TIME', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 8;

      // Client and Psychologist Names - BLACK, BOLD, Size 11
      doc.setFont('times', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      const psychologistName = user?.name || 'Psychologist';
      doc.text(`Client: ${clientName}`, margin, yPosition);
      yPosition += 6;
      doc.text(`Psychologist: ${psychologistName}`, margin, yPosition);
      yPosition += 6;
      doc.text(`Theme: ${themeName}`, margin, yPosition);
      yPosition += 6;
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, margin, yPosition);
      yPosition += 10;

      // Process content - remove HTML tags
      let content = thematicReportContent
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"');

      const rawContent = cleanText(content);
      const contentBlocks = rawContent.split('\n\n');

      // Process content blocks
      contentBlocks.forEach((block) => {
        if (!block.trim()) return;
        
        const lines = block.split('\n').filter(line => line.trim());
        
        lines.forEach((line) => {
          const cleanedLine = line.trim();
          if (!cleanedLine) return;
          
          const headingInfo = getHeadingLevel(cleanedLine);
          
          if (headingInfo.isHeading) {
            checkNewPage(5);
            doc.setFont('times', 'bold');
            
            if (headingInfo.level === 1) {
              // Level 1: Main Heading - RED, 11pt
              doc.setFontSize(11);
              doc.setTextColor(255, 0, 0);
            } else if (headingInfo.level === 2) {
              // Level 2: Sub-heading - GREEN, 11pt
              doc.setFontSize(11);
              doc.setTextColor(0, 128, 0);
            } else if (headingInfo.level === 3) {
              // Level 3: Third-level - BLACK, 11pt
              doc.setFontSize(11);
              doc.setTextColor(0, 0, 0);
            }
            
            const headingLines = doc.splitTextToSize(cleanedLine, maxWidth);
            headingLines.forEach(hLine => {
              checkNewPage();
              doc.text(hLine, margin, yPosition, { 
                align: 'justify',
                maxWidth: maxWidth
              });
              yPosition += 5.5; // Adjusted for 11pt font
            });
            
          } else {
            // PARAGRAPH: BLUE, BOLD, Times New Roman, Size 10
            checkNewPage(5);
            doc.setFont('times', 'bold');
            doc.setFontSize(10);
            doc.setTextColor(0, 0, 255);
            
            const paragraphLines = doc.splitTextToSize(cleanedLine, maxWidth);
            paragraphLines.forEach((pLine) => {
              checkNewPage();
              doc.text(pLine, margin, yPosition, { 
                align: 'justify',
                maxWidth: maxWidth
              });
              yPosition += 5.5; // Adjusted for 10pt font (10pt × 0.55)
            });
          }
        });
      });

      // Footer with page numbers
      const totalPages = doc.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.setFont('times', 'normal');
        doc.setTextColor(100, 100, 100);
        
        const pageText = `Page ${i} of ${totalPages}`;
        const reportTitle = `Thematic Threads: ${themeName}`.substring(0, 40);
        
        doc.text(
          `${reportTitle} | ${pageText}`,
          pageWidth / 2,
          pageHeight - 8,
          { align: 'center' }
        );
      }

      // Save PDF
      const timestamp = new Date().toISOString().split('T')[0].replace(/-/g, '');
      const cleanTheme = cleanText(themeName).replace(/\s+/g, '_').substring(0, 30);
      const fileName = `Thematic_Threads_${cleanTheme}_${timestamp}.pdf`;
      doc.save(fileName);
      
      showSuccess('Professional Analysis PDF downloaded successfully!');
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      showError('Failed to generate PDF. Please try again.');
    }
  };

  const handleDeleteQuery = async (queryIndex) => {
    if (!selectedSessionForFeatures) return;

    try {
      const token = sessionStorage.getItem('token');
      await axios.delete(
        `${API}/sessions/${selectedSessionForFeatures}/phase5/queries/${queryIndex}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      // Query deleted (silent)
      await fetchPhase5DataForSession(selectedSessionForFeatures);
    } catch (error) {
      console.error('Failed to delete query:', error);
      // Silent error
    }
  };

  const downloadQueryAsPDF = (query, response, timestamp) => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text('Ask Buddy Query', 20, 20);
    doc.setFontSize(10);
    doc.text(`Date: ${new Date(timestamp).toLocaleString()}`, 20, 30);
    doc.text('Query:', 20, 40);
    doc.setFontSize(9);
    const queryLines = doc.splitTextToSize(query, 170);
    doc.text(queryLines, 20, 50);
    const responseY = 50 + (queryLines.length * 5) + 10;
    doc.setFontSize(10);
    doc.text('Response:', 20, responseY);
    doc.setFontSize(9);
    const responseLines = doc.splitTextToSize(response, 170);
    doc.text(responseLines, 20, responseY + 10);
    doc.save(`AskBuddy_Query_${new Date(timestamp).toISOString().split('T')[0]}.pdf`);
  };

  const handleConsultationAction = async (consultationId, status, date = null) => {
    // Show warning modal for approval of ANY consultations (TESTING MODE)
    // TODO: Add back condition: consultation.consultation_type === 'follow_up'
    if (status === 'approved') {
      const consultation = consultations.find(c => c.id === consultationId);
      console.log('Consultation found:', consultation);
      console.log('Consultation type:', consultation?.consultation_type);
      if (consultation) {
        console.log('Showing approval warning modal');
        setPendingApprovalConsultation({ consultationId, status, date, consultation });
        setShowApprovalWarning(true);
        return;
      }
    }

    // Show cancel modal for cancellation
    if (status === 'cancelled') {
      const consultation = consultations.find(c => c.id === consultationId);
      setCancelConsultation(consultation);
      setCancellationReason('');
      setShowCancelModal(true);
      return;
    }
    
    // VALIDATION: Check if date is at least 1 hour in the future for scheduling
    if (status === 'scheduled' && date) {
      const selectedDateTime = new Date(date);
      const now = new Date();
      
      if (selectedDateTime <= now) {
        showError('Cannot schedule consultations in the past. Please select a future date and time.');
        return;
      }
      
      const hoursDiff = (selectedDateTime - now) / (1000 * 60 * 60);
      if (hoursDiff < 1) {
        showError('Consultation date must be at least 1 hour from now. Please select a later time.');
        return;
      }
    }
    
    setProcessingConsultation(consultationId);
    try {
      await axios.patch(`${API}/consultations/${consultationId}`, {
        status: status,
        consultation_date: date
      });
      showSuccess(`Consultation ${status} successfully!${status === 'approved' ? ' New session created for client.' : ''}`);
      setConsultationDate('');
      await fetchConsultations();
      await fetchSessions(); // Refresh sessions to show new session
    } catch (error) {
      showError(error.response?.data?.detail || `Failed to ${status} consultation`);
    } finally {
      setProcessingConsultation(null);
    }
  };

  const handleProceedApproval = async () => {
    if (pendingApprovalConsultation) {
      const { consultationId, status, date } = pendingApprovalConsultation;
      setShowApprovalWarning(false);
      setPendingApprovalConsultation(null);
      
      setProcessingConsultation(consultationId);
      try {
        await axios.patch(`${API}/consultations/${consultationId}`, {
          status: status,
          consultation_date: date
        });
        showSuccess(`Consultation ${status} successfully! New session created for client.`);
        setConsultationDate('');
        await fetchConsultations();
        await fetchSessions(); // Refresh sessions to show new session
      } catch (error) {
        showError(error.response?.data?.detail || `Failed to ${status} consultation`);
      } finally {
        setProcessingConsultation(null);
      }
    }
  };

  const handleReschedule = (consultation) => {
    setRescheduleConsultation(consultation);
    setRescheduleDate(consultation.consultation_date || '');
    setRescheduleReason('');
    setShowRescheduleModal(true);
  };

  const handleConfirmReschedule = async () => {
    if (!rescheduleDate) {
      showError('Please select a new date and time');
      return;
    }

    // VALIDATION: Check if date is in the past or less than 1 hour in the future
    const selectedDateTime = new Date(rescheduleDate);
    const now = new Date();
    
    // Validate the selected time (always check, even if same as current)
    if (selectedDateTime <= now) {
      showError('❌ Cannot schedule consultations in the past. Please select a future date and time.');
      return;
    }
    
    const minutesDiff = (selectedDateTime - now) / (1000 * 60);
    const hoursDiff = minutesDiff / 60;
    
    if (hoursDiff < 1) {
      // Provide clear, specific error message
      const minsRemaining = Math.ceil(60 - minutesDiff);
      showError(`⏰ Consultation must be at least 1 hour from now. Selected time is only ${Math.floor(minutesDiff)} minutes away. Please select a time at least ${minsRemaining} minutes later.`);
      return;
    }

    setProcessingConsultation(rescheduleConsultation.id);
    try {
      // Convert to ISO format with timezone for backend
      const isoDateTime = selectedDateTime.toISOString();
      
      // Update consultation with new date
      await axios.patch(`${API}/consultations/${rescheduleConsultation.id}`, {
        status: 'scheduled',
        consultation_date: isoDateTime
      });
      
      // Send notification to client via Q&A system
      const newDate = new Date(rescheduleDate);
      const dateStr = newDate.toLocaleDateString();
      const timeStr = newDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      
      const notificationMessage = `📅 CONSULTATION RESCHEDULED\n\nYour consultation has been rescheduled.\n\nNew Date: ${dateStr}\nNew Time: ${timeStr}\n\n${rescheduleReason ? `Reason: ${rescheduleReason}\n\n` : ''}Please confirm your availability or contact me if this time doesn't work for you.`;
      
      try {
        await axios.post(`${API}/psychologist/questions?client_id=${rescheduleConsultation.client_id}&session_id=${rescheduleConsultation.session_id}&question_type=session_specific&subject=${encodeURIComponent(`📅 Consultation Rescheduled to ${dateStr} at ${timeStr}`)}&message=${encodeURIComponent(notificationMessage)}`);
      } catch (notifError) {
        console.error('Failed to send notification:', notifError);
        // Continue even if notification fails
      }
      
      showSuccess(`✅ Consultation rescheduled successfully! Client has been notified.`);
      setShowRescheduleModal(false);
      setRescheduleConsultation(null);
      setRescheduleDate('');
      setRescheduleReason('');
      await fetchConsultations();
    } catch (error) {
      // Display detailed error message from backend
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to reschedule consultation';
      console.error('Reschedule error:', error.response?.data || error);
      showError(`❌ ${errorMessage}`);
    } finally {
      setProcessingConsultation(null);
    }
  };

  const handleConfirmCancellation = async () => {
    if (!cancellationReason.trim()) {
      showError('Please provide a reason for cancellation');
      return;
    }

    if (!cancelConsultation || !cancelConsultation.id) {
      showError('Invalid consultation data. Please refresh the page and try again.');
      return;
    }

    setProcessingConsultation(cancelConsultation.id);
    try {
      console.log('Cancelling consultation:', cancelConsultation.id);
      
      // Update consultation status to cancelled with reason
      const response = await axios.patch(`${API}/consultations/${cancelConsultation.id}`, {
        status: 'cancelled',
        cancellation_reason: cancellationReason
      });
      
      console.log('Consultation cancelled successfully:', response.data);
      
      // Send notification to client
      const notificationMessage = `❌ CONSULTATION CANCELLED\n\nYour scheduled consultation has been cancelled by your psychologist.\n\nReason: ${cancellationReason}\n\nPlease feel free to book another appointment or contact your psychologist for further assistance.`;
      
      try {
        await axios.post(`${API}/psychologist/questions?client_id=${cancelConsultation.client_id}&session_id=${cancelConsultation.session_id}&question_type=session_specific&subject=${encodeURIComponent('❌ Consultation Cancelled')}&message=${encodeURIComponent(notificationMessage)}`);
        console.log('Notification sent to client');
      } catch (notifError) {
        console.error('Failed to send notification:', notifError);
        // Don't fail the whole operation if notification fails
      }
      
      showSuccess('Consultation cancelled. Client has been notified.');
      setShowCancelModal(false);
      setCancelConsultation(null);
      setCancellationReason('');
      await fetchConsultations();
    } catch (error) {
      console.error('Error cancelling consultation:', error);
      console.error('Error details:', error.response);
      
      let errorMsg = 'Failed to cancel consultation';
      
      if (error.response) {
        // Server responded with error
        if (error.response.status === 404) {
          errorMsg = 'Consultation not found. It may have been already cancelled or removed. Please refresh the page.';
        } else if (error.response.status === 403) {
          errorMsg = 'You do not have permission to cancel this consultation.';
        } else {
          errorMsg = error.response.data?.detail || `Server error: ${error.response.status}`;
        }
      } else if (error.request) {
        // Request made but no response
        errorMsg = 'Network error: Unable to reach the server. Please check your internet connection.';
      } else {
        // Something else happened
        errorMsg = error.message || 'An unexpected error occurred';
      }
      
      showError(errorMsg);
    } finally {
      setProcessingConsultation(null);
    }
  };

  // Video Call Handlers
  const handleStartVideoCall = async (consultation) => {
    console.log('🎥 [handleStartVideoCall] Called with consultation:', consultation?.id);
    console.log('🔐 [handleStartVideoCall] User video consent:', user?.video_consent_accepted);
    
    // Check if user has given video call consent
    if (!user?.video_consent_accepted) {
      console.log('⚠️ [handleStartVideoCall] No consent, showing modal');
      // Show consent modal first
      setPendingVideoCallConsultation(consultation);
      setShowVideoConsentModal(true);
      return;
    }

    console.log('✅ [handleStartVideoCall] User has consent, calling startVideoCallDirectly');
    // User has already consented, proceed with call
    startVideoCallDirectly(consultation);
  };

  const startVideoCallDirectly = async (consultation) => {
    console.log('📞 [startVideoCallDirectly] Starting call for consultation:', consultation?.id);
    setSelectedVideoConsultation(consultation);
    setInitiatingCall(true);
    
    try {
      console.log('📡 [startVideoCallDirectly] Sending POST to /video-calls/initiate');
      // Start video call (transcription removed)
      const response = await axios.post(`${API}/video-calls/initiate`, {
        consultation_id: consultation.id
      });

      console.log('✅ [startVideoCallDirectly] Video call response:', response.data);
      showSuccess('Video call started! Switching to Live Call tab...');
      setActiveVideoCall(response.data);
      setActiveTab('video-call'); // Switch to video call tab
      console.log('✅ [startVideoCallDirectly] activeVideoCall state set and tab switched');
    } catch (error) {
      console.error('❌ [startVideoCallDirectly] Video call error:', error);
      console.error('❌ [startVideoCallDirectly] Error response:', error.response?.data);
      // Extract proper error message from various error formats
      let errorMsg = 'Failed to start video call';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Handle Pydantic validation errors (array of error objects)
        if (Array.isArray(detail)) {
          errorMsg = detail.map(err => err.msg || err).join(', ');
        } else if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (detail.msg) {
          errorMsg = detail.msg;
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      showError(errorMsg);
    } finally {
      setInitiatingCall(false);
    }
  };

  const handleVideoConsentAccept = async () => {
    try {
      // Save consent to backend
      await axios.patch(`${API}/users/video-call-consent`);
      
      // Update local user state
      const updatedUser = { ...user, video_consent_accepted: true };
      sessionStorage.setItem('user', JSON.stringify(updatedUser));
      
      showSuccess('Video call consent saved successfully');
      setShowVideoConsentModal(false);
      
      // Now proceed with the video call directly
      if (pendingVideoCallConsultation) {
        startVideoCallDirectly(pendingVideoCallConsultation);
        setPendingVideoCallConsultation(null);
      }
    } catch (error) {
      console.error('Error saving video consent:', error);
      showError('Failed to save consent. Please try again.');
    }
  };

  const handleVideoConsentDecline = () => {
    setShowVideoConsentModal(false);
    setPendingVideoCallConsultation(null);
    showSuccess('You can schedule alternative consultation methods');
  };

  const handleCallEnd = async (remoteEnded = false) => {
    console.log('📞 PsychologistDashboard: handleCallEnd called, remoteEnded:', remoteEnded);
    
    if (activeVideoCall && !remoteEnded) {
      // Only call the backend and show success if we ended the call
      try {
        await axios.post(`${API}/video-calls/${activeVideoCall.id}/end`);
        showSuccess('Video call ended successfully');
      } catch (error) {
        console.error('Error ending call:', error);
      }
    }
    
    // CRITICAL: Wait for cleanup to complete BEFORE unmounting
    // Increased to 3 seconds to ensure camera fully releases
    console.log('⏳ Waiting 3 seconds for camera cleanup to complete...');
    console.log('⏳ DO NOT REFRESH - Camera is being released...');
    await new Promise(resolve => setTimeout(resolve, 3000));
    console.log('✅ Cleanup wait complete, unmounting video call component');
    
    setActiveVideoCall(null);
    setSelectedVideoConsultation(null);
    setActiveTab('consultations'); // Switch back to consultations tab
    
    // Wait a bit more before refreshing to avoid race condition
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Refresh consultations to update status if needed
    fetchConsultations();
  };

  const canStartVideoCall = (consultation) => {
    // Allow video call for both scheduled and approved consultations
    if (consultation.status !== 'scheduled' && consultation.status !== 'approved') return false;
    if (!consultation.consultation_date) return false;
    
    // Video call can be started 30 minutes before the scheduled time OR after scheduled time has passed
    const appointmentDateTime = new Date(consultation.consultation_date);
    const now = new Date();
    const minutesUntilAppointment = (appointmentDateTime - now) / 1000 / 60;
    
    // Allow starting video call 30 minutes before appointment time OR anytime after scheduled time
    return minutesUntilAppointment <= 30;
  };

  const getTimeUntilVideoCall = (consultation) => {
    if (!consultation.consultation_date) return null;
    
    const appointmentDateTime = new Date(consultation.consultation_date);
    const now = new Date();
    const minutesUntilAppointment = Math.ceil((appointmentDateTime - now) / 1000 / 60);
    
    if (minutesUntilAppointment <= 0) return 'Available now';
    if (minutesUntilAppointment <= 30) return 'Available now';
    
    const hours = Math.floor(minutesUntilAppointment / 60);
    const minutes = minutesUntilAppointment % 60;
    
    if (hours > 0) {
      return `Available in ${hours}h ${minutes}m`;
    }
    return `Available in ${minutes}m`;
  };

  const fetchClientFeedback = async () => {
    setLoadingClientFeedback(true);
    try {
      const res = await axios.get(`${API}/admin/feedback`);
      setClientFeedback(res.data);
    } catch (error) {
      console.error('Failed to load client feedback:', error);
    } finally {
      setLoadingClientFeedback(false);
    }
  };

  const handleReplyToClientFeedback = async (e) => {
    e.preventDefault();
    if (!selectedClientFeedback || !replyText.trim()) return;
    
    setReplyingToFeedback(true);
    try {
      await axios.post(`${API}/admin/feedback/${selectedClientFeedback.id}/reply`, {
        admin_reply: replyText
      });
      showSuccess('Reply sent successfully!');
      setReplyText('');
      setShowClientFeedbackModal(false);
      setSelectedClientFeedback(null);
      await fetchClientFeedback();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to send reply');
    } finally {
      setReplyingToFeedback(false);
    }
  };

  const handleDeleteClientFeedback = async (feedbackId) => {
    setFeedbackToDelete(feedbackId);
    setShowDeleteConfirmModal(true);
  };
  
  const confirmDeleteFeedback = async () => {
    if (!feedbackToDelete) return;
    
    try {
      await axios.delete(`${API}/admin/feedback/${feedbackToDelete}`);
      showSuccess('Feedback deleted successfully!');
      await fetchClientFeedback();
      setShowDeleteConfirmModal(false);
      setFeedbackToDelete(null);
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to delete feedback');
    }
  };

  const handleEndChat = async () => {
    if (!chatToEnd) return;
    
    setEndingChat(true);
    try {
      await axios.patch(`${API}/questions/${chatToEnd}/end-chat`);
      showSuccess('Chat ended successfully');
      setShowEndChatModal(false);
      setChatToEnd(null);
      await fetchQuestions();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to end chat');
    } finally {
      setEndingChat(false);
    }
  };

  // const fetchAnalytics = async () => {
  //   setLoadingAnalytics(true);
  //   try {
  //     const res = await axios.get(`${API}/psychologist/analytics`);
  //     setAnalytics(res.data);
  //   } catch (error) {
  //     showError('Failed to load analytics');
  //   } finally {
  //     setLoadingAnalytics(false);
  //   }
  // };

  const assignToMe = async (sessionId) => {
    try {
      await axios.patch(`${API}/sessions/${sessionId}/assign?psychologist_id=${user.id}`);
      showSuccess('Session assigned to you');
      fetchSessions();
    } catch (error) {
      showError('Failed to assign session');
    }
  };

  const activeSessions = sessions.filter(s => s.status !== 'completed');
  const completedSessions = sessions.filter(s => s.status === 'completed');
  const unassignedSessions = []; // Psychologists don't see unassigned sessions anymore

  const handleMarkSessionComplete = async (sessionId) => {
    if (!window.confirm('Mark this session as complete? This will activate 3-month analysis validity for the client.')) {
      return;
    }
    
    try {
      await axios.patch(`${API}/sessions/${sessionId}/complete`);
      showSuccess('Session marked as complete! Client can now reuse this analysis for 3 months.');
      await fetchSessions();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to mark session complete');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      showError('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      showError('Password must be at least 6 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.put(`${API}/users/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      showSuccess('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSubmitFeedback = async (e) => {
    e.preventDefault();
    
    if (!feedbackCategory) {
      showError('Please select a category');
      return;
    }
    
    if (!feedbackMessage.trim()) {
      showError('Please enter your feedback');
      return;
    }
    
    setSubmittingFeedback(true);
    try {
      await axios.post(`${API}/feedback`, {
        category: feedbackCategory,
        message: feedbackMessage,
        rating: feedbackRating ? parseInt(feedbackRating) : null,
        recipient_type: 'admin'
      });
      showSuccess('Feedback submitted successfully!');
      setShowFeedback(false);
      setFeedbackCategory('');
      setFeedbackRating('');
      setFeedbackMessage('');
    } catch (error) {
      showError('Failed to submit feedback');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  // Notification functions
  const fetchNotifications = async () => {
    setLoadingNotifications(true);
    try {
      const res = await axios.get(`${API}/notifications`);
      setNotifications(res.data);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const fetchUnreadNotificationCount = async () => {
    try {
      const res = await axios.get(`${API}/notifications/unread-count`);
      setUnreadNotificationCount(res.data.count);
    } catch (error) {
      console.error('Failed to load unread notification count:', error);
    }
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      await axios.patch(`${API}/notifications/${notificationId}/read`);
      fetchUnreadNotificationCount();
      fetchNotifications();
    } catch (error) {
      showError('Failed to mark notification as read');
    }
  };

  const markAllNotificationsAsRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`);
      fetchUnreadNotificationCount();
      fetchNotifications();
      showSuccess('All notifications marked as read');
    } catch (error) {
      showError('Failed to mark all notifications as read');
    }
  };

  const handleNotificationClick = (notif) => {
    // Mark as read
    if (!notif.read) {
      markNotificationAsRead(notif.id);
    }
    
    // Navigate to relevant tab based on notification type
    if (notif.type === 'consultation_request') {
      setShowNotificationsModal(false);
      // Switch to consultations tab
      document.querySelector('[data-testid="tab-consultations"]')?.click();
    }
  };

  // Profile editing functions
  const [publicDetails, setPublicDetails] = useState({});
  const [profileSpecialization, setProfileSpecialization] = useState('');
  const handleEditProfile = () => {
    setPublicDetails(user?.proforma?.public_details || {});
    setProfileSpecialization(user?.proforma?.specialization || '');
    setEditingProfile(true);
    setProfileBio(user?.proforma?.bio || '');
    setProfileExperience(user?.proforma?.experience_years || '');
    setProfileQualifications(user?.proforma?.qualifications || '');
    setProfilePhotoPreview(user?.proforma?.photo_url || null);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setProfilePhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    
    setUpdatingProfile(true);
    try {
      await axios.put(`${API}/psychologist/profile`, {
        bio: profileBio, qualifications: profileQualifications,
        experience_years: profileExperience === '' ? null : Number(profileExperience),
        specialization: profileSpecialization, public_details: cleanPublicDetails(publicDetails),
      });
      
      // Use FormData endpoint for photo if present
      if (profilePhoto) {
        const formData = new FormData();
        formData.append('photo', profilePhoto);
        await axios.patch(`${API}/psychologist/profile`, formData);
      }
      
      // Update user context with new data
      const token = sessionStorage.getItem('token');
      if (token) {
        const meRes = await axios.get(`${API}/auth/me`);
        // Update the user in context (you might need to add a method to AuthContext for this)
        window.location.reload(); // Simple reload for now
      }
      
      showSuccess('Profile updated successfully!');
      setEditingProfile(false);
      setProfilePhoto(null);
    } catch (error) {
      console.error('Profile update error:', error.response || error);
      const errorMsg = error.response?.data?.detail || 'Failed to update profile';
      showError(errorMsg);
    } finally {
      setUpdatingProfile(false);
    }
  };

  // Q&A Functions

  // File Upload Function
  const handleFileUpload = async (file) => {
    if (!file) return null;
    
    if (file.size > 10 * 1024 * 1024) {
      showError('File too large. Maximum size is 10MB');
      return null;
    }
    
    setUploadingFile(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      console.log('Uploading file:', file.name, 'Size:', file.size);
      const res = await axios.post(`${API}/questions/upload-attachment`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      console.log('Upload success:', res.data);
      showSuccess(`File "${file.name}" uploaded successfully`);
      return res.data;
    } catch (error) {
      console.error('Upload error:', error);
      showError(`Failed to upload file: ${error.response?.data?.detail || error.message}`);
      return null;
    } finally {
      setUploadingFile(false);
    }
  };

  // Q&A Functions
  const fetchQuestions = async () => {
    setLoadingQuestions(true);
    try {
      const res = await axios.get(`${API}/psychologist/questions`);
      setQuestions(res.data);
    } catch (error) {
      showError('Failed to load questions');
    } finally {
      setLoadingQuestions(false);
    }
  };

  const fetchUnansweredCount = async () => {
    try {
      const res = await axios.get(`${API}/psychologist/questions/count`);
      setUnansweredCount(res.data.unanswered_count);
    } catch (error) {
      console.log('Could not fetch unanswered count');
    }
  };

  const fetchMyFeedback = async () => {
    setLoadingMyFeedback(true);
    try {
      const res = await axios.get(`${API}/my-feedback`);
      setMyFeedback(res.data);
    } catch (error) {
      console.log('Failed to load feedback');
    } finally {
      setLoadingMyFeedback(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSettings(res.data);
      setWordsText(res.data.phase3_words?.join(', ') || '');
    } catch (error) {
      console.log('Failed to load settings');
    }
  };

  const updateSettings = async (updates) => {
    try {
      await axios.patch(`${API}/settings`, updates);
      showSuccess('Settings updated');
      fetchSettings();
    } catch (error) {
      showError('Failed to update settings');
    }
  };

  const handleOpenFeedback = (feedback) => {
    setSelectedFeedback(feedback);
    setShowFeedbackDetailModal(true);
  };

  const handleDeleteFeedbackClick = (feedback) => {
    setSelectedFeedback(feedback);
    setShowDeleteFeedbackModal(true);
  };

  const handleConfirmDeleteFeedback = async () => {
    if (!selectedFeedback) return;
    
    setDeletingFeedback(true);
    try {
      await axios.delete(`${API}/my-feedback/${selectedFeedback.id}`);
      showSuccess('Feedback deleted successfully');
      setShowDeleteFeedbackModal(false);
      setSelectedFeedback(null);
      fetchMyFeedback(); // Refresh the list
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to delete feedback');
    } finally {
      setDeletingFeedback(false);
    }
  };

  // Group sessions by client
  const groupSessionsByClient = (sessions) => {
    const grouped = {};
    sessions.forEach(session => {
      const clientId = session.client_id;
      const clientName = session.client_name || 'Unknown Client';
      
      if (!grouped[clientId]) {
        grouped[clientId] = {
          clientId,
          clientName,
          sessions: []
        };
      }
      grouped[clientId].sessions.push(session);
    });
    
    // Sort sessions within each client by date (newest first)
    Object.values(grouped).forEach(client => {
      client.sessions.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    });
    
    return Object.values(grouped);
  };
  const handleReplyToQuestion = async (questionId) => {
    if (!replyMessage.trim()) {
      showError('Please enter your reply');
      return;
    }
    
    setSubmittingReply(true);
    try {
      await axios.post(`${API}/psychologist/questions/${questionId}/reply`, {
        message: replyMessage,
        attachments: (replyAttachments[questionId] || []).map(a => a.file_url)
      });
      showSuccess('Reply sent successfully!');
      setReplyMessage('');
      setReplyAttachments(prev => ({ ...prev, [questionId]: [] }));
      fetchQuestions();
      fetchUnansweredCount();
    fetchMyFeedback();
      setSelectedQuestion(null);
    } catch (error) {
      showError('Failed to send reply');
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleCloseQuestion = async (questionId) => {
    try {
      await axios.patch(`${API}/psychologist/questions/${questionId}/close`);
      showSuccess('Chat closed successfully');
      fetchQuestions();
      fetchUnansweredCount();
    fetchMyFeedback();
      setSelectedQuestion(null);
    } catch (error) {
      showError('Failed to close chat');
    }
  };

  const handleEditMessage = (questionId, messageIndex, currentMessage) => {
    setEditingMessage({ questionId, messageIndex, currentMessage });
    setEditMessageText(currentMessage);
  };

  const handleSaveEdit = async () => {
    if (!editMessageText.trim()) {
      showError('Message cannot be empty');
      return;
    }
    
    setSavingEdit(true);
    try {
      await axios.patch(
        `${API}/psychologist/questions/${editingMessage.questionId}/messages/${editingMessage.messageIndex}`,
        {
          message: editMessageText,
          attachments: []
        }
      );
      showSuccess('Message edited successfully!');
      setEditingMessage(null);
      setEditMessageText('');
      await fetchQuestions();
      
      // Refresh the viewing thread if it's open
      if (viewingThread && viewingThread.id === editingMessage.questionId) {
        const updatedQuestion = await axios.get(`${API}/psychologist/questions/${editingMessage.questionId}`);
        setViewingThread(updatedQuestion.data);
      }
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to edit message');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingMessage(null);
    setEditMessageText('');
  };

  const downloadQaPdf = async (questionId) => {
    try {
      const response = await axios.get(`${API}/psychologist/questions/${questionId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `client_question_${questionId.substring(0, 8)}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSuccess('Question downloaded');
    } catch (error) {
      console.error('Download error:', error);
      // Handle blob response errors properly
      if (error.response && error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          showError(errorData.detail || 'Failed to download question');
        } catch (e) {
          showError('Failed to download question');
        }
      } else {
        showError(error.response?.data?.detail || 'Failed to download question');
      }
    }
  };

  const deleteQuestion = async (questionId) => {
    try {
      await axios.delete(`${API}/psychologist/questions/${questionId}`);
      showSuccess('Question deleted successfully');
      fetchQuestions();
      fetchUnansweredCount();
    fetchMyFeedback();
    } catch (error) {
      showError('Failed to delete question');
    }
  };

  const handleEditClientQuestion = (question) => {
    setEditingClientQuestion(question);
    setEditClientQuestionSubject(question.subject);
    // Get the first message (initial question from client)
    setEditClientQuestionMessage(question.messages?.[0]?.message || '');
    setShowEditClientQuestionDialog(true);
  };

  const handleUpdateClientQuestion = async (e) => {
    e.preventDefault();
    if (!editClientQuestionSubject.trim() || !editClientQuestionMessage.trim()) {
      showError('Subject and message are required');
      return;
    }

    setUpdatingClientQuestion(true);
    try {
      await axios.patch(`${API}/psychologist/questions/${editingClientQuestion.id}?subject=${encodeURIComponent(editClientQuestionSubject)}&message=${encodeURIComponent(editClientQuestionMessage)}`);
      showSuccess('Question updated successfully');
      setShowEditClientQuestionDialog(false);
      setEditingClientQuestion(null);
      setEditClientQuestionSubject('');
      setEditClientQuestionMessage('');
      fetchQuestions();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to update question');
    } finally {
      setUpdatingClientQuestion(false);
    }
  };

  const downloadAttachment = async (attachmentUrl) => {
    try {
      // Validate attachment URL
      if (!attachmentUrl || typeof attachmentUrl !== 'string') {
        console.error('Invalid attachment URL:', attachmentUrl);
        showError('Invalid attachment');
        return;
      }

      const filename = attachmentUrl.split('/').pop();
      if (!filename) {
        showError('Invalid attachment filename');
        return;
      }

      console.log('Downloading attachment:', attachmentUrl);
      
      // Remove /api prefix from attachmentUrl if it exists, since API already includes it
      const cleanUrl = attachmentUrl.startsWith('/api/') ? attachmentUrl.substring(4) : attachmentUrl;
      
      const response = await axios.get(`${API}${cleanUrl}`, {
        responseType: 'blob'
      });
      
      console.log('Download response:', response.status, response.headers['content-type']);
      
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      showSuccess('Attachment downloaded');
    } catch (error) {
      console.error('Attachment download error:', error);
      // Handle blob response errors properly
      if (error.response && error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          showError(errorData.detail || 'Failed to download attachment');
        } catch (e) {
          showError('Failed to download attachment');
        }
      } else {
        showError(error.response?.data?.detail || 'Failed to download attachment');
      }
      
      if (error.response?.status === 404) {
        showError('Attachment not found');
      } else if (error.response?.status === 403) {
        showError('Not authorized to download attachment');
      } else {
        showError('Failed to download attachment');
      }
    }
  };

  const openCustomReportModal = (session) => {
    setSelectedSessionForCustomReport(session);
    setShowCustomReportModal(true);
  };

  const handleGenerateCustomReport = async () => {
    if (!customReportName.trim()) {
      showError('Please enter an Analysis name');
      return;
    }
    if (!customReportFocus.trim()) {
      showError('Please describe what you want in the Analysis');
      return;
    }

    setGeneratingCustomReport(true);
    showInfo('Generating comprehensive custom analysis... This may take 1-2 minutes.');
    
    try {
      const response = await axios.post(
        `${API}/sessions/${selectedSessionForCustomReport.id}/phase5/generate-custom-report`,
        {
          report_name: customReportName.trim(),
          custom_focus: customReportFocus.trim()
        }
      );
      
      showSuccess('Custom analysis generated with risk analysis, disclaimer, and APA citations!');
      setShowCustomReportModal(false);
      setCustomReportName('');
      setCustomReportFocus('');
      setSelectedSessionForCustomReport(null);
      fetchSessions(); // Refresh to show new visibility settings
    } catch (error) {
      // Show detailed error in a modal
      console.error('Custom report error:', error);
      let errorDetail = 'Failed to generate custom analysis. Please try again.';
      
      // Handle different error formats
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorDetail = error.response.data;
        } else if (error.response.data.detail) {
          // If detail is an object, stringify it
          if (typeof error.response.data.detail === 'object') {
            errorDetail = JSON.stringify(error.response.data.detail, null, 2);
          } else {
            errorDetail = error.response.data.detail;
          }
        } else if (error.response.data.message) {
          errorDetail = error.response.data.message;
        }
      } else if (error.message) {
        errorDetail = error.message;
      }
      
      setErrorMessage(String(errorDetail)); // Ensure it's always a string
      setShowErrorModal(true);
      showError('Custom analysis generation failed');
    } finally {
      setGeneratingCustomReport(false);
    }
  };

  const openReportSettings = async (session) => {
    setSelectedSessionForSettings(session);
    
    // Fetch custom reports for this session
    try {
      const response = await axios.get(`${API}/sessions/${session.id}/phase5/custom-reports`);
      const customReports = response.data.custom_reports || [];
      setCustomReports(customReports);
    } catch (error) {
      setCustomReports([]);
    }
    
    // Fetch current visibility settings
    const visibility = session.report_visibility || {
      show_exploring_client_feelings: true,
      show_emotional_balance_snapshot: true,
      show_strengths_and_coping_guide: true,
      show_deep_dive_inner_world: true,
      show_ai_summary_report: true,
      show_metaphorical_client_portrait: true
    };
    setReportVisibility(visibility);
    setShowReportSettingsModal(true);
  };

  const saveReportVisibility = async () => {
    try {
      await axios.patch(`${API}/sessions/${selectedSessionForSettings.id}/report-visibility`, reportVisibility);
      showSuccess('Analysis visibility settings saved');
      setShowReportSettingsModal(false);
      fetchSessions(); // Refresh sessions
    } catch (error) {
      showError('Failed to save settings');
      console.error('Error saving report visibility:', error);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    try {
      await axios.delete(`${API}/sessions/${sessionId}`);
      showSuccess('Session deleted successfully');
      fetchSessions(); // Refresh sessions list
    } catch (error) {
      showError('Failed to delete session');
      console.error('Error deleting session:', error);
    }
  };

  return (
    <div className="psychologist-workspace min-h-screen" data-testid="psychologist-dashboard">
      <nav className="px-8 py-6 flex justify-between items-center bg-white border-b border-gray-100 shadow-sm">
        <div className="flex items-center space-x-2">
          <img src="/insight-logo.png" alt="INSIGHT Logo" className="h-16" />
        </div>
        <div className="flex items-center space-x-4">
          <span className="text-gray-600">{withDoctorTitle(user?.name)}</span>
          
          {/* Notifications Bell */}
          <Dialog open={showNotificationsModal} onOpenChange={setShowNotificationsModal}>
            <DialogTrigger asChild>
              <Button 
                variant="outline" 
                className="relative"
                onClick={() => {
                  setShowNotificationsModal(true);
                  fetchNotifications();
                }}
              >
                <Bell className="w-4 h-4 mr-2" />
                Notifications
                {unreadNotificationCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {unreadNotificationCount}
                  </span>
                )}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle>Notifications</DialogTitle>
                  {notifications.some(n => !n.read) && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={markAllNotificationsAsRead}
                    >
                      Mark all as read
                    </Button>
                  )}
                </div>
              </DialogHeader>
              <div className="space-y-3">
                {loadingNotifications ? (
                  <div className="flex justify-center items-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin" />
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Bell className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No notifications</p>
                  </div>
                ) : (
                  notifications.map((notif) => (
                    <Card 
                      key={notif.id}
                      className={`cursor-pointer hover:bg-gray-50 transition-colors ${
                        !notif.read ? 'border-l-4 border-l-blue-500 bg-blue-50' : ''
                      }`}
                      onClick={() => handleNotificationClick(notif)}
                    >
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <h4 className="font-semibold text-sm mb-1">{notif.title}</h4>
                            <p className="text-sm text-gray-600">{notif.message}</p>
                            <p className="text-xs text-gray-400 mt-2">
                              {new Date(notif.created_at).toLocaleString()}
                            </p>
                          </div>
                          {!notif.read && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1"></div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </DialogContent>
          </Dialog>
          
          {/* Feedback Button for Psychologist */}
          <Dialog open={showFeedback} onOpenChange={setShowFeedback}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <MessageSquare className="w-4 h-4 mr-2" />
                Send Feedback
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Send Feedback to Admin</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmitFeedback} className="space-y-4">
                <div>
                  <Label htmlFor="category">Category *</Label>
                  <Select value={feedbackCategory} onValueChange={setFeedbackCategory} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Bug Report">Bug Analysis</SelectItem>
                      <SelectItem value="Feature Request">Feature Request</SelectItem>
                      <SelectItem value="General Feedback">General Feedback</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="rating">Rating (optional)</Label>
                  <Select value={feedbackRating} onValueChange={setFeedbackRating}>
                    <SelectTrigger>
                      <SelectValue placeholder="Rate your experience..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">⭐⭐⭐⭐⭐ Excellent</SelectItem>
                      <SelectItem value="4">⭐⭐⭐⭐ Good</SelectItem>
                      <SelectItem value="3">⭐⭐⭐ Average</SelectItem>
                      <SelectItem value="2">⭐⭐ Poor</SelectItem>
                      <SelectItem value="1">⭐ Very Poor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    value={feedbackMessage}
                    onChange={(e) => setFeedbackMessage(e.target.value)}
                    placeholder="Share your feedback, describe issues, or suggest improvements..."
                    rows={5}
                    required
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowFeedback(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submittingFeedback}>
                    {submittingFeedback ? 'Submitting...' : 'Submit Feedback'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          
          <Button 
            variant="outline" 
            onClick={() => navigate('/training-module')} 
            data-testid="training-module-btn"
          >
            <BookOpen className="w-4 h-4 mr-2" /> Training
          </Button>
          
          <Dialog open={showAccount} onOpenChange={setShowAccount}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="account-btn">
                <Lock className="w-4 h-4 mr-2" /> Account
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Account Settings</DialogTitle>
              </DialogHeader>
              <div className="space-y-6">
                <PsychologistPublicLink />
                {/* Profile Information */}
                <Card>
                  <CardHeader>
                    <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>Profile Information</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-sm text-gray-600">Name</Label>
                        <p className="text-base font-medium">{user?.name}</p>
                      </div>
                      <div>
                        <Label className="text-sm text-gray-600">Email</Label>
                        <p className="text-base font-medium">{user?.email}</p>
                      </div>
                      {user?.license_number && (
                        <div>
                          <Label className="text-sm text-gray-600">RCI CRR No / License No</Label>
                          <p className="text-base font-medium">{user.license_number}</p>
                        </div>
                      )}
                      <div>
                        <Label className="text-sm text-gray-600">Role</Label>
                        <p className="text-base font-medium capitalize">{user?.role}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Edit Profile - For Approved Psychologists Only */}
                {user?.approval_status === 'approved' && (
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                          Professional Profile
                        </CardTitle>
                        {!editingProfile && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={handleEditProfile}
                          >
                            Edit Profile
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      {!editingProfile ? (
                        <div className="space-y-4">
                          {user?.proforma?.photo_url && (
                            <div>
                              <Label className="text-sm text-gray-600">Profile Photo</Label>
                              <img 
                                src={`${API}${user.proforma.photo_url}`} 
                                alt="Profile" 
                                className="w-32 h-32 object-cover rounded-lg mt-2"
                              />
                            </div>
                          )}
                          <div>
                            <Label className="text-sm text-gray-600">Bio</Label>
                            <p className="text-base">{user?.proforma?.bio || 'Not provided'}</p>
                          </div>
                          <div>
                            <Label className="text-sm text-gray-600">Years of Experience</Label>
                            <p className="text-base">{user?.proforma?.experience_years || 'Not provided'}</p>
                          </div>
                          <div>
                            <Label className="text-sm text-gray-600">Qualifications</Label>
                            <p className="text-base">{user?.proforma?.qualifications || 'Not provided'}</p>
                          </div>
                        </div>
                      ) : (
                        <form onSubmit={handleUpdateProfile} className="space-y-4">
                          <div>
                            <Label htmlFor="profile-photo">Profile Photo</Label>
                            <Input
                              id="profile-photo"
                              type="file"
                              accept="image/*"
                              onChange={handlePhotoChange}
                              className="mt-1"
                            />
                            {profilePhotoPreview && (
                              <img 
                                src={profilePhotoPreview.startsWith('data:') ? profilePhotoPreview : `${API}${profilePhotoPreview}`}
                                alt="Preview" 
                                className="w-32 h-32 object-cover rounded-lg mt-3"
                              />
                            )}
                          </div>
                          <PublicProfileFields value={publicDetails} onChange={setPublicDetails} qualifications={profileQualifications} basic={<>
                          <div>
                            <Label htmlFor="profile-bio">Professional Bio</Label>
                            <Textarea
                              id="profile-bio"
                              value={profileBio}
                              onChange={(e) => setProfileBio(e.target.value)}
                              placeholder="Tell us about yourself..."
                              rows={4}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor="profile-experience">Years of Experience</Label>
                            <Input
                              id="profile-experience"
                              type="number"
                              value={profileExperience}
                              onChange={(e) => setProfileExperience(e.target.value)}
                              placeholder="e.g., 5"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor="profile-qualifications">Qualifications</Label>
                            <Textarea
                              id="profile-qualifications"
                              value={profileQualifications}
                              onChange={(e) => setProfileQualifications(e.target.value)}
                              placeholder="e.g., Ph.D. in Clinical Psychology, Licensed Clinical Psychologist"
                              rows={3}
                              className="mt-1"
                            />
                          </div>
                          
</>} expertise={<>
                          <label className="block text-sm font-medium">Specializations
                            <Input value={profileSpecialization} onChange={e=>setProfileSpecialization(e.target.value)} placeholder="Separate specializations with commas" />
                          <p className="text-xs text-slate-600">For example: anxiety, trauma, relationship concerns.</p></label>
</>} />
                          <p className="text-sm text-slate-600">Changes to professional details require a fresh verification review. Your account approval remains unchanged.</p>
                          <div className="flex gap-2">
                            <Button 
                              type="submit" 
                              disabled={updatingProfile}
                              style={{ background: '#2563EB' }}
                            >
                              {updatingProfile ? 'Updating...' : 'Save Changes'}
                            </Button>
                            <Button 
                              type="button"
                              variant="outline"
                              onClick={() => {
                                setEditingProfile(false);
                                setProfilePhoto(null);
                                setProfilePhotoPreview(null);
                              }}
                              disabled={updatingProfile}
                            >
                              Cancel
                            </Button>
                          </div>
                        </form>
                      )}
                    </CardContent>
                  </Card>
                )}
                
                {/* Change Password */}
                <Card>
                  <CardHeader>
                    <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>Change Password</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleChangePassword} className="space-y-4">
                      <div>
                        <Label htmlFor="current-password">Current Password</Label>
                        <Input
                          id="current-password"
                          type="password"
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          required
                          data-testid="current-password-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="new-password">New Password</Label>
                        <Input
                          id="new-password"
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          required
                          minLength={6}
                          data-testid="new-password-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="confirm-password">Confirm New Password</Label>
                        <Input
                          id="confirm-password"
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                          minLength={6}
                          data-testid="confirm-password-input"
                        />
                      </div>
                      <Button 
                        type="submit" 
                        disabled={changingPassword}
                        data-testid="change-password-btn"
                        style={{ background: '#2563EB' }}
                      >
                        {changingPassword ? 'Changing Password...' : 'Change Password'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
                
                {/* Account Settings Component */}
                <AccountSettings 
                  onLogout={logout} 
                  showNotification={(type, message) => {
                    if (type === 'success') showSuccess(message);
                    else if (type === 'error') showError(message);
                    else if (type === 'info') showInfo(message);
                  }}
                />
              </div>
            </DialogContent>
          </Dialog>
          <Button variant="ghost" onClick={logout} data-testid="psychologist-logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </nav>

      <div className="psychologist-workspace-content">
        <header className="psychologist-workspace-heading">
          <p className="psychologist-workspace-eyebrow">INSIGHT · PROFESSIONAL WORKSPACE</p>
          <h1>Psychologist Dashboard</h1>
          <p className="psychologist-workspace-subtitle">Manage consultations, follow client progress, and keep your analysis workflow in view.</p>
        </header>

        {/* Pending Approval Notice */}
        {user?.approval_status === 'pending' && (
          <Card className="mb-8 border-l-4 border-orange-500 bg-blue-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <AlertCircle className="w-8 h-8 text-blue-600 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-blue-900 mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    Account Pending Approval
                  </h3>
                  <p className="text-orange-800 mb-4">
                    Your psychologist account is currently under review by the administrator. You will be notified once your account has been approved and you can start accessing client sessions.
                  </p>
                  <div className="bg-white p-4 rounded border border-blue-200">
                    <p className="text-sm text-gray-700 mb-2">
                      <strong>Your Proforma Details:</strong>
                    </p>
                    {user?.proforma && (
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {user.proforma.specialization && (
                          <div>
                            <span className="text-gray-600">Specialization:</span> {user.proforma.specialization}
                          </div>
                        )}
                        {user.proforma.experience_years && (
                          <div>
                            <span className="text-gray-600">Experience:</span> {user.proforma.experience_years} years
                          </div>
                        )}
                        {user.proforma.qualifications && (
                          <div className="col-span-2">
                            <span className="text-gray-600">Qualifications:</span> {user.proforma.qualifications}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-blue-700 mt-4">
                    Please check back later or contact the administrator for more information.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Rejected Status Notice */}
        {user?.approval_status === 'rejected' && (
          <Card className="mb-8 border-l-4 border-red-500 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <XCircle className="w-8 h-8 text-red-600 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-red-900 mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    Account Not Approved
                  </h3>
                  <p className="text-red-800 mb-4">
                    Unfortunately, your psychologist account application has not been approved. Please contact the administrator for more information.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Dashboard Content - Only show if approved */}
        {user?.approval_status === 'approved' ? (
          <>
          <FreeAccessBanner />
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="flex flex-wrap w-full gap-1 mb-4 h-auto justify-start">
            <TabsTrigger value="consultations" data-testid="tab-consultations">
              <CalendarCheck className="w-4 h-4 mr-2" /> Consultations
              {consultations.filter(c => c.status === 'pending').length > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                  {consultations.filter(c => c.status === 'pending').length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="phase3-words" data-testid="tab-phase3-words">
              <BookOpen className="w-4 h-4 mr-2" /> Add Domain Specific Words
            </TabsTrigger>
            {activeVideoCall && (
              <TabsTrigger value="video-call" data-testid="tab-video-call">
                <Video className="w-4 h-4 mr-2" /> 🔴 Live Call
              </TabsTrigger>
            )}
            <TabsTrigger value="questions" data-testid="tab-questions">
              <MessageSquare className="w-4 h-4 mr-2" /> Client Questions
              {unansweredCount > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded-full">
                  {unansweredCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="feedback" data-testid="tab-feedback">
              <MessageCircle className="w-4 h-4 mr-2" /> Client Feedback
              {clientFeedback.filter(f => !f.admin_reply).length > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs bg-orange-100 text-orange-800 rounded-full">
                  {clientFeedback.filter(f => !f.admin_reply).length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="reports-library" data-testid="tab-reports-library">
              <FileText className="w-4 h-4 mr-2" />
              Analysis Library
            </TabsTrigger>
            
            {/* Features Dropdown — hidden entirely when every feature-tab report is disabled */}
            {anyFeatureEnabled && (
            <div className="relative">
              <button
                onClick={() => setShowPhase5Dropdown(!showPhase5Dropdown)}
                className="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm hover:bg-accent hover:text-accent-foreground"
              >
                <FileText className="w-4 h-4 mr-2" />
                Features
                <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${showPhase5Dropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {showPhase5Dropdown && (
                <div className="absolute top-full left-0 mt-1 bg-white shadow-lg rounded-md border border-gray-200 min-w-[220px] z-50">
                  {isReportEnabled('thematic_threads') && (
                  <button
                    onClick={() => {
                      if (!selectedSessionForFeatures) {
                        showModalNotification('info', 'Please activate a client session first to access Thematic Threads Analysis');
                        setShowPhase5Dropdown(false);
                        return;
                      }
                      handleOpenThematicThreadsModal();
                      setShowPhase5Dropdown(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 hover:bg-gray-100 flex items-center gap-2 transition ${
                      phase5SubMenu === 'thematic-threads' ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                    </svg>
                    Thematic Threads Analysis
                  </button>
                  )}
                  {isReportEnabled('persistent_patterns') && (
                  <button
                    onClick={() => {
                      if (!selectedSessionForFeatures) {
                        showModalNotification('info', 'Please activate a client session first to access Persistent Patterns Spotlight');
                        setShowPhase5Dropdown(false);
                        return;
                      }
                      handleOpenPersistentPatternsModal();
                      setShowPhase5Dropdown(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 hover:bg-gray-100 flex items-center gap-2 transition rounded-b-md ${
                      phase5SubMenu === 'persistent-patterns' ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7c0-2-1-3-3-3H7c-2 0-3 1-3 3zm8-2v14m7-7H5" />
                    </svg>
                    Persistent Patterns Spotlight
                  </button>
                  )}
                </div>
              )}
            </div>
            )}
          </TabsList>

          {/* Analytics section removed */}

          {/* Consultations Tab */}
          <TabsContent value="phase3-words" data-testid="phase3-words-content">
            <Phase3ExtraWordsManager sessions={sessions} />
          </TabsContent>


          <TabsContent value="consultations" data-testid="consultations-content">
            <Card className="psychologist-current-clients mb-6"><CardHeader><CardTitle>Current Clients</CardTitle></CardHeader>
              <CardContent>{currentClients.length ? <ul className="flex flex-wrap gap-3">{currentClients.map(client => <li key={client.id} className="rounded border px-3 py-2">{client.name}</li>)}</ul> : <p className="text-gray-500">No current clients yet.</p>}</CardContent>
            </Card>

            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Consultation Requests
                </h3>
              </div>

              {loadingConsultations ? (
                <div className="flex justify-center items-center h-64">
                  <p className="text-gray-500">Loading consultations...</p>
                </div>
              ) : (
                <>
                  {/* Pending Consultations */}
                  {consultations.filter(c => c.status === 'pending').length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                          Pending Requests ({consultations.filter(c => c.status === 'pending').length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {consultations
                            .filter(c => c.status === 'pending')
                            .slice((pendingConsultationsPage - 1) * pendingConsultationsPerPage, pendingConsultationsPage * pendingConsultationsPerPage)
                            .map((consultation) => {
                              // Find the session for this consultation
                              const session = sessions.find(s => s.id === consultation.session_id);
                              
                              return (
                              <div
                                key={consultation.id}
                               
                                className="psychologist-request-card p-4 border rounded-lg space-y-3"
                              >
                                <div>
                                  <div className="flex items-center justify-between">
                                    <h4 className="font-semibold text-lg">{consultation.client_name}</h4>
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                                      consultation.consultation_type === 'follow_up' 
                                        ? 'bg-blue-100 text-blue-700' 
                                        : 'bg-gray-100 text-gray-700'
                                    }`}>
                                      {consultation.consultation_type === 'follow_up' ? '🔄 Follow-up' : '📋 Initial'}
                                    </span>
                                  </div>
                                  <p className="text-sm text-gray-600">
                                    Requested: {new Date(consultation.created_at).toLocaleDateString()}
                                  </p>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => fetchClientHistory(consultation.client_id, consultation.client_name)}
                                    className="mt-2 text-xs"
                                    title="Check for previous sessions and Analysis"
                                  >
                                    📋 View Old Analysis
                                  </Button>
                                  {consultation.request_message && (
                                    <p className="text-sm text-gray-700 mt-2 p-2 bg-gray-50 rounded">
                                      "{consultation.request_message}"
                                    </p>
                                  )}
                                  {consultation.consultation_type === 'follow_up' && (
                                    <p className="text-xs text-blue-600 mt-2">
                                      ℹ️ A new session will be auto-created upon approval
                                    </p>
                                  )}
                                  {consultation.reusing_existing_assessment && session?.analysis_valid && (
                                    <div className="mt-2 p-3 bg-green-50 border-2 border-green-300 rounded-lg">
                                      <div className="flex items-start gap-2">
                                        <span className="text-lg">✅</span>
                                        <div className="flex-1">
                                          <p className="text-sm font-bold text-green-900 mb-1">
                                            Analysis Validity Active
                                          </p>
                                          <p className="text-xs text-green-800 mb-2">
                                            Client has completed analysis. No need for them to redo phases. You can view their existing Analysis after approval.
                                          </p>
                                          {session?.assessment_validity_expires_at && (
                                            <>
                                              <p className="text-xs text-green-700 font-semibold">
                                                {session.analysis_always_valid ? '♾️ Valid indefinitely' : <>📅 Valid until: {new Date(session.assessment_validity_expires_at).toLocaleDateString('en-US', {
                                                  month: 'long', 
                                                  day: 'numeric', 
                                                  year: 'numeric' 
                                                })}</>}
                                              </p>
                                              {!session.analysis_always_valid && <p className="text-xs text-gray-600 mt-1">
                                                ({session.assessment_days_remaining} days remaining)
                                              </p>}
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                  {consultation.reusing_existing_assessment && session?.assessment_expired && (
                                    <div className="mt-2 p-3 bg-red-50 border-2 border-red-300 rounded-lg">
                                      <div className="flex items-start gap-2">
                                        <span className="text-lg">⚠️</span>
                                        <div className="flex-1">
                                          <p className="text-sm font-bold text-red-900 mb-1">
                                            Analysis Validity Expired
                                          </p>
                                          <p className="text-xs text-red-800 mb-2">
                                            Client's previous analysis has expired. They need to complete a new 4-phase analysis.
                                          </p>
                                          <p className="text-xs text-red-700 font-semibold">
                                            📅 Expired on: {new Date(session.assessment_validity_expires_at).toLocaleDateString('en-US', { 
                                              month: 'long', 
                                              day: 'numeric', 
                                              year: 'numeric' 
                                            })}
                                          </p>
                                          <p className="text-xs text-gray-600 mt-2">
                                            This consultation will be auto-cancelled if analysis is not completed before appointment time.
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                  {!consultation.reusing_existing_assessment && consultation.assessment_required && (
                                    <p className="text-xs text-blue-600 mt-2">
                                      ⚠️ Client needs to complete 5-phase analysis before appointment
                                    </p>
                                  )}
                                </div>
                                <div className="flex items-center space-x-2">
                                  <Dialog>
                                    <DialogTrigger asChild>
                                      <Button
                                        disabled={processingConsultation === consultation.id}
                                        className="bg-green-600 hover:bg-green-700"
                                      >
                                        Schedule
                                      </Button>
                                    </DialogTrigger>
                                    <DialogContent>
                                      <DialogHeader>
                                        <DialogTitle>Schedule Consultation</DialogTitle>
                                      </DialogHeader>
                                      <div className="space-y-4">
                                        <div>
                                          <Label>Select Date & Time</Label>
                                          <Input
                                            type="datetime-local"
                                            value={consultationDate}
                                            onChange={(e) => setConsultationDate(e.target.value)}
                                            min={getMinDateTime()}
                                            required
                                          />
                                          <p className="text-xs text-gray-500 mt-1">
                                            ⏰ Must be at least 1 hour from now
                                          </p>
                                        </div>
                                        <Button
                                          onClick={() => handleConsultationAction(consultation.id, 'scheduled', consultationDate)}
                                          disabled={!consultationDate || processingConsultation === consultation.id}
                                          className="w-full"
                                        >
                                          {processingConsultation === consultation.id ? 'Scheduling...' : 'Confirm Schedule'}
                                        </Button>
                                      </div>
                                    </DialogContent>
                                  </Dialog>
                                  <Button
                                    onClick={() => handleConsultationAction(consultation.id, 'approved')}
                                    disabled={processingConsultation === consultation.id}
                                    variant="outline"
                                  >
                                    {processingConsultation === consultation.id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      'Approve'
                                    )}
                                  </Button>
                                  <Button
                                    onClick={() => handleConsultationAction(consultation.id, 'cancelled')}
                                    disabled={processingConsultation === consultation.id}
                                    variant="destructive"
                                  >
                                    Decline
                                  </Button>
                                </div>
                              </div>
                            );
                            })}
                        </div>
                        <Pagination
                          currentPage={pendingConsultationsPage}
                          totalItems={consultations.filter(c => c.status === 'pending').length}
                          itemsPerPage={pendingConsultationsPerPage}
                          onPageChange={setPendingConsultationsPage}
                          onItemsPerPageChange={setPendingConsultationsPerPage}
                        />
                      </CardContent>
                    </Card>
                  )}

                  {/* Scheduled/Approved Consultations */}
                  {consultations.filter(c => ['approved', 'scheduled', 'payment_pending'].includes(c.status)).length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                          Upcoming Consultations
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {consultations
                            .filter(c => ['approved', 'scheduled', 'payment_pending'].includes(c.status))
                            .slice((scheduledConsultationsPage - 1) * scheduledConsultationsPerPage, scheduledConsultationsPage * scheduledConsultationsPerPage)
                            .map((consultation) => {
                              // Find session - try by consultation.session_id first, then by client_id
                              let session = null;
                              if (consultation.session_id) {
                                session = sessions.find(s => s.id === consultation.session_id);
                              }
                              // If not found by session_id, try finding by client_id
                              if (!session && consultation.client_id) {
                                session = sessions.find(s => s.client_id === consultation.client_id);
                              }
                              
                              const { completed: completedPhases, count: phasesComplete, allComplete: allPhasesComplete } = getClientPhaseProgress(session);
                              const phase5Complete = session?.phase5_analysis_complete || false;

                              return (
                              <div
                                key={consultation.id}
                                data-testid={`psychologist-progress-${session?.id}`}
                                className="psychologist-request-card p-4 border rounded-lg space-y-3"
                              >
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-semibold text-lg">{consultation.client_name}</h4>
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                                      consultation.consultation_type === 'follow_up' 
                                        ? 'bg-blue-100 text-blue-700' 
                                        : 'bg-gray-100 text-gray-700'
                                    }`}>
                                      {consultation.consultation_type === 'follow_up' ? '🔄 Follow-up' : '📋 Initial'}
                                    </span>
                                    {consultation.status === 'payment_pending' && (
                                      <span className="px-2 py-1 rounded text-xs font-medium bg-orange-100 text-blue-700">
                                        Awaiting activation
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-600">
                                    Requested: {new Date(consultation.created_at).toLocaleDateString()}
                                  </p>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => fetchClientHistory(consultation.client_id, consultation.client_name)}
                                    className="mt-2 text-xs"
                                    title="Check for previous sessions and Analysis"
                                  >
                                    📋 View Old Analysis
                                  </Button>
                                  
                                  {/* Phase Progress Display */}
                                  {session ? (
                                    <div className="psychologist-phase-progress mt-3 p-4 rounded-lg border border-blue-200">
                                      {/* Progress Header */}
                                      <div className="flex items-center justify-between mb-3">
                                        <p className="text-base font-bold text-blue-900">
                                          📊 Client Progress: {phasesComplete}/4 Phases Complete
                                        </p>
                                      </div>
                                      
                                      {/* Phase Indicators */}
                                      <div className="psychologist-phase-steps flex gap-3 mb-3">
                                        {[1, 2, 3, 4].map((phaseNum) => {
                                          const isComplete = completedPhases[phaseNum - 1];
                                          return (
                                            <div key={phaseNum} className="flex items-center gap-1.5">
                                              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shadow-sm ${
                                                isComplete 
                                                  ? 'bg-green-500 text-white' 
                                                  : 'bg-gray-300 text-gray-600'
                                              }`}>
                                                {isComplete ? '✓' : phaseNum}
                                              </span>
                                              <span className="text-xs font-medium text-gray-700">
                                                {phaseNum === 1 ? 'Client Contextual Demographics' : 
                                                 phaseNum === 2 ? 'Client Moral Reflections' : 
                                                 phaseNum === 3 ? 'Client Views' : 
                                                 'Client Aspirations'}
                                              </span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                      
                                      {/* Status Messages */}
                                      {!allPhasesComplete ? (
                                        <div className="p-3 bg-blue-100 border border-blue-300 rounded-lg">
                                          <p className="text-sm font-semibold text-blue-900 mb-1">
                                            ⏳ Waiting for Client
                                          </p>
                                          <p className="text-xs text-blue-800">
                                            Client {phasesComplete === 0 ? "hasn't completed" : "has completed"} {phasesComplete} out of 4 phases of the Analysis. Please wait until the client completes all phases before proceeding with analysis.
                                          </p>
                                        </div>
                                      ) : !phase5Complete ? (
                                        <div className="p-3 bg-amber-100 border-2 border-amber-400 rounded-lg">
                                          <p className="text-sm font-bold text-amber-900 mb-1">
                                            ⚠️ Action Required: Begin Analysis
                                          </p>
                                          <p className="text-xs text-amber-800 mb-3">
                                            Client has completed all 4 phases. You may now begin Phase 5 – Analysis to interpret and categorize the client's responses.
                                          </p>
                                          <Button
                                            onClick={() => navigate(`/session/${session.id}`)}
                                            size="sm"
                                            className="w-full bg-amber-600 hover:bg-amber-700 font-semibold"
                                          >
                                            📝 Analyze Client Work (Phase 5)
                                          </Button>
                                        </div>
                                      ) : (
                                        <div className="p-3 bg-green-100 border-2 border-green-400 rounded-lg">
                                          <p className="text-sm font-bold text-green-900 mb-1">
                                            ✅ Structured Qualitative Interpretative Summary Complete
                                          </p>
                                          <p className="text-xs text-green-800">
                                            Structured Qualitative Interpretative Summary has been submitted. Video call is now available. Client has been notified to review the Analysis.
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="mt-3 p-3 bg-red-100 border border-red-300 rounded">
                                      <p className="text-sm font-semibold text-red-900">❌ Session Not Found</p>
                                      <p className="text-xs text-red-800 mt-1">
                                        Unable to load session data. This consultation may not have a linked session yet.
                                      </p>
                                    </div>
                                  )}
                                </div>
                                
                                {consultation.consultation_date && (
                                  <div className="text-sm font-semibold text-blue-600 mt-1">
                                    <div>📅 {new Date(consultation.consultation_date).toLocaleDateString()}</div>
                                    <div>🕐 {new Date(consultation.consultation_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                  </div>
                                )}
                                
                                <div className="flex gap-2">
                                  {/* View Client Reports Button - Disabled when Action Required section is shown (Phase 5 incomplete) */}
                                  {session && session.client_assessment_complete && (
                                    <Button
                                      onClick={() => navigate(`/session/${session.id}?viewReportsOnly=true`)}
                                      variant="outline"
                                      size="sm"
                                      className="border-blue-500 text-blue-600 hover:bg-blue-50"
                                      disabled={allPhasesComplete && !phase5Complete}
                                    >
                                      <FileText className="w-4 h-4 mr-2" />
                                      {session.phase5_analysis_complete ? 'View Client Analysis' : 'Analyze Client Work'}
                                    </Button>
                                  )}
                                  
                                  {/* Work with Session Button — hidden when every feature-tab report is disabled */}
                                  {session && session.phase5_analysis_complete && anyFeatureEnabled && (
                                    <Button
                                      onClick={() => handleSessionSelect(session.id)}
                                      variant={selectedSessionForFeatures === session.id ? "default" : "outline"}
                                      size="sm"
                                      className={selectedSessionForFeatures === session.id ? "bg-blue-600 text-white" : "border-purple-500 text-blue-600 hover:bg-blue-50"}
                                    >
                                      <Settings className="w-4 h-4 mr-2" />
                                      {selectedSessionForFeatures === session.id ? 'Active Session' : 'Work with Session'}
                                    </Button>
                                  )}
                                  
                                  {(consultation.status === 'scheduled' || consultation.status === 'approved') && consultation.consultation_date && (
                                    <Button
                                      onClick={() => handleStartVideoCall(consultation)}
                                      disabled={!canStartVideoCall(consultation) || !phase5Complete}
                                      className={
                                        canStartVideoCall(consultation) && phase5Complete
                                          ? 'bg-green-600 hover:bg-green-700' 
                                          : 'bg-gray-400'
                                      }
                                      size="sm"
                                    >
                                      <Video className="w-4 h-4 mr-2" />
                                      {!phase5Complete
                                        ? '🔒 Complete Analysis First' 
                                        : canStartVideoCall(consultation) 
                                          ? 'Start Video Call' 
                                          : getTimeUntilVideoCall(consultation)
                                      }
                                    </Button>
                                  )}
                                  <Button
                                    onClick={() => handleReschedule(consultation)}
                                    disabled={processingConsultation === consultation.id}
                                    variant="outline"
                                    size="sm"
                                  >
                                    Reschedule
                                  </Button>
                                  {/* NOTE: Psychologist CANNOT cancel after approving - only at 'pending' stage via 'Decline' button */}
                                  {/* Cancel booking option removed after approval to maintain psychologist's commitment */}
                                  <Button
                                    onClick={() => handleConsultationAction(consultation.id, 'completed')}
                                    disabled={processingConsultation === consultation.id || !phase5Complete}
                                    size="sm"
                                    title={!phase5Complete ? 'Complete Phase 5 Analysis first to mark consultation as complete' : 'Mark consultation as complete'}
                                  >
                                    {!phase5Complete ? '🔒 Complete Analysis First' : 'Mark Complete'}
                                  </Button>
                                </div>
                              </div>
                            );
                            })}
                        </div>
                        <Pagination
                          currentPage={scheduledConsultationsPage}
                          totalItems={consultations.filter(c => ['approved', 'scheduled', 'payment_pending'].includes(c.status)).length}
                          itemsPerPage={scheduledConsultationsPerPage}
                          onPageChange={setScheduledConsultationsPage}
                          onItemsPerPageChange={setScheduledConsultationsPerPage}
                        />
                      </CardContent>
                    </Card>
                  )}

                  {/* Completed/Cancelled Consultations */}
                  {consultations.filter(c => ['completed', 'cancelled'].includes(c.status)).length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                          Past Consultations
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {consultations
                            .filter(c => ['completed', 'cancelled'].includes(c.status))
                            .slice((completedConsultationsPage - 1) * completedConsultationsPerPage, completedConsultationsPage * completedConsultationsPerPage)
                            .map((consultation) => (
                              <div
                                key={consultation.id}
                                className="p-4 border rounded-lg"
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <h4 className="font-semibold text-lg">{consultation.client_name}</h4>
                                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                                        consultation.consultation_type === 'follow_up' 
                                          ? 'bg-blue-100 text-blue-700' 
                                          : 'bg-gray-100 text-gray-700'
                                      }`}>
                                        {consultation.consultation_type === 'follow_up' ? '🔄 Follow-up' : '📋 Initial'}
                                      </span>
                                    </div>
                                    <p className="text-sm text-gray-600">
                                      {new Date(consultation.created_at).toLocaleDateString()}
                                    </p>
                                  </div>
                                  <span className={`px-3 py-1 text-sm rounded-full ${
                                    consultation.status === 'completed' 
                                      ? 'bg-blue-100 text-blue-800' 
                                      : 'bg-red-100 text-red-800'
                                  }`}>
                                    {consultation.status === 'completed' ? '✓ Completed' : '✗ Cancelled'}
                                  </span>
                                </div>
                                
                                {/* Display Cancellation Reason for Cancelled Consultations */}
                                {consultation.status === 'cancelled' && consultation.cancellation_reason && (
                                  <div className="mt-3 p-3 bg-red-50 border-l-4 border-red-400 rounded">
                                    <p className="text-sm font-semibold text-red-900 mb-1">
                                      Cancellation Reason:
                                    </p>
                                    <p className="text-sm text-red-800">
                                      {consultation.cancellation_reason}
                                    </p>
                                    {consultation.cancelled_at && (
                                      <p className="text-xs text-red-600 mt-1">
                                        Cancelled on: {new Date(consultation.cancelled_at).toLocaleString()}
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                        </div>
                        <Pagination
                          currentPage={completedConsultationsPage}
                          totalItems={consultations.filter(c => ['completed', 'cancelled'].includes(c.status)).length}
                          itemsPerPage={completedConsultationsPerPage}
                          onPageChange={setCompletedConsultationsPage}
                          onItemsPerPageChange={setCompletedConsultationsPerPage}
                        />
                      </CardContent>
                    </Card>
                  )}

                  {/* No Consultations Message */}
                  {consultations.length === 0 && (
                    <Card>
                      <CardContent className="py-12 text-center">
                        <CalendarCheck className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                        <p className="text-gray-600">No consultation requests yet</p>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}
            </div>
          </TabsContent>

          {/* Active Sessions, Completed Sessions, and Custom Reports tabs removed */}

          {/* Client Questions Tab */}
          <TabsContent value="questions" data-testid="questions-content">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-bold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Client Questions
                </h3>
                <div className="flex space-x-2 items-center">
                  <Button 
                    onClick={() => setShowGridView(!showGridView)} 
                    variant="outline" 
                    size="sm"
                  >
                    {showGridView ? '💬 Chat View' : '📊 Grid View'}
                  </Button>
                  <div className="text-sm text-gray-600">
                    {unansweredCount > 0 ? (
                      <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full font-medium">
                        {unansweredCount} Unanswered
                      </span>
                    ) : (
                      <span className="text-green-600">All caught up!</span>
                    )}
                  </div>
                </div>
              </div>

              {!showGridView ? (
                loadingQuestions ? (
                <div className="flex justify-center items-center h-64">
                  <p className="text-gray-500">Loading questions...</p>
                </div>
              ) : questions.length === 0 ? (
                <Card>
                  <CardContent className="text-center py-12">
                    <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-gray-500">No questions from clients yet</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-4">
                    {questions
                      .slice((questionsPage - 1) * questionsItemsPerPage, questionsPage * questionsItemsPerPage)
                      .map((question) => (
                    <Card key={question.id} className={`hover:shadow-lg transition-shadow ${question.status === 'open' ? 'border-l-4 border-l-yellow-500' : ''}`}>
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <CardTitle className="text-lg mb-2">{question.subject}</CardTitle>
                            <div className="flex items-center space-x-4 text-sm text-gray-500">
                              <span className="font-semibold text-blue-600">From: {question.client_name}</span>
                              <span>{question.question_type === 'session_specific' ? '📋 Session Question' : '💬 General Question'}</span>
                              <span>{new Date(question.created_at).toLocaleDateString()}</span>
                            </div>
                            {/* Session Status Information */}
                            {question.question_type === 'session_specific' && question.session_status && (
                              <div className="mt-2 flex items-center space-x-2 text-xs">
                                {question.session_status === 'cancelled' && (
                                  <span className="px-2 py-1 bg-red-100 text-red-800 font-medium rounded">
                                    ⚠️ Session Cancelled
                                  </span>
                                )}
                                {question.session_status === 'completed' && (
                                  <span className="px-2 py-1 bg-blue-100 text-blue-800 font-medium rounded">
                                    ✓ Session Completed
                                  </span>
                                )}
                                {question.session_status === 'in_progress' && (
                                  <span className="px-2 py-1 bg-green-100 text-green-800 font-medium rounded">
                                    ⏳ Session In Progress (Phase {question.session_current_phase}/5)
                                  </span>
                                )}
                                {question.session_status === 'not_found' && (
                                  <span className="px-2 py-1 bg-gray-100 text-gray-600 font-medium rounded">
                                    ⚠️ Session Not Found
                                  </span>
                                )}
                              </div>
                            )}
                            {question.session_cancellation_reason && (
                              <div className="mt-1 text-xs text-red-600">
                                Reason: {question.session_cancellation_reason}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            {question.status === 'open' && (
                              <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                                Needs Response
                              </span>
                            )}
                            {question.status === 'answered' && (
                              <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                                Answered
                              </span>
                            )}
                            {question.status === 'resolved' && (
                              <span className="px-3 py-1 bg-gray-100 text-gray-800 text-xs font-medium rounded-full">
                                Resolved
                              </span>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {/* Messages Thread */}
                          <div className="space-y-3 max-h-96 overflow-y-auto">
                            {question.messages && Array.isArray(question.messages) && question.messages.map((msg, idx) => (
                              <div
                                key={idx}
                                className={`p-4 rounded-lg ${
                                  msg.sender_role === 'psychologist'
                                    ? 'bg-green-50 ml-8'
                                    : 'bg-blue-50 mr-8'
                                }`}
                              >
                                <div className="flex justify-between items-start mb-2">
                                  <span className="font-semibold text-sm">
                                    {msg.sender_role === 'psychologist' ? 'You' : msg.sender_name}
                                  </span>
                                  <div className="flex items-center space-x-2">
                                    <span className="text-xs text-gray-500">
                                      {new Date(msg.timestamp).toLocaleString()}
                                      {msg.edited_at && ' (edited)'}
                                    </span>
                                  </div>
                                </div>
                                {editingMessage?.questionId === question.id && editingMessage?.messageIndex === idx ? (
                                  <div className="space-y-2">
                                    <Textarea
                                      value={editMessageText}
                                      onChange={(e) => setEditMessageText(e.target.value)}
                                      rows={3}
                                    />
                                    <div className="flex space-x-2">
                                      <Button
                                        size="sm"
                                        onClick={handleSaveEdit}
                                        disabled={savingEdit}
                                      >
                                        {savingEdit ? 'Saving...' : 'Save'}
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={handleCancelEdit}
                                      >
                                        Cancel
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <p className="text-gray-700 whitespace-pre-wrap">{msg.message}</p>
                                    {/* Display Attachments */}
                                    {msg.attachments && Array.isArray(msg.attachments) && msg.attachments.length > 0 && (
                                      <div className="mt-2 space-y-1">
                                        {msg.attachments.filter(att => att && typeof att === 'string' && att.trim() !== '').map((attachment, attIdx) => (
                                          <button
                                            key={attIdx}
                                            onClick={() => downloadAttachment(attachment)}
                                            className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 text-sm cursor-pointer underline"
                                          >
                                            <span>📎 View Attachment</span>
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                    {/* Edit Button for Psychologist Messages */}
                                    {msg.sender_role === 'psychologist' && (
                                      <div className="mt-2">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => handleEditMessage(question.id, idx, msg.message)}
                                          className="text-xs"
                                        >
                                          Edit Message
                                        </Button>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            ))}
                          </div>

                          {/* Reply Section */}
                          {question.status !== 'resolved' && (
                            <div className="border-t pt-4">
                              <Textarea
                                value={selectedQuestion?.id === question.id ? replyMessage : ''}
                                onChange={(e) => {
                                  setReplyMessage(e.target.value);
                                  setSelectedQuestion(question);
                                }}
                                placeholder="Type your professional response..."
                                rows={4}
                                className="mb-2"
                              />
                              {/* Reply Attachments */}
                              <div className="mb-2">
                                <Label className="text-sm mb-1 block">Attach File (Optional)</Label>
                                <Input
                                  type="file"
                                  onChange={async (e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                      const uploadResult = await handleFileUpload(file);
                                      if (uploadResult) {
                                        setReplyAttachments(prev => ({
                                          ...prev,
                                          [question.id]: [...(prev[question.id] || []), uploadResult]
                                        }));
                                      }
                                    }
                                    e.target.value = '';
                                  }}
                                  disabled={uploadingFile}
                                  className="text-sm mb-2"
                                />
                                {uploadingFile && (
                                  <div className="text-sm text-blue-600 mb-2">Uploading...</div>
                                )}
                                {(replyAttachments[question.id] || []).length > 0 && (
                                  <div className="mt-2 space-y-1">
                                    <div className="text-xs font-medium mb-1">Attached:</div>
                                    {replyAttachments[question.id].map((attachment, idx) => (
                                      <div key={idx} className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded text-sm">
                                        <span className="text-green-700">📎 {attachment.filename}</span>
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => {
                                            setReplyAttachments(prev => ({
                                              ...prev,
                                              [question.id]: prev[question.id].filter((_, i) => i !== idx)
                                            }));
                                          }}
                                          className="text-red-600 hover:text-red-800"
                                        >
                                          ✕
                                        </Button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="mt-4 flex justify-between items-center">
                                <Button
                                  onClick={() => handleReplyToQuestion(question.id)}
                                  disabled={submittingReply || !replyMessage.trim() || selectedQuestion?.id !== question.id}
                                  style={{ background: '#10B981' }}
                                >
                                  {submittingReply ? 'Sending...' : 'Send Reply'}
                                </Button>
                                {question.status !== 'closed' && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      setChatToEnd(question.id);
                                      setShowEndChatModal(true);
                                    }}
                                    className="text-red-600 hover:text-red-700 border-red-300"
                                  >
                                    <XCircle className="w-4 h-4 mr-2" />
                                    End Chat
                                  </Button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                
                <Pagination
                  currentPage={questionsPage}
                  totalItems={questions.length}
                  itemsPerPage={questionsItemsPerPage}
                  onPageChange={setQuestionsPage}
                  onItemsPerPageChange={setQuestionsItemsPerPage}
                />
              </div>
            ) // End of accordion view
          ) : ( // Start of grid view
                <Card>
                  <CardContent className="pt-6">
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse border border-gray-300">
                        <thead className="bg-blue-50">
                          <tr>
                            <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Subject</th>
                            <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Client Name</th>
                            <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Type</th>
                            <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Q&A Status</th>
                            <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Messages</th>
                            <th className="border border-gray-300 px-4 py-3 text-center font-semibold text-gray-700">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {questions
                            .slice((questionsPage - 1) * questionsItemsPerPage, questionsPage * questionsItemsPerPage)
                            .map((question) => (
                            <tr key={question.id} className="hover:bg-gray-50">
                              <td className="border border-gray-300 px-4 py-3 text-sm">
                                <p className="font-semibold">{question.subject}</p>
                                <p className="text-xs text-gray-500">{new Date(question.created_at).toLocaleDateString()}</p>
                                {question.session_cancellation_reason && (
                                  <p className="text-xs text-red-600 mt-1">
                                    Reason: {question.session_cancellation_reason}
                                  </p>
                                )}
                              </td>
                              <td className="border border-gray-300 px-4 py-3 text-sm font-semibold text-blue-600">
                                {question.client_name || 'Unknown'}
                              </td>
                              <td className="border border-gray-300 px-4 py-3 text-sm">
                                {question.question_type === 'session_specific' ? '📋 Session' : '💬 General'}
                              </td>
                              <td className="border border-gray-300 px-4 py-3 text-sm">
                                {question.status === 'open' && (
                                  <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                                    Open
                                  </span>
                                )}
                                {question.status === 'answered' && (
                                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                                    Answered
                                  </span>
                                )}
                                {question.status === 'resolved' && (
                                  <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs font-medium rounded-full">
                                    Resolved
                                  </span>
                                )}
                              </td>
                              <td className="border border-gray-300 px-4 py-3 text-sm text-center">
                                <span className="font-medium">{question.messages?.length || 0}</span>
                              </td>
                              <td className="border border-gray-300 px-4 py-3">
                                <div className="flex justify-center space-x-2">
                                  <Button
                                    onClick={() => {
                                      setViewingThread(question);
                                      setViewThreadDialog(true);
                                    }}
                                    size="sm"
                                    variant="outline"
                                    title="View Full Thread"
                                  >
                                    👁️ View
                                  </Button>
                                  {question.status !== 'resolved' && (
                                    <Button
                                      onClick={() => handleEditClientQuestion(question)}
                                      size="sm"
                                      variant="outline"
                                      title="Edit Question"
                                      className="text-blue-600 hover:text-blue-800"
                                    >
                                      ✏️ Edit
                                    </Button>
                                  )}
                                  <Button
                                    onClick={() => downloadQaPdf(question.id)}
                                    size="sm"
                                    variant="outline"
                                    title="Download"
                                  >
                                    📄
                                  </Button>
                                  {question.status !== 'resolved' && (
                                    <Button
                                      onClick={() => handleCloseQuestion(question.id)}
                                      size="sm"
                                      className="bg-blue-500 hover:bg-blue-600 text-white"
                                      title="Close Chat"
                                    >
                                      🔒
                                    </Button>
                                  )}
                                  <Button
                                    onClick={() => deleteQuestion(question.id)}
                                    size="sm"
                                    variant="destructive"
                                    title="Delete"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    <Pagination
                      currentPage={questionsPage}
                      totalItems={questions.length}
                      itemsPerPage={questionsItemsPerPage}
                      onPageChange={setQuestionsPage}
                      onItemsPerPageChange={setQuestionsItemsPerPage}
                    />
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Custom Reports tab content removed */}

          {/* View Full Thread Dialog */}
          <Dialog open={viewThreadDialog} onOpenChange={setViewThreadDialog}>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{viewingThread?.subject}</DialogTitle>
              </DialogHeader>
              {viewingThread && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded">
                    <div>
                      <p className="text-sm text-gray-600">Type:</p>
                      <p className="font-medium">{viewingThread.question_type === 'session_specific' ? 'Session Question' : 'General Question'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Status:</p>
                      <p className="font-medium capitalize">{viewingThread.status}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Client:</p>
                      <p className="font-medium">{viewingThread.client_name || 'Not assigned'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Created:</p>
                      <p className="font-medium">{new Date(viewingThread.created_at).toLocaleString()}</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h4 className="font-semibold">Conversation:</h4>
                    {viewingThread.messages?.map((msg, idx) => (
                      <div
                        key={idx}
                        className={`p-4 rounded-lg ${
                          msg.sender_role === 'psychologist'
                            ? 'bg-green-50 ml-8'
                            : 'bg-blue-50 mr-8'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-semibold text-sm">
                            {msg.sender_role === 'psychologist' ? 'You' : msg.sender_name}
                          </span>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-gray-500">
                              {new Date(msg.timestamp).toLocaleString()}
                              {msg.edited_at && ' (edited)'}
                            </span>
                          </div>
                        </div>
                        {editingMessage?.questionId === viewingThread.id && editingMessage?.messageIndex === idx ? (
                          <div className="space-y-2">
                            <Textarea
                              value={editMessageText}
                              onChange={(e) => setEditMessageText(e.target.value)}
                              rows={3}
                            />
                            <div className="flex space-x-2">
                              <Button
                                size="sm"
                                onClick={handleSaveEdit}
                                disabled={savingEdit}
                              >
                                {savingEdit ? 'Saving...' : 'Save'}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={handleCancelEdit}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p className="text-gray-700 whitespace-pre-wrap">{msg.message}</p>
                            {msg.attachments && Array.isArray(msg.attachments) && msg.attachments.length > 0 && (
                              <div className="mt-2 space-y-1">
                                {msg.attachments.filter(att => att && typeof att === 'string' && att.trim() !== '').map((attachment, attIdx) => (
                                  <button
                                    key={attIdx}
                                    onClick={() => downloadAttachment(attachment)}
                                    className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 text-sm cursor-pointer underline"
                                  >
                                    <span>📎 View Attachment</span>
                                  </button>
                                ))}
                              </div>
                            )}
                            {/* Edit Button for Psychologist Messages */}
                            {msg.sender_role === 'psychologist' && (
                              <div className="mt-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleEditMessage(viewingThread.id, idx, msg.message)}
                                  className="text-xs"
                                >
                                  Edit Message
                                </Button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Client Feedback Tab (Admin Rights) */}
          <TabsContent value="feedback" data-testid="feedback-content">
            <Card>
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>Client Feedback Management</CardTitle>
                <p className="text-sm text-gray-600 mt-2">Manage feedback from clients - reply, review, and track all client feedback</p>
              </CardHeader>
              <CardContent>
                {loadingClientFeedback ? (
                  <div className="flex justify-center items-center h-32">
                    <p className="text-gray-500">Loading feedback...</p>
                  </div>
                ) : clientFeedback.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageCircle className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                    <p className="text-gray-600">No client feedback yet</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Client Name</th>
                          <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Category</th>
                          <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Message</th>
                          <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Rating</th>
                          <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Status</th>
                          <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Date</th>
                          <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientFeedback
                          .slice((clientFeedbackPage - 1) * clientFeedbackItemsPerPage, clientFeedbackPage * clientFeedbackItemsPerPage)
                          .map((fb, idx) => (
                            <tr key={fb.id} className="hover:bg-gray-50">
                              <td className="border border-gray-300 px-4 py-3 text-gray-700">{fb.user_name}</td>
                              <td className="border border-gray-300 px-4 py-3 text-gray-700">{fb.category}</td>
                              <td className="border border-gray-300 px-4 py-3 text-gray-700 max-w-xs truncate">{fb.message}</td>
                              <td className="border border-gray-300 px-4 py-3 text-gray-700">
                                {fb.rating ? '⭐'.repeat(fb.rating) : '-'}
                              </td>
                              <td className="border border-gray-300 px-4 py-3">
                                {fb.admin_reply ? (
                                  <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">Replied</span>
                                ) : (
                                  <span className="px-2 py-1 text-xs bg-orange-100 text-orange-800 rounded-full">Pending</span>
                                )}
                              </td>
                              <td className="border border-gray-300 px-4 py-3 text-gray-700 text-sm">
                                {new Date(fb.created_at).toLocaleDateString()}
                              </td>
                              <td className="border border-gray-300 px-4 py-3">
                                <div className="flex space-x-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setSelectedClientFeedback(fb);
                                      setReplyText(fb.admin_reply || '');
                                      setShowClientFeedbackModal(true);
                                    }}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => handleDeleteClientFeedback(fb.id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                    
                    <Pagination
                      currentPage={clientFeedbackPage}
                      totalItems={clientFeedback.length}
                      itemsPerPage={clientFeedbackItemsPerPage}
                      onPageChange={setClientFeedbackPage}
                      onItemsPerPageChange={setClientFeedbackItemsPerPage}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>



          {/* Live Video Call Tab Content */}
          {activeVideoCall && (
            <TabsContent value="video-call" data-testid="video-call-content">
              <div className="w-full h-[600px] bg-white rounded-lg overflow-hidden border shadow-lg">
                <WebRTCVideoCall
                  roomName={activeVideoCall.room_name}
                  userDisplayName={user?.name || 'Psychologist'}
                  userEmail={user?.email}
                  onCallEnd={handleCallEnd}
                  consultationId={activeVideoCall.consultation_id}
                  videoCallId={activeVideoCall.id}
                  isInitiator={true}
                />
              </div>
            </TabsContent>
          )}

          <TabsContent value="reports-library" data-testid="reports-library-content">
            <ReportsLibrary onError={(msg) => showError(msg)} />
          </TabsContent>

          {/* Features Content */}
          <TabsContent value="phase5-features" data-testid="phase5-features-content">
            <div className="space-y-6">

              {/* Custom Reports Sub-Content */}
              {false && phase5SubMenu === 'custom-reports' && (
            <div className="space-y-6">
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Custom Analysis
              </h3>
              
              {/* Instruction Text - Only show when no session selected */}
              {!selectedSessionForFeatures && (
                <div className="mb-6">
                  <p className="text-sm font-semibold text-gray-700">Choose an active client session to generate Analysis</p>
                </div>
              )}

              {selectedSessionForFeatures ? (
                <>
                  {/* Read-Only Notice for Completed Sessions */}
                  {isSessionCompleted() && (
                    <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-lg mb-4">
                      <div className="flex items-start gap-3">
                        <Lock className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div>
                          <h5 className="font-semibold text-amber-900">Session Completed - Read-Only Mode</h5>
                          <p className="text-sm text-amber-800">This session is complete. You can view existing Analysis but cannot generate new ones.</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Generate Form - Only show if session not completed */}
                  {!isSessionCompleted() && (
                    <div className="p-6 bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-300 rounded-xl">
                      <h4 className="font-bold text-xl mb-3" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2563eb' }}>
                        ✨ Generate Custom Clinical Analysis
                      </h4>
                      <p className="text-sm text-gray-700 mb-4">
                        Create a comprehensive Analysis with automatic inclusion of risk analysis, clinical disclaimer, and APA citations.
                      </p>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-semibold mb-2">Analysis Name *</label>
                          <input
                            type="text"
                            value={customReportName}
                            onChange={(e) => setCustomReportName(e.target.value)}
                            placeholder="e.g., Trauma Analysis, Attachment Analysis, Coping Mechanisms Review"
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            disabled={generatingCustomReport}
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-semibold mb-2">Analysis Focus *</label>
                          <textarea
                            value={customReportFocus}
                            onChange={(e) => setCustomReportFocus(e.target.value)}
                            placeholder="Describe what you want to analyze...

Examples:
• Analyze patterns of trauma responses and their manifestations
• Evaluate attachment styles and relational dynamics
• Assess coping mechanisms and their effectiveness
• Explore identity themes and self-perception patterns"
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[180px] text-sm"
                            disabled={generatingCustomReport}
                          />
                          <p className="text-xs text-gray-500 mt-2">
                            💡 Focus on your specific analysis needs - risk analysis, disclaimer, and citations are added automatically
                          </p>
                        </div>
                        
                        <div className="flex justify-end gap-3">
                          <Button 
                            variant="outline" 
                            onClick={() => { setCustomReportName(''); setCustomReportFocus(''); }}
                            disabled={generatingCustomReport}
                          >
                            Clear
                          </Button>
                          <Button 
                            onClick={handleGenerateCustomReportDashboard}
                            disabled={generatingCustomReport || !customReportName.trim() || !customReportFocus.trim()}
                            style={{ background: '#2563eb' }}
                            className="text-white"
                          >
                            {generatingCustomReport ? (
                              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating...</>
                            ) : (
                              <><FileText className="w-4 h-4 mr-2" /> Generate Analysis</>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Where to Find Reports Info Box */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h5 className="font-semibold text-blue-900 mb-1">Where to find your custom analysis</h5>
                        <p className="text-sm text-blue-800">
                          Once generated, custom analysis will appear in the <strong>AI Generated Analysis</strong> section 
                          of Phase 5 (Session view), where you can review, download, and control their visibility to the client.
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="p-8 text-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600 text-lg mb-2">No Active Session</p>
                  <p className="text-sm text-gray-500">Click "Work with Session" on a completed consultation to generate custom analysis</p>
                </div>
              )}
            </div>
              )}

              {/* Ask Buddy Sub-Content */}
              {false && phase5SubMenu === 'ask-buddy' && (
            <div className="space-y-6">
              <h3 className="text-xl font-bold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Ask Buddy</h3>
              <p className="text-sm text-gray-600">Ask questions about the client's Analysis and receive AI-powered insights.</p>
              
              {/* Instruction Text - Only show when no session selected */}
              {!selectedSessionForFeatures && (
                <div className="mb-6">
                  <p className="text-sm font-semibold text-gray-700">Choose an active client session to generate Analysis</p>
                </div>
              )}

              {selectedSessionForFeatures ? (
                <>
                  {/* Read-Only Notice for Completed Sessions */}
                  {isSessionCompleted() && (
                    <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-lg mb-4">
                      <div className="flex items-start gap-3">
                        <Lock className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div>
                          <h5 className="font-semibold text-amber-900">Session Completed - Read-Only Mode</h5>
                          <p className="text-sm text-amber-800">This session is complete. You can view existing queries but cannot ask new questions.</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* New Query Input - Only show if session not completed */}
                  {!isSessionCompleted() && (
                    <div className="space-y-4 border p-6 rounded-lg bg-gray-50">
                      <h4 className="font-semibold text-lg" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Ask a New Question</h4>
                      <Textarea
                        value={aiQuery}
                        onChange={(e) => setAiQuery(e.target.value)}
                        placeholder="Type your question here... (e.g., 'What are the main psychosocial stressors affecting this client?')"
                        className="min-h-[100px]"
                        disabled={queryLoading}
                        data-testid="ai-query-input"
                      />
                    
                    {/* File Upload Section */}
                    <div className="space-y-3 p-4 border border-dashed border-gray-300 rounded bg-white">
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-gray-700">
                          📎 Attach File (Optional)
                        </label>
                      </div>
                      <input
                        type="file"
                        accept=".txt,.pdf,.doc,.docx,.csv,.json"
                        onChange={(e) => setQueryFile(e.target.files[0])}
                        className="text-sm"
                        disabled={queryLoading}
                        data-testid="query-file-input"
                      />
                      {queryFile && (
                        <div className="flex items-center gap-3 p-2 bg-blue-50 rounded">
                          <span className="text-sm text-gray-700">📄 {queryFile.name}</span>
                          <button
                            onClick={() => setQueryFile(null)}
                            className="text-red-600 hover:text-red-800 text-xs"
                            disabled={queryLoading}
                          >
                            Remove
                          </button>
                        </div>
                      )}
                      {queryFile && (
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={includeFileInPrompt}
                            onChange={(e) => setIncludeFileInPrompt(e.target.checked)}
                            className="w-4 h-4"
                            disabled={queryLoading}
                            data-testid="include-file-checkbox"
                          />
                          <span className="text-gray-700">Include file content in AI analysis</span>
                        </label>
                      )}
                      <p className="text-xs text-gray-500">
                        💡 When enabled, the file content will be included in the prompt for AI triangulation and reference.
                      </p>
                    </div>
                    
                      <Button 
                        onClick={handleAskBuddySubmit} 
                        disabled={queryLoading || !aiQuery.trim()}
                        style={{ background: '#2563eb' }}
                        data-testid="submit-query-btn"
                      >
                        {queryLoading ? (
                          <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing Query...</>
                        ) : (
                          <>Send Query</>
                        )}
                      </Button>
                    </div>
                  )}

                  {/* Where to find your responses Info Box */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h5 className="font-semibold text-blue-900 mb-1">Where to find your AI responses</h5>
                        <p className="text-sm text-blue-800">
                          Once generated, the AI responses will appear in the <strong>AI Generated Analysis</strong> section 
                          of Phase 5 (Session view), where you can review, download, and control their visibility to the client.
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="p-8 text-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <Brain className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600 text-lg mb-2">No Active Session</p>
                  <p className="text-sm text-gray-500">Select a client session from the dropdown above to ask AI questions</p>
                </div>
              )}
            </div>
              )}

              {/* Research Framework Synthesis Sub-Content */}
              {phase5SubMenu === 'research-framework-synthesis' && isReportEnabled('research_framework_synthesis') && (
            <div className="space-y-6">
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                <FileSearch className="inline-block w-6 h-6 mr-2" />
                Research Framework Synthesis
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Evidence-based theoretical frameworks that illuminate your client's patterns, with verified APA citations to foundational research. 
                This feature searches academic literature for established theories (Joiner, Kakar, Hewitt & Flett, etc.) that explain your client's themes.
              </p>
              
              {/* Instruction Text - Only show when no session selected */}
              {!selectedSessionForFeatures && (
                <div className="mb-6">
                  <p className="text-sm font-semibold text-gray-700">Choose an Active client Session to generate APA Case Parallels</p>
                </div>
              )}

              {selectedSessionForFeatures ? (
                <>
                  {/* Read-Only Notice for Completed Sessions */}
                  {isSessionCompleted() && (
                    <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-lg mb-4">
                      <div className="flex items-start gap-3">
                        <Lock className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div>
                          <h5 className="font-semibold text-amber-900">Session Completed - Read-Only Mode</h5>
                          <p className="text-sm text-amber-800">This session is complete. You can view existing Analysis but cannot generate new ones.</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Generate Button - Only show if session not completed */}
                  {!isSessionCompleted() && (
                    <div className="p-6 bg-gradient-to-r from-teal-50 to-cyan-50 border-2 border-teal-300 rounded-xl">
                      <h4 className="font-bold text-xl mb-3" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0d9488' }}>
                        📚 Generate Research Framework Synthesis
                      </h4>
                      <p className="text-sm text-gray-700 mb-4">
                        This will search academic databases (OpenAlex, Semantic Scholar, PubMed) for established theoretical frameworks that explain your client's patterns. 
                        The Analysis includes verified APA citations, framework explanations, and clinical applications—no fabricated research.
                      </p>
                      
                      <div className="bg-white p-4 rounded-lg border border-teal-200 mb-4">
                        <p className="text-xs text-gray-600 mb-2"><strong>What you'll receive:</strong></p>
                        <ul className="text-xs text-gray-600 space-y-1 list-disc list-inside">
                          <li>Verified theoretical frameworks (e.g., Joiner, Kakar, Hewitt & Flett)</li>
                          <li>Full APA citations with DOIs</li>
                          <li>How each framework explains client patterns</li>
                          <li>Clinical applications and interventions</li>
                          <li>Honest about literature limitations</li>
                        </ul>
                        <p className="text-xs text-gray-500 mt-2 italic">
                          Searches 485M+ academic sources. Analysis based on core thematic data (Points 1-14, Analysis 1-4).
                        </p>
                      </div>
                      
                      <div className="flex justify-end gap-3">
                        <Button 
                          onClick={async () => {
                            try {
                              setGeneratingCustomReport(true);
                              const response = await axios.post(
                                `${API}/sessions/${selectedSessionForFeatures}/phase5/research-framework-synthesis`
                              );
                              
                              showModalNotification('success', 'Research Framework Synthesis Analysis generated successfully! Check the AI Generated Analysis section in Phase 5.');
                              
                              // Refresh phase 5 data
                              if (selectedSessionForFeatures) {
                                const phase5Response = await axios.get(`${API}/sessions/${selectedSessionForFeatures}/phase5`);
                                setPhase5Data(phase5Response.data);
                              }
                            } catch (error) {
                              console.error('Error generating research framework synthesis:', error);
                              showModalNotification('error', error.response?.data?.detail || 'Failed to generate research framework synthesis');
                            } finally {
                              setGeneratingCustomReport(false);
                            }
                          }}
                          disabled={generatingCustomReport}
                          style={{ background: '#0d9488' }}
                          className="text-white"
                        >
                          {generatingCustomReport ? (
                            <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating Analysis...</>
                          ) : (
                            <><FileSearch className="w-4 h-4 mr-2" /> Generate Research Framework Synthesis</>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Info about where to find the report */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <h5 className="font-semibold text-blue-900 mb-1">Where to find your Analysis</h5>
                        <p className="text-sm text-blue-800">
                          Once generated, the Research Framework Synthesis Analysis will appear in the <strong>AI Generated Analysis</strong> section 
                          of Phase 5 (Session view), where you can review, download, and control its visibility to the client.
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="p-8 text-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <FileSearch className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600 text-lg mb-2">No Active Session</p>
                  <p className="text-sm text-gray-500">Select a client session from the dropdown above to generate Research Framework Synthesis</p>
                </div>
              )}
            </div>
              )}

              {/* APA Case Parallels Sub-Content */}
              {phase5SubMenu === 'apa-case-parallels' && isReportEnabled('apa_case_parallels') && (
            <div className="space-y-6">
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                <FileSearch className="inline-block w-6 h-6 mr-2" />
                APA Case Parallels
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Discover published case studies with similar clinical patterns to your client. System searches free academic databases 
                (OpenAlex, Semantic Scholar, PubMed) and verifies each citation for authenticity and accessibility.
              </p>
              
              {/* Instruction Text - Only show when no session selected */}
              {!selectedSessionForFeatures && (
                <div className="mb-6">
                  <p className="text-sm font-semibold text-gray-700">Choose an Active client Session to search for case parallels</p>
                </div>
              )}

              {selectedSessionForFeatures ? (
                <>
                  {/* Read-Only Notice for Completed Sessions */}
                  {isSessionCompleted() && (
                    <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-lg mb-4">
                      <div className="flex items-start gap-3">
                        <Lock className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div>
                          <h5 className="font-semibold text-amber-900">Session Completed - Read-Only Mode</h5>
                          <p className="text-sm text-amber-800">This session is complete. You can view existing Analysis but cannot generate new ones.</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Generate Button - Only show if session not completed */}
                  {!isSessionCompleted() && (
                    <div className="flex justify-center">
                      <Button 
                        onClick={async () => {
                          if (!selectedSessionForFeatures) {
                            showModalNotification('error', 'Please select a session first');
                            return;
                          }
                          
                          setGeneratingReport(true);
                          try {
                            await axios.post(
                              `${API}/sessions/${selectedSessionForFeatures}/phase5/apa-case-parallels`
                            );
                            showModalNotification('success', 'APA Case Parallels Analysis generated successfully! Check the AI Generated Analysis section in Phase 5.');
                            await fetchPhase5DataForSession(selectedSessionForFeatures);
                          } catch (error) {
                            console.error('Error generating APA case parallels:', error);
                            showModalNotification('error', error.response?.data?.detail || 'Failed to generate APA case parallels');
                          } finally {
                            setGeneratingReport(false);
                          }
                        }}
                        disabled={generatingReport}
                        className="bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white px-8 py-3 text-base"
                      >
                        {generatingReport ? (
                          <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Searching Academic Databases...
                          </>
                        ) : (
                          <><FileSearch className="w-4 h-4 mr-2" /> Generate APA Case Parallels</>
                        )}
                      </Button>
                    </div>
                  )}

                  {/* Info about where to find the report */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <h5 className="font-semibold text-blue-900 mb-1">Where to find your case parallels</h5>
                        <p className="text-sm text-blue-800 mb-2">
                          Once generated, the APA Case Parallels Analysis will appear in the <strong>AI Generated Analysis</strong> section 
                          of Phase 5 (Session view), where you can review, download, and control its visibility to the client.
                        </p>
                        <div className="mt-3 p-3 bg-white rounded border border-blue-200">
                          <p className="text-xs font-semibold text-blue-900 mb-2">🔍 How it works:</p>
                          <ul className="text-xs text-blue-800 space-y-1">
                            <li>• Searches OpenAlex (250M works), Semantic Scholar (200M papers), PubMed (35M citations)</li>
                            <li>• Filters for case studies & qualitative research with thematic relevance</li>
                            <li>• Verifies each citation via CrossRef API for authenticity</li>
                            <li>• Returns only verified, accessible case studies</li>
                            <li>• If &lt;3 cases found: provides literature gaps analysis & alternative frameworks</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="p-8 text-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <FileSearch className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600 text-lg mb-2">No Active Session</p>
                  <p className="text-sm text-gray-500">Select a client session from the dropdown above to search for APA case parallels</p>
                </div>
              )}
            </div>
              )}


              {/* Developmental-Ecological-Cultural Analysis Sub-Content */}
              {phase5SubMenu === 'dev-eco-cultural' && isReportEnabled('developmental_ecological_cultural') && (
            <div className="space-y-6">
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                <Network className="inline-block w-6 h-6 mr-2" />
                Developmental-Ecological-Cultural Integration Analysis
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                A comprehensive multi-level analysis that situates the client's psychological patterns within developmental stage (Erikson), 
                ecological systems (Bronfenbrenner), cultural context, power dynamics, and interpersonal complementarity patterns.
              </p>
              
              {!selectedSessionForFeatures && (
                <div className="mb-6">
                  <p className="text-sm font-semibold text-gray-700">Choose an Active client Session to generate this analysis</p>
                </div>
              )}

              {selectedSessionForFeatures ? (
                <>
                  {isSessionCompleted() && (
                    <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-lg mb-4">
                      <div className="flex items-start gap-3">
                        <Lock className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div>
                          <h5 className="font-semibold text-amber-900">Session Completed - Read-Only Mode</h5>
                          <p className="text-sm text-amber-800">This session is complete. You can view existing Analysis but cannot generate new ones.</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {!isSessionCompleted() && (
                    <div className="flex justify-center">
                      <Button 
                        onClick={async () => {
                          if (!selectedSessionForFeatures) {
                            showModalNotification('error', 'Please select a session first');
                            return;
                          }
                          
                          setGeneratingReport(true);
                          try {
                            await axios.post(
                              `${API}/sessions/${selectedSessionForFeatures}/reports/developmental-ecological-cultural`
                            );
                            showModalNotification('success', 'Developmental-Ecological-Cultural Analysis generated successfully! Check the AI Generated Analysis section in Phase 5.');
                            await fetchPhase5DataForSession(selectedSessionForFeatures);
                          } catch (error) {
                            console.error('Error generating report:', error);
                            showModalNotification('error', error.response?.data?.detail || 'Failed to generate analysis');
                          } finally {
                            setGeneratingReport(false);
                          }
                        }}
                        disabled={generatingReport}
                        className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-3 text-base"
                      >
                        {generatingReport ? (
                          <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Generating Analysis...
                          </>
                        ) : (
                          <><Network className="w-4 h-4 mr-2" /> Generate Analysis</>
                        )}
                      </Button>
                    </div>
                  )}

                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <h5 className="font-semibold text-blue-900 mb-1">Comprehensive Multi-Level Analysis</h5>
                        <p className="text-sm text-blue-800 mb-2">
                          This analysis integrates multiple theoretical frameworks to provide holistic understanding of the client's experience.
                        </p>
                        <div className="mt-3 p-3 bg-white rounded border border-blue-200">
                          <p className="text-xs font-semibold text-blue-900 mb-2">📊 Framework Integration:</p>
                          <ul className="text-xs text-blue-800 space-y-1">
                            <li>• <strong>Developmental (Erikson):</strong> Psychosocial stage analysis and developmental tasks</li>
                            <li>• <strong>Ecological (Bronfenbrenner):</strong> Micro, meso, exo, macro, and chronosystem mapping</li>
                            <li>• <strong>Cultural & Power:</strong> Intersectional identity analysis and structural barriers</li>
                            <li>• <strong>Interpersonal (Complementarity):</strong> Relationship patterns and dynamics</li>
                            <li>• <strong>Multi-Level Interventions:</strong> Individual, interpersonal, systemic, cultural approaches</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
                  <Network className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600 text-lg mb-2">No Active Session</p>
                  <p className="text-sm text-gray-500">Select a client session from the dropdown above to generate this comprehensive analysis</p>
                </div>
              )}
            </div>
              )}


              {/* Book Recommendations Sub-Content */}
              {false && phase5SubMenu === 'book-recommendations' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-bold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  📚 Book Reflections
                </h3>
              </div>
              
              {/* Instruction Text - Only show when no session selected */}
              {!selectedSessionForFeatures && (
                <div className="mb-6">
                  <p className="text-sm font-semibold text-gray-700">Choose an active client session to generate Analysis</p>
                </div>
              )}

              {selectedSessionForFeatures ? (
                <>
                  {/* Read-Only Notice for Completed Sessions */}
                  {isSessionCompleted() && (
                    <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-lg mb-4">
                      <div className="flex items-start gap-3">
                        <Lock className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div>
                          <h5 className="font-semibold text-amber-900">Session Completed - Read-Only Mode</h5>
                          <p className="text-sm text-amber-800">This session is complete. You can view existing recommendations but cannot generate new ones.</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Generate Button - Only show if session not completed AND no recommendations exist */}
                  {!isSessionCompleted() && !bookRecommendations && !phase5Data?.book_recommendations && (
                    <div className="space-y-6">
                      {/* Main Generation Card - Teal/Cyan Style */}
                      <div className="bg-white border-2 border-cyan-400 rounded-lg p-6 space-y-4">
                        <div className="flex items-start gap-3">
                          <BookOpen className="w-6 h-6 text-cyan-600 mt-1" />
                          <div className="flex-1">
                            <h4 className="text-lg font-bold text-gray-900 mb-2">Generate Book Reflections</h4>
                            <p className="text-sm text-gray-700 mb-4">
                              AI-powered therapeutic reading suggestions tailored to your client's analysis data. The system analyzes comprehensive analysis results to recommend books that align with their therapeutic needs and personal growth journey.
                            </p>
                            
                            {/* What you'll receive section */}
                            <div className="bg-white rounded-lg p-4 mb-4">
                              <h5 className="font-semibold text-gray-900 mb-3">What you'll receive:</h5>
                              <ul className="space-y-2 text-sm text-gray-700">
                                <li className="flex items-start gap-2">
                                  <span className="text-cyan-600 mt-0.5">•</span>
                                  <span>3 carefully selected therapeutic books with full details</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <span className="text-cyan-600 mt-0.5">•</span>
                                  <span>Title, author, and publication information</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <span className="text-cyan-600 mt-0.5">•</span>
                                  <span>Why each book is relevant to this specific client</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <span className="text-cyan-600 mt-0.5">•</span>
                                  <span>Key themes and therapeutic applications</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <span className="text-cyan-600 mt-0.5">•</span>
                                  <span>Control over client visibility (share when ready)</span>
                                </li>
                              </ul>
                              <p className="text-xs text-gray-500 mt-3 italic">
                                Uses Why + Next Steps.
                              </p>
                            </div>

                            {/* Generate Button */}
                            <Button 
                              onClick={handleGenerateBookRecommendations}
                              disabled={generatingBooks || !phase5Data?.why_patterns?.content || !phase5Data?.next_steps?.content}
                              className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-semibold py-3"
                            >
                              {generatingBooks ? (
                                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating Book Reflections...</>
                              ) : (
                                <><BookOpen className="w-5 h-5 mr-2" /> Generate Book Reflections</>
                              )}
                            </Button>
                            
                            {(!phase5Data?.why_patterns?.content || !phase5Data?.next_steps?.content) && (
                              <p className="text-xs text-amber-600 mt-2 text-center">
                                Generate Why and Next Steps first.
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Where to find your report - Info Card */}
                      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-5">
                        <div className="flex items-start gap-3">
                          <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-white text-sm font-bold">ℹ️</span>
                          </div>
                          <div className="flex-1">
                            <h5 className="font-semibold text-blue-900 mb-2">Where to find your recommendations</h5>
                            <p className="text-sm text-blue-800">
                              Once generated, the Book Reflections will appear below in this section, where you can review each recommendation, share them with your client, or regenerate if needed for different suggestions.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Display Recommendations */}
                  {(bookRecommendations || phase5Data?.book_recommendations) && (
                    <div className="space-y-6">
                      {/* Header Section */}
                      <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6">
                        <h4 className="text-xl font-bold mb-3 text-green-900">📚 Recommended by:</h4>
                        <p className="text-sm text-gray-700 mb-2">{user?.name || 'Psychologist'}</p>
                        <p className="text-xs text-gray-500">{new Date().toLocaleString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}</p>
                        
                        {!isSessionCompleted() && (
                          <div className="mt-4 flex items-center gap-3 flex-wrap">
                            <Button
                              variant="outline"
                              onClick={handleToggleShareBooks}
                              size="sm"
                              className={bookRecsShared ? "bg-green-100 border-green-500 text-green-700" : ""}
                            >
                              {bookRecsShared ? '✓ Share with Client' : 'Share with Client'}
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => {
                                console.log('[REGENERATE BOOKS] Button clicked');
                                console.log('[REGENERATE BOOKS] Session:', selectedSessionForFeatures);
                                console.log('[REGENERATE BOOKS] Currently generating:', generatingBooks);
                                handleGenerateBookRecommendations();
                              }}
                              disabled={generatingBooks || !phase5Data?.why_patterns?.content || !phase5Data?.next_steps?.content}
                              size="sm"
                              className="bg-blue-600 text-white hover:bg-blue-700"
                            >
                              {generatingBooks ? (
                                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Regenerating...</>
                              ) : (
                                <>🔄 Regenerate Books</>
                              )}
                            </Button>
                            <span className="text-xs text-gray-600">
                              {bookRecsShared ? 'These recommendations are visible to the client' : 'Client cannot see these recommendations yet'}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {/* Book Recommendations - Display first 2 books (Legacy Format) */}
                      {(bookRecommendations?.book_recommendations || phase5Data?.book_recommendations?.book_recommendations || []).slice(0, 2).map((book, idx) => (
                        <div key={idx} className="border-2 border-gray-300 rounded-lg overflow-hidden shadow-lg">
                          {/* Book Header */}
                          <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <span className="text-2xl font-bold text-green-600">#{idx + 1}</span>
                                  <h5 className="text-xl font-bold text-gray-900">{book.title}</h5>
                                </div>
                                <p className="text-sm text-gray-700 mb-1">
                                  <strong>Author:</strong> {book.author} {book.year && `(${book.year})`}
                                </p>
                                <div className="flex items-center gap-4 text-xs text-gray-600">
                                  {book.estimated_reading_time && <span>⏱️ {book.estimated_reading_time}</span>}
                                  {book.relevance_score && (
                                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full font-semibold">
                                      {book.relevance_score}% Relevant
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Book Content */}
                          <div className="p-6 space-y-4 bg-white">
                            {/* Legacy Format Warning */}
                            {book.legacy_format_note && (
                              <div className="bg-amber-50 border-2 border-amber-400 p-4 rounded-lg">
                                <div className="flex items-start gap-3">
                                  <span className="text-2xl">⚠️</span>
                                  <div>
                                    <p className="text-sm font-semibold text-amber-900 mb-2">{book.legacy_format_note}</p>
                                    <button
                                      onClick={() => handleGenerateBookRecommendations()}
                                      className="mt-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-semibold"
                                    >
                                      🔄 Regenerate Recommendations
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            {/* Psychological Theme */}
                            {book.psychological_theme && (
                              <div className="bg-indigo-50 border-l-4 border-indigo-500 p-4 rounded">
                                <h6 className="font-semibold text-sm mb-2 flex items-center gap-2 text-indigo-900">
                                  <span>🧠</span> Psychological Theme
                                </h6>
                                <p className="text-sm text-gray-700 whitespace-pre-line">{book.psychological_theme}</p>
                              </div>
                            )}

                            {/* Why Recommended */}
                            {book.why_recommended && (
                              <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                                <h6 className="font-semibold text-sm mb-2 flex items-center gap-2 text-green-900">
                                  <span>💡</span> Why This Book?
                                </h6>
                                <p className="text-sm text-gray-700 whitespace-pre-line">{book.why_recommended}</p>
                              </div>
                            )}

                            {/* Therapeutic Value */}
                            {book.therapeutic_value && (
                              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                                <h6 className="font-semibold text-sm mb-2 flex items-center gap-2 text-blue-900">
                                  <span>🎯</span> Therapeutic Value
                                </h6>
                                <p className="text-sm text-gray-700 whitespace-pre-line">{book.therapeutic_value}</p>
                              </div>
                            )}

                            {/* Specific Fit for Client */}
                            {book.specific_fit_for_client && (
                              <div className="bg-teal-50 border-l-4 border-teal-500 p-4 rounded">
                                <h6 className="font-semibold text-sm mb-2 flex items-center gap-2 text-teal-900">
                                  <span>👤</span> Specific Fit for Client
                                </h6>
                                <p className="text-sm text-gray-700 whitespace-pre-line">{book.specific_fit_for_client}</p>
                              </div>
                            )}

                            {/* Themes */}
                            {((book.themes && book.themes.length > 0) || (book.relevant_themes && book.relevant_themes.length > 0)) && (
                              <div className="mt-3">
                                <h6 className="font-semibold text-sm mb-2 text-green-900">🎭 Themes:</h6>
                                <div className="flex flex-wrap gap-2">
                                  {(book.themes || book.relevant_themes || []).map((theme, tIdx) => (
                                    <span key={tIdx} className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                                      {theme}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* 2-Minute Verbal Summary */}
                            {book.two_minute_summary && Array.isArray(book.two_minute_summary) && book.two_minute_summary.length > 0 && (
                              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
                                <h6 className="font-semibold text-sm mb-2 flex items-center gap-2 text-yellow-900">
                                  <span>🗣️</span> 2-Minute Summary (Share with Client Verbally)
                                </h6>
                                <p className="text-xs text-yellow-800 mb-2 italic">Read these points aloud to your client during the session:</p>
                                <div className="space-y-2 text-sm text-yellow-900">
                                  {book.two_minute_summary.map((point, pIdx) => (
                                    <p key={pIdx} className="leading-relaxed">• {point}</p>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Therapeutic Techniques - Legacy Format */}
                            {book.therapeutic_techniques && Array.isArray(book.therapeutic_techniques) && book.therapeutic_techniques.length > 0 && (
                              <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-lg">
                                <h6 className="font-bold text-lg mb-4 text-blue-900">
                                  🎯 Implementation Guide (Legacy Format)
                                </h6>
                                <p className="text-sm text-gray-700 mb-4 italic">
                                  All therapeutic techniques mentioned by the author, formatted for clinical use
                                </p>
                                
                                {book.therapeutic_techniques.map((technique, tIdx) => (
                                  <div key={tIdx} className="bg-white p-6 rounded border-2 border-blue-200 mb-6 text-sm">
                                    {/* Technique Header */}
                                    <div className="border-b-2 border-blue-300 pb-3 mb-4">
                                      <h3 className="text-lg font-bold text-blue-900">
                                        TECHNIQUE {technique.technique_number}: {technique.technique_name}
                                      </h3>
                                    </div>
                                    
                                    {/* Purpose */}
                                    <div className="mb-4">
                                      <h4 className="font-bold text-blue-800 mb-2">PURPOSE & WHY IT WORKS:</h4>
                                      <p className="text-gray-800 leading-relaxed">{technique.purpose_and_why}</p>
                                    </div>
                                    
                                    {/* Steps */}
                                    {technique.steps && technique.steps.length > 0 && (
                                      <div className="mb-4">
                                        <h4 className="font-bold text-blue-800 mb-3">STEP-BY-STEP IMPLEMENTATION:</h4>
                                        <div className="space-y-3">
                                          {technique.steps.map((step, sIdx) => (
                                            <div key={sIdx} className="pl-4 border-l-2 border-blue-300">
                                              <p className="font-semibold text-gray-900 mb-2">Step {step.step_number}: {step.step_title}</p>
                                              <div className="pl-4 space-y-1 text-gray-700">
                                                <p>• <span className="font-medium">Example:</span> {step.example}</p>
                                                <p>• <span className="font-medium">Common mistake:</span> {step.common_mistake}</p>
                                                <p>• <span className="font-medium">Success indicator:</span> {step.success_indicator}</p>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Practical Application */}
                                    {technique.practical_application && (
                                      <div className="mb-4">
                                        <h4 className="font-bold text-blue-800 mb-2">PRACTICAL APPLICATION:</h4>
                                        <div className="grid grid-cols-2 gap-2 text-gray-700">
                                          <p>• <span className="font-medium">When to use:</span> {technique.practical_application.when_to_use}</p>
                                          <p>• <span className="font-medium">Frequency:</span> {technique.practical_application.frequency}</p>
                                          <p>• <span className="font-medium">Duration:</span> {technique.practical_application.duration}</p>
                                          <p>• <span className="font-medium">Best timing:</span> {technique.practical_application.best_timing}</p>
                                          <p>• <span className="font-medium">Tools needed:</span> {technique.practical_application.tools_needed}</p>
                                          <p>• <span className="font-medium">Setting:</span> {technique.practical_application.setting}</p>
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Expected Timeline */}
                                    {technique.expected_timeline && (
                                      <div className="mb-4">
                                        <h4 className="font-bold text-blue-800 mb-2">EXPECTED TIMELINE:</h4>
                                        <div className="pl-4 space-y-1 text-gray-700">
                                          <p>• <span className="font-medium">Initial results:</span> {technique.expected_timeline.initial_results}</p>
                                          <p>• <span className="font-medium">Significant progress:</span> {technique.expected_timeline.significant_progress}</p>
                                          <p>• <span className="font-medium">Full mastery:</span> {technique.expected_timeline.full_mastery}</p>
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Chapter Reference */}
                                    {technique.chapter_reference && (
                                      <div className="mt-4 pt-4 border-t border-blue-200">
                                        <p className="text-sm text-blue-700">
                                          <span className="font-bold">📖 Chapter Reference:</span> {technique.chapter_reference}
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                ))}
                                
                                <div className="mt-4 bg-blue-100 border border-blue-400 p-4 rounded">
                                  <p className="text-sm font-semibold text-blue-900 mb-2">📘 For Psychologists:</p>
                                  <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                                    <li>This legacy format includes ALL techniques mentioned by the author</li>
                                    <li>Each technique has detailed step-by-step implementation with examples</li>
                                    <li>Common mistakes and success indicators help guide client progress</li>
                                    <li>Practical application details provide session structure guidance</li>
                                    <li>Expected timelines help set realistic expectations with clients</li>
                                    <li>Chapter references allow you to study the source material</li>
                                  </ul>
                                </div>
                              </div>
                            )}

                            {/* Therapeutic Techniques - Object Format */}
                            {book.author_solutions && typeof book.author_solutions === 'object' && book.author_solutions.therapeutic_techniques && (
                              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                                <h6 className="font-bold text-base mb-3 text-blue-900">🎯 Therapeutic Techniques</h6>
                                <div className="space-y-4">
                                  {book.author_solutions.therapeutic_techniques.map((tech, tIdx) => (
                                    <div key={tIdx} className="bg-white p-4 rounded-lg border-2 border-blue-300">
                                      <h7 className="font-bold text-md text-blue-900 mb-2">{tech.name}</h7>
                                      {tech.description && <p className="text-sm text-gray-700 mb-3">{tech.description}</p>}
                                      {tech.steps && tech.steps.length > 0 && (
                                        <ol className="list-decimal list-inside space-y-1 text-sm text-gray-800 bg-gray-50 p-3 rounded">
                                          {tech.steps.map((step, sIdx) => (
                                            <li key={sIdx} className="pl-2">{step}</li>
                                          ))}
                                        </ol>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Key Chapters */}
                            {book.key_chapters && book.key_chapters.length > 0 && (
                              <div className="bg-gray-50 p-4 rounded">
                                <h6 className="font-semibold text-sm mb-2 text-gray-900">📖 Focus on These Chapters:</h6>
                                <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                                  {book.key_chapters.map((chapter, cIdx) => (
                                    <li key={cIdx}>{chapter}</li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Expected Outcomes */}
                            {book.expected_outcomes && Array.isArray(book.expected_outcomes) && book.expected_outcomes.length > 0 && (
                              <div className="bg-blue-50 p-4 rounded">
                                <h6 className="font-semibold text-sm mb-2 text-purple-900">🎯 Expected Outcomes:</h6>
                                <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                                  {book.expected_outcomes.map((outcome, oIdx) => (
                                    <li key={oIdx}>{outcome}</li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Download Links section removed as per user request */}

                            {/* Key Themes */}
                            {book.key_themes && book.key_themes.length > 0 && (
                              <div className="mt-3">
                                <h6 className="font-semibold text-sm mb-2 text-green-900">📖 Key Themes:</h6>
                                <div className="flex flex-wrap gap-2">
                                  {book.key_themes.map((theme, tIdx) => (
                                    <span key={tIdx} className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                                      {theme}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* View Full Details Button */}
                            <div className="mt-4 pt-4 border-t border-gray-200">
                              <button
                                onClick={() => {
                                  setSelectedBookForModal(book);
                                  setShowBookModal(true);
                                }}
                                className="w-full px-4 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-lg hover:from-green-700 hover:to-blue-700 transition-all font-semibold flex items-center justify-center gap-2"
                              >
                                <Eye className="w-5 h-5" />
                                View Full Details in Modal
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="p-8 text-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600 text-lg mb-2">No Active Session</p>
                  <p className="text-sm text-gray-500">Click "Work with Session" on a completed consultation to generate book recommendations</p>
                </div>
              )}
            </div>
              )}

              {/* Movie Recommendations Sub-Content */}
              {false && phase5SubMenu === 'movie-recommendations' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-bold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  🎬 Movie Reflections
                </h3>
              </div>
              
              {/* Instruction Text - Only show when no session selected */}
              {!selectedSessionForFeatures && (
                <div className="mb-6">
                  <p className="text-sm font-semibold text-gray-700">Choose an active client session to generate Analysis</p>
                </div>
              )}

              {selectedSessionForFeatures ? (
                <>
                  {/* Read-Only Notice for Completed Sessions */}
                  {isSessionCompleted() && (
                    <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-lg mb-4">
                      <div className="flex items-start gap-3">
                        <Lock className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div>
                          <h5 className="font-semibold text-amber-900">Session Completed - Read-Only Mode</h5>
                          <p className="text-sm text-amber-800">This session is complete. You can view existing recommendations but cannot generate new ones.</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Generate Button - Only show if session not completed AND no recommendations exist */}
                  {!isSessionCompleted() && !movieRecommendations && !phase5Data?.movie_recommendations && (
                    <div className="space-y-6">
                      {/* Main Generation Card - Teal/Cyan Style */}
                      <div className="bg-white border-2 border-cyan-400 rounded-lg p-6 space-y-4">
                        <div className="flex items-start gap-3">
                          <div className="w-6 h-6 text-cyan-600 mt-1">🎬</div>
                          <div className="flex-1">
                            <h4 className="text-lg font-bold text-gray-900 mb-2">Generate Movie Reflections</h4>
                            <p className="text-sm text-gray-700 mb-4">
                              AI-powered cinematic suggestions that resonate with your client's emotional journey. The system analyzes comprehensive analysis data to recommend films with therapeutic themes that can serve as conversation starters, healing tools, or sources of insight and validation.
                            </p>
                            
                            {/* What you'll receive section */}
                            <div className="bg-white rounded-lg p-4 mb-4">
                              <h5 className="font-semibold text-gray-900 mb-3">What you'll receive:</h5>
                              <ul className="space-y-2 text-sm text-gray-700">
                                <li className="flex items-start gap-2">
                                  <span className="text-cyan-600 mt-0.5">•</span>
                                  <span>3 carefully selected films with full details</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <span className="text-cyan-600 mt-0.5">•</span>
                                  <span>Title, year, and genre information</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <span className="text-cyan-600 mt-0.5">•</span>
                                  <span>Why each movie is therapeutically relevant to this client</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <span className="text-cyan-600 mt-0.5">•</span>
                                  <span>Key themes and emotional resonance explained</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <span className="text-cyan-600 mt-0.5">•</span>
                                  <span>How films can support therapeutic conversations</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <span className="text-cyan-600 mt-0.5">•</span>
                                  <span>Control over client visibility (share when ready)</span>
                                </li>
                              </ul>
                              <p className="text-xs text-gray-500 mt-3 italic">
                                Uses Why + Next Steps.
                              </p>
                            </div>

                            {/* Generate Button */}
                            <Button 
                              onClick={handleGenerateMovieRecommendations}
                              disabled={generatingMovies || !phase5Data?.why_patterns?.content || !phase5Data?.next_steps?.content}
                              className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-semibold py-3"
                            >
                              {generatingMovies ? (
                                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating Movie Reflections...</>
                              ) : (
                                <span className="flex items-center justify-center">🎬 Generate Movie Reflections</span>
                              )}
                            </Button>
                            {(!phase5Data?.why_patterns?.content || !phase5Data?.next_steps?.content) && (
                              <p className="text-xs text-amber-600 mt-2 text-center">Generate Why and Next Steps first.</p>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Where to find your report - Info Card */}
                      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-5">
                        <div className="flex items-start gap-3">
                          <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-white text-sm font-bold">ℹ️</span>
                          </div>
                          <div className="flex-1">
                            <h5 className="font-semibold text-blue-900 mb-2">Where to find your recommendations</h5>
                            <p className="text-sm text-blue-800">
                              Once generated, the Movie Reflections will appear below in this section, where you can review each film suggestion, share them with your client when therapeutically appropriate, or regenerate for different options.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Display Recommendations */}
                  {(movieRecommendations || phase5Data?.movie_recommendations) && (
                    <div className="space-y-6">
                      {/* Header Section */}
                      <div className="bg-blue-50 border-2 border-purple-200 rounded-lg p-6">
                        <h4 className="text-xl font-bold mb-3 text-purple-900">🎬 Recommended by:</h4>
                        <p className="text-sm text-gray-700 mb-2">{user?.name || 'Psychologist'}</p>
                        <p className="text-xs text-gray-500">{new Date().toLocaleString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}</p>
                        
                        {!isSessionCompleted() && (
                          <div className="mt-4 flex items-center gap-3 flex-wrap">
                            <Button
                              variant="outline"
                              onClick={handleToggleShareMovies}
                              size="sm"
                              className={movieRecsShared ? "bg-blue-100 border-purple-500 text-purple-700" : ""}
                            >
                              {movieRecsShared ? '✓ Share with Client' : 'Share with Client'}
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => {
                                console.log('[REGENERATE MOVIES] Button clicked');
                                console.log('[REGENERATE MOVIES] Session:', selectedSessionForFeatures);
                                console.log('[REGENERATE MOVIES] Currently generating:', generatingMovies);
                                handleGenerateMovieRecommendations();
                              }}
                              disabled={generatingMovies || !phase5Data?.why_patterns?.content || !phase5Data?.next_steps?.content}
                              size="sm"
                              className="bg-blue-600 text-white hover:bg-blue-700"
                            >
                              {generatingMovies ? (
                                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Regenerating...</>
                              ) : (
                                <>🔄 Regenerate Movies</>
                              )}
                            </Button>
                            <span className="text-xs text-gray-600">
                              {movieRecsShared ? 'These recommendations are visible to the client' : 'Client cannot see these recommendations yet'}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {/* Movie Recommendations */}
                      {(movieRecommendations?.movie_recommendations || phase5Data?.movie_recommendations?.movie_recommendations || []).map((movie, idx) => (
                        <div key={idx} className="border-2 border-gray-300 rounded-lg overflow-hidden shadow-lg">
                          {/* Movie Header */}
                          <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <span className="text-2xl font-bold text-blue-600">#{idx + 1}</span>
                                  <h5 className="text-xl font-bold text-gray-900">{movie.title}</h5>
                                </div>
                                <p className="text-sm text-gray-700 mb-1">
                                  <strong>Director:</strong> {movie.director} {movie.year && `(${movie.year})`}
                                </p>
                                {movie.genre && (
                                  <div className="flex items-center gap-2 text-xs text-gray-600 mt-1">
                                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full font-semibold">
                                      {movie.genre}
                                    </span>
                                    {movie.relevance_score && (
                                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full font-semibold">
                                        {movie.relevance_score}% Relevant
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Movie Content */}
                          <div className="p-6 space-y-4 bg-white">
                            {/* Description */}
                            {movie.description && (
                              <div>
                                <h6 className="font-semibold text-sm mb-2 flex items-center gap-2">
                                  <span className="text-blue-600">📝</span> Synopsis
                                </h6>
                                <p className="text-sm text-gray-700 whitespace-pre-line">{movie.description}</p>
                              </div>
                            )}

                            {/* Psychological Theme */}
                            {movie.psychological_theme && (
                              <div className="bg-indigo-50 border-l-4 border-indigo-500 p-4 rounded">
                                <h6 className="font-semibold text-sm mb-2 flex items-center gap-2 text-indigo-900">
                                  <span>🧠</span> Psychological Theme
                                </h6>
                                <p className="text-sm text-gray-700 whitespace-pre-line">{movie.psychological_theme}</p>
                              </div>
                            )}

                            {/* Why This Movie */}
                            {movie.why_recommended && (
                              <div className="bg-blue-50 border-l-4 border-purple-500 p-4 rounded">
                                <h6 className="font-semibold text-sm mb-2 flex items-center gap-2 text-purple-900">
                                  <span>💡</span> Why This Movie?
                                </h6>
                                <p className="text-sm text-gray-700 whitespace-pre-line">{movie.why_recommended}</p>
                              </div>
                            )}

                            {/* Therapeutic Value */}
                            {movie.therapeutic_value && (
                              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                                <h6 className="font-semibold text-sm mb-2 flex items-center gap-2 text-blue-900">
                                  <span>🎯</span> Therapeutic Value
                                </h6>
                                <p className="text-sm text-gray-700 whitespace-pre-line">{movie.therapeutic_value}</p>
                              </div>
                            )}

                            {/* Specific Fit for Client */}
                            {movie.specific_fit_for_client && (
                              <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                                <h6 className="font-semibold text-sm mb-2 flex items-center gap-2 text-green-900">
                                  <span>👤</span> Specific Fit for Client
                                </h6>
                                <p className="text-sm text-gray-700 whitespace-pre-line">{movie.specific_fit_for_client}</p>
                              </div>
                            )}

                            {/* Key Scenes to Discuss */}
                            {movie.key_scenes && Array.isArray(movie.key_scenes) && movie.key_scenes.length > 0 && (
                              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
                                <h6 className="font-semibold text-sm mb-2 flex items-center gap-2 text-yellow-900">
                                  <span>🎬</span> Key Scenes to Discuss with Client
                                </h6>
                                <ul className="text-sm text-gray-700 space-y-2 list-disc list-inside">
                                  {movie.key_scenes.map((scene, sIdx) => (
                                    <li key={sIdx} className="leading-relaxed">{scene}</li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Discussion Questions */}
                            {movie.discussion_questions && Array.isArray(movie.discussion_questions) && movie.discussion_questions.length > 0 && (
                              <div className="bg-green-50 p-4 rounded border border-green-200">
                                <h6 className="font-semibold text-sm mb-3 text-green-900">💬 Discussion Questions for Session:</h6>
                                <ol className="text-sm text-gray-700 space-y-2 list-decimal list-inside">
                                  {movie.discussion_questions.map((question, qIdx) => (
                                    <li key={qIdx} className="leading-relaxed pl-2">{question}</li>
                                  ))}
                                </ol>
                              </div>
                            )}

                            {/* Connection to Client */}
                            {movie.connection_to_client && (
                              <div className="bg-blue-50 p-4 rounded border border-blue-200">
                                <h6 className="font-semibold text-sm mb-2 text-blue-900">🔗 Connection to Client:</h6>
                                <p className="text-sm text-gray-700">{movie.connection_to_client}</p>
                              </div>
                            )}

                            {/* Relevant Themes - supports both 'themes' and 'relevant_themes' field names */}
                            {((movie.themes && movie.themes.length > 0) || (movie.relevant_themes && movie.relevant_themes.length > 0)) && (
                              <div className="mt-3">
                                <h6 className="font-semibold text-sm mb-2 text-purple-900">🎭 Themes:</h6>
                                <div className="flex flex-wrap gap-2">
                                  {(movie.themes || movie.relevant_themes || []).map((theme, tIdx) => (
                                    <span key={tIdx} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                                      {theme}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Viewing Instructions */}
                            {movie.viewing_instructions && (
                              <div className="bg-gray-50 p-4 rounded border border-gray-200">
                                <h6 className="font-semibold text-sm mb-2 text-gray-900">📺 Viewing Instructions:</h6>
                                <p className="text-sm text-gray-700">{movie.viewing_instructions}</p>
                              </div>
                            )}

                            {/* Expected Outcomes */}
                            {movie.expected_outcomes && Array.isArray(movie.expected_outcomes) && movie.expected_outcomes.length > 0 && (
                              <div className="bg-blue-50 p-4 rounded">
                                <h6 className="font-semibold text-sm mb-2 text-purple-900">🎯 Expected Outcomes:</h6>
                                <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                                  {movie.expected_outcomes.map((outcome, oIdx) => (
                                    <li key={oIdx}>{outcome}</li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Watch Links */}
                            {movie.watch_links && (
                              <div className="bg-blue-50 p-4 rounded border border-purple-200">
                                <h6 className="font-semibold text-sm mb-3 text-purple-900">📺 Where to Watch:</h6>
                                <div className="flex flex-wrap gap-3">
                                  {movie.watch_links.streaming && (
                                    <a href={movie.watch_links.streaming} target="_blank" rel="noopener noreferrer" 
                                       className="inline-flex items-center px-3 py-2 bg-white border border-purple-500 text-purple-700 rounded hover:bg-blue-100 text-xs">
                                      Streaming →
                                    </a>
                                  )}
                                  {movie.watch_links.imdb && (
                                    <a href={movie.watch_links.imdb} target="_blank" rel="noopener noreferrer"
                                       className="inline-flex items-center px-3 py-2 bg-white border border-purple-500 text-purple-700 rounded hover:bg-blue-100 text-xs">
                                      IMDb →
                                    </a>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="p-8 text-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
                  </svg>
                  <p className="text-gray-600 text-lg mb-2">No Active Session</p>
                  <p className="text-sm text-gray-500">Click "Work with Session" on a completed consultation to generate movie recommendations</p>
                </div>
              )}
            </div>
              )}

            </div>
          </TabsContent>


          {/* Client Feedback Detail Modal */}
          <Dialog open={showClientFeedbackModal} onOpenChange={setShowClientFeedbackModal}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Client Feedback Details</DialogTitle>
              </DialogHeader>
              {selectedClientFeedback && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-gray-600">Client Name</Label>
                      <p className="text-base font-medium">{selectedClientFeedback.user_name}</p>
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600">Category</Label>
                      <p className="text-base font-medium">{selectedClientFeedback.category}</p>
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600">Rating</Label>
                      <p className="text-base font-medium">
                        {selectedClientFeedback.rating ? '⭐'.repeat(selectedClientFeedback.rating) : 'No rating'}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600">Date</Label>
                      <p className="text-base font-medium">{new Date(selectedClientFeedback.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm text-gray-600">Client Message</Label>
                    <p className="text-base p-3 bg-gray-50 rounded-lg mt-1">{selectedClientFeedback.message}</p>
                  </div>
                  <form onSubmit={handleReplyToClientFeedback} className="space-y-4">
                    <div>
                      <Label htmlFor="reply">Your Reply</Label>
                      <Textarea
                        id="reply"
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Type your reply to the client..."
                        rows={5}
                        required
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" onClick={() => {
                        setShowClientFeedbackModal(false);
                        setReplyText('');
                        setSelectedClientFeedback(null);
                      }}>
                        Cancel
                      </Button>
                      <Button type="submit" disabled={replyingToFeedback}>
                        {replyingToFeedback ? 'Sending...' : 'Send Reply'}
                      </Button>
                    </div>
                  </form>
                </div>
              )}
            </DialogContent>
          </Dialog>
      </Tabs>

      {/* Report Visibility Settings Modal */}
      <Dialog open={showReportSettingsModal} onOpenChange={setShowReportSettingsModal}>
        <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Analysis Visibility Settings
            </DialogTitle>
            <p className="text-sm text-gray-600 mt-2">
              Control which Analysis {selectedSessionForSettings?.client_name} can see
            </p>
          </DialogHeader>
          <div className="space-y-3 py-4 overflow-y-auto flex-1" style={{ maxHeight: 'calc(80vh - 180px)' }}>
            {/* Master Toggle */}
            <div className="flex items-center justify-between p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
              <div>
                <label className="font-semibold text-base">Share ALL Analysis with Client</label>
                <p className="text-xs text-gray-600 mt-1">Toggle to share/hide all Analysis at once</p>
              </div>
              <input
                type="checkbox"
                checked={reportVisibility.shared_with_client}
                onChange={(e) => setReportVisibility({...reportVisibility, shared_with_client: e.target.checked})}
                className="w-6 h-6 text-blue-600 cursor-pointer"
              />
            </div>
            
            <div className="border-t pt-2 mt-2">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Individual Analysis Controls</p>
              <p className="text-xs text-gray-500 mb-3">Fine-tune which specific Analysis is visible (only works if "Share ALL" is enabled above)</p>
            </div>
            
            <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition">
              <div>
                <label className="font-medium text-sm">Exploring Client Feelings</label>
                <p className="text-xs text-gray-500">Client's emotional experiences and feelings</p>
              </div>
              <input
                type="checkbox"
                checked={reportVisibility.show_exploring_client_feelings}
                onChange={(e) => setReportVisibility({...reportVisibility, show_exploring_client_feelings: e.target.checked})}
                className="w-5 h-5 text-blue-600 cursor-pointer"
              />
            </div>
            
            <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition">
              <div>
                <label className="font-medium text-sm">Emotional Balance Snapshot</label>
                <p className="text-xs text-gray-500">Emotional state and balance analysis</p>
              </div>
              <input
                type="checkbox"
                checked={reportVisibility.show_emotional_balance_snapshot}
                onChange={(e) => setReportVisibility({...reportVisibility, show_emotional_balance_snapshot: e.target.checked})}
                className="w-5 h-5 text-blue-600 cursor-pointer"
              />
            </div>
            
            <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition">
              <div>
                <label className="font-medium text-sm">Strengths and Coping Guide</label>
                <p className="text-xs text-gray-500">Client's strengths and coping mechanisms</p>
              </div>
              <input
                type="checkbox"
                checked={reportVisibility.show_strengths_and_coping_guide}
                onChange={(e) => setReportVisibility({...reportVisibility, show_strengths_and_coping_guide: e.target.checked})}
                className="w-5 h-5 text-blue-600 cursor-pointer"
              />
            </div>
            
            <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition">
              <div>
                <label className="font-medium text-sm">Deep Dive into Inner World</label>
                <p className="text-xs text-gray-500">Client's internal psychological landscape</p>
              </div>
              <input
                type="checkbox"
                checked={reportVisibility.show_deep_dive_inner_world}
                onChange={(e) => setReportVisibility({...reportVisibility, show_deep_dive_inner_world: e.target.checked})}
                className="w-5 h-5 text-blue-600 cursor-pointer"
              />
            </div>
            
            <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition">
              <div>
                <label className="font-medium text-sm">Client Synthesis</label>
                <p className="text-xs text-gray-500">Comprehensive psychological analysis</p>
              </div>
              <input
                type="checkbox"
                checked={reportVisibility.show_ai_summary_report}
                onChange={(e) => setReportVisibility({...reportVisibility, show_ai_summary_report: e.target.checked})}
                className="w-5 h-5 text-blue-600 cursor-pointer"
              />
            </div>
            
            <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition">
              <div>
                <label className="font-medium text-sm">Metaphorical Client Portrait</label>
                <p className="text-xs text-gray-500">Symbolic narrative representation</p>
              </div>
              <input
                type="checkbox"
                checked={reportVisibility.show_metaphorical_client_portrait}
                onChange={(e) => setReportVisibility({...reportVisibility, show_metaphorical_client_portrait: e.target.checked})}
                className="w-5 h-5 text-blue-600 cursor-pointer"
              />
            </div>
          </div>
          
          {/* Custom Reports Section */}
          {customReports.length > 0 && (
            <div className="space-y-3 py-4 border-t">
              <div className="pt-2">
                <p className="text-xs font-semibold text-blue-600 uppercase mb-2">Custom Analysis</p>
              </div>
              
              {customReports.map((report, idx) => {
                // Create unique visibility key using index and sanitized name
                const sanitizedName = report.report_name
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, '_')  // Replace all non-alphanumeric with underscore
                  .replace(/^_+|_+$/g, '');      // Remove leading/trailing underscores
                const visibilityKey = `show_custom_${idx}_${sanitizedName}`;
                
                return (
                  <div key={idx} className="flex items-center justify-between p-3 border border-purple-200 rounded-lg hover:bg-blue-50 transition">
                    <div className="flex-1">
                      <label className="font-medium text-sm">{analysisDisplayText(report.report_name)}</label>
                      <p className="text-xs text-gray-500">{report.custom_focus?.substring(0, 60)}...</p>
                      <p className="text-xs text-blue-600 mt-1">Generated: {new Date(report.generated_at).toLocaleDateString()}</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={reportVisibility[visibilityKey] !== false}
                      onChange={(e) => setReportVisibility({...reportVisibility, [visibilityKey]: e.target.checked})}
                      className="w-5 h-5 text-blue-600 cursor-pointer"
                    />
                  </div>
                );
              })}
            </div>
          )}
          
          <div className="flex justify-end gap-3 pt-4 border-t bg-white">
            <Button variant="outline" onClick={() => setShowReportSettingsModal(false)}>
              Cancel
            </Button>
            <Button onClick={saveReportVisibility}>
              Save Settings
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ask Buddy Query Modal - View Full Query & Response */}
      {showReportModal && selectedReport && (
        <Dialog open={showReportModal} onOpenChange={setShowReportModal}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{analysisDisplayText(selectedReport.report_name)}</DialogTitle>
            </DialogHeader>
            <div className="whitespace-pre-wrap text-sm">{selectedReport.content}</div>
          </DialogContent>
        </Dialog>
      )}

      {/* Book Recommendations Detail Modal */}
      {bookRecommendations && (
        <Dialog open={false}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Book Reflections</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {(bookRecommendations?.book_recommendations || phase5Data?.book_recommendations?.book_recommendations || []).slice(0, 3).map((book, idx) => (
                <div key={idx} className="p-4 border rounded-lg">
                  <h5 className="font-bold text-lg">{book.title}</h5>
                  <p className="text-gray-700 italic">by {book.author}</p>
                  <p className="text-gray-600 mt-2">{book.description}</p>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Movie Recommendations Detail Modal */}
      {movieRecommendations && (
        <Dialog open={false}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Movie Reflections</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {(movieRecommendations?.movie_recommendations || phase5Data?.movie_recommendations?.movie_recommendations || []).map((movie, idx) => (
                <div key={idx} className="p-4 border rounded-lg">
                  <h5 className="font-bold text-lg">{movie.title}</h5>
                  <p className="text-gray-700 italic">Directed by {movie.director} ({movie.year})</p>
                  <p className="text-gray-600 mt-2">{movie.description}</p>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Custom Report Generation Modal */}
      <Dialog open={showCustomReportModal} onOpenChange={setShowCustomReportModal}>
        <DialogContent className="max-w-[700px]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Generate Custom Clinical Analysis
            </DialogTitle>
            <p className="text-sm text-gray-600 mt-2">
              Create a comprehensive Analysis with automatic inclusion of risk analysis, clinical disclaimer, and APA citations.
            </p>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 bg-blue-50 border border-purple-300 rounded-lg">
              <p className="text-sm font-semibold text-purple-900 mb-2">✨ Automatically Included:</p>
              <ul className="text-xs text-blue-800 space-y-1">
                <li>• Comprehensive Risk Analysis (suicide/self-harm evaluation)</li>
                <li>• Clinical Disclaimer and Methodology</li>
                <li>• APA Citations and Evidence-Based References</li>
                <li>• Data Triangulation (All Phases + 14 Points SQIS)</li>
              </ul>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2">Analysis Name *</label>
              <input
                type="text"
                value={customReportName}
                onChange={(e) => setCustomReportName(e.target.value)}
                placeholder="e.g., Trauma Analysis, Attachment Analysis, Coping Mechanisms Review"
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={generatingCustomReport}
              />
            </div>
            
            <div>
              <label className="block text-sm font-semibold mb-2">Analysis Focus *</label>
              <textarea
                value={customReportFocus}
                onChange={(e) => setCustomReportFocus(e.target.value)}
                placeholder="Describe what you want to analyze...

Examples:
• Analyze patterns of trauma responses and their manifestations
• Evaluate attachment styles and relational dynamics
• Assess coping mechanisms and their effectiveness
• Explore identity themes and self-perception patterns"
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[180px] text-sm"
                disabled={generatingCustomReport}
              />
              <p className="text-xs text-gray-500 mt-2">
                💡 Focus on your specific analysis needs - risk analysis, disclaimer, and citations are added automatically
              </p>
            </div>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowCustomReportModal(false);
                setCustomReportName('');
                setCustomReportFocus('');
              }}
              disabled={generatingCustomReport}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleGenerateCustomReport}
              disabled={generatingCustomReport || !customReportName.trim() || !customReportFocus.trim()}
              style={{ background: '#2563eb' }}
              className="text-white"
            >
              {generatingCustomReport ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating...
                </>
              ) : (
                <>Generate Analysis</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Error Modal for Custom Report Generation */}
      <Dialog open={showErrorModal} onOpenChange={setShowErrorModal}>
        <DialogContent className="max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              <AlertCircle className="h-6 w-6" />
              Custom Analysis Generation Failed
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <div className="p-4 bg-red-50 border border-red-300 rounded-lg mb-4">
              <p className="text-sm text-red-900 font-semibold mb-2">❌ Error Details:</p>
              <p className="text-sm text-red-800 whitespace-pre-wrap">{errorMessage}</p>
            </div>
            
            <div className="p-4 bg-blue-50 border border-blue-300 rounded-lg">
              <p className="text-sm font-semibold text-blue-900 mb-2">💡 Possible Solutions:</p>
              <ul className="text-xs text-blue-800 space-y-1">
                <li>• Ensure the client has completed all phases</li>
                <li>• Verify that 14 synthesis points have been generated</li>
                <li>• Check your internet connection</li>
                <li>• Try again in a few moments</li>
                <li>• If the issue persists, contact support</li>
              </ul>
            </div>
          </div>
          
          <div className="flex justify-end">
            <Button 
              onClick={() => setShowErrorModal(false)}
              style={{ background: '#2563EB' }}
              className="text-white"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    
      
      <NotificationModal
        isOpen={notification.isOpen}
        onClose={closeNotification}
        type={notification.type}
        title={notification.title}
        message={notification.message}
      />

      {/* Feedback Detail Modal */}
      <Dialog open={showFeedbackDetailModal} onOpenChange={setShowFeedbackDetailModal}>
        <DialogContent className="max-w-[700px]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>Feedback Details</DialogTitle>
          </DialogHeader>
          
          {selectedFeedback && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded">
                <div>
                  <Label className="text-sm text-gray-600">Name</Label>
                  <p className="font-medium">{selectedFeedback.user_name}</p>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Role</Label>
                  <p className="font-medium capitalize">{selectedFeedback.user_role}</p>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Category</Label>
                  <p className="font-medium">{selectedFeedback.category}</p>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Date</Label>
                  <p className="font-medium">{new Date(selectedFeedback.created_at).toLocaleDateString()}</p>
                </div>
                {selectedFeedback.rating && (
                  <div>
                    <Label className="text-sm text-gray-600">Rating</Label>
                    <p className="font-medium">{'⭐'.repeat(selectedFeedback.rating)}</p>
                  </div>
                )}
              </div>
              
              <div>
                <Label className="text-sm text-gray-600">Your Message</Label>
                <div className="mt-2 p-4 bg-white border border-gray-300 rounded-lg">
                  <p className="text-sm whitespace-pre-wrap">{selectedFeedback.message}</p>
                </div>
              </div>
              
              {selectedFeedback.admin_reply ? (
                <div>
                  <Label className="text-sm text-gray-600">Admin Reply</Label>
                  <div className="mt-2 p-4 bg-blue-50 border border-blue-300 rounded-lg">
                    <p className="text-sm text-blue-900 whitespace-pre-wrap">{selectedFeedback.admin_reply}</p>
                    <p className="text-xs text-blue-600 mt-2">
                      Replied on: {new Date(selectedFeedback.admin_reply_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-yellow-50 border border-yellow-300 rounded-lg">
                  <p className="text-sm text-yellow-800">Admin has not replied to this feedback yet.</p>
                </div>
              )}
              
              <div className="flex justify-end">
                <Button onClick={() => setShowFeedbackDetailModal(false)} style={{ background: '#2563EB' }}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteFeedbackModal} onOpenChange={setShowDeleteFeedbackModal}>
        <DialogContent className="max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="text-red-600" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Delete Feedback
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-gray-700 mb-4">
              Are you sure you want to delete this feedback? This action cannot be undone.
            </p>
            
            {selectedFeedback && (
              <div className="p-3 bg-gray-50 border border-gray-300 rounded-lg">
                <p className="text-xs text-gray-600 mb-1">Category: {selectedFeedback.category}</p>
                <p className="text-sm text-gray-800 line-clamp-3">{selectedFeedback.message}</p>
              </div>
            )}
          </div>
          
          <div className="flex justify-end gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowDeleteFeedbackModal(false)}
              disabled={deletingFeedback}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleConfirmDeleteFeedback}
              disabled={deletingFeedback}
            >
              {deletingFeedback ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reschedule Consultation Modal */}
      <Dialog open={showRescheduleModal} onOpenChange={setShowRescheduleModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-blue-600 flex items-center gap-2">
              <CalendarCheck className="w-6 h-6" /> Reschedule Consultation
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {rescheduleConsultation && (
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="text-sm font-semibold text-blue-900 mb-1">Current Details:</p>
                <div className="text-sm text-blue-800">
                  <p><span className="font-medium">Client:</span> {rescheduleConsultation.client_name}</p>
                  {rescheduleConsultation.consultation_date && (
                    <div>
                      <p><span className="font-medium">Current Date:</span> {new Date(rescheduleConsultation.consultation_date).toLocaleDateString()}</p>
                      <p><span className="font-medium">Current Time:</span> {new Date(rescheduleConsultation.consultation_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div>
              <Label>New Date & Time *</Label>
              <Input
                type="datetime-local"
                value={rescheduleDate}
                onChange={(e) => setRescheduleDate(e.target.value)}
                min={getMinDateTime()}
                required
                className="mt-1"
              />
              <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                <p className="text-xs text-blue-800">
                  ⏰ <span className="font-semibold">Minimum notice required:</span> 1 hour from now
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  📅 Earliest allowed time: {new Date(getMinDateTime()).toLocaleString('en-US', { 
                    weekday: 'short', 
                    month: 'short', 
                    day: 'numeric', 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </p>
              </div>
            </div>

            <div>
              <Label>Reason for Rescheduling (Optional)</Label>
              <Textarea
                value={rescheduleReason}
                onChange={(e) => setRescheduleReason(e.target.value)}
                placeholder="E.g., Personal emergency, scheduling conflict..."
                rows={3}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Client will be notified of the change</p>
            </div>

            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <p className="text-xs text-orange-800">
                <span className="font-semibold">⚠️ Note:</span> The client will receive a notification about this schedule change. Please ensure the new time works for both parties.
              </p>
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowRescheduleModal(false);
                setRescheduleConsultation(null);
                setRescheduleDate('');
                setRescheduleReason('');
              }}
              disabled={processingConsultation}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmReschedule}
              disabled={!rescheduleDate || processingConsultation}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {processingConsultation ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Rescheduling...
                </>
              ) : (
                'Confirm Reschedule'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Consultation Modal */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-red-600 flex items-center gap-2">
              <AlertCircle className="w-6 h-6" /> Cancel Consultation
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {cancelConsultation && (
              <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                <p className="text-sm font-semibold text-red-900 mb-1">Consultation Details:</p>
                <div className="text-sm text-red-800">
                  <p><span className="font-medium">Client:</span> {cancelConsultation.client_name}</p>
                  {cancelConsultation.consultation_date && (
                    <div>
                      <p><span className="font-medium">Scheduled Date:</span> {new Date(cancelConsultation.consultation_date).toLocaleDateString()}</p>
                      <p><span className="font-medium">Scheduled Time:</span> {new Date(cancelConsultation.consultation_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div>
              <Label>Reason for Cancellation *</Label>
              <Textarea
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder="Please provide a reason for cancelling this consultation (e.g., personal emergency, unavailability, client request)..."
                rows={4}
                className="mt-1"
                required
              />
              <p className="text-xs text-gray-500 mt-1">This reason will be shared with the client</p>
            </div>

            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <p className="text-xs text-orange-800">
                <span className="font-semibold">⚠️ Important:</span> The client will receive a notification about this cancellation along with your reason. They will be able to book another appointment.
              </p>
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowCancelModal(false);
                setCancelConsultation(null);
                setCancellationReason('');
              }}
              disabled={processingConsultation}
            >
              Keep Consultation
            </Button>
            <Button 
              onClick={handleConfirmCancellation}
              disabled={!cancellationReason.trim() || !!processingConsultation}
              className="bg-red-600 hover:bg-red-700"
            >
              {processingConsultation ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Cancelling...
                </>
              ) : (
                'Confirm Cancellation'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Psychologist Approval Warning Modal */}
      <Dialog open={showApprovalWarning} onOpenChange={setShowApprovalWarning}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-blue-600 flex items-center gap-2">
              <AlertCircle className="w-6 h-6" /> New Session Approval
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {pendingApprovalConsultation?.consultation && (
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <p className="text-sm font-semibold text-blue-900 mb-2">
                  You are about to approve a follow-up consultation request:
                </p>
                <div className="space-y-2 text-sm text-blue-800">
                  <div className="flex justify-between">
                    <span className="font-medium">Client:</span>
                    <span className="font-bold">{pendingApprovalConsultation.consultation.client_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Type:</span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">🔄 Follow-up Session</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Requested:</span>
                    <span>{new Date(pendingApprovalConsultation.consultation.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-3 text-sm text-gray-700">
              <p className="font-semibold text-gray-800">This approval will:</p>
              <div className="space-y-2 ml-2">
                <div className="flex gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span>Create a new therapy session for this client</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span>Assign you as the psychologist for this session</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span>Notify the client to begin their analysis</span>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm font-semibold text-blue-900 mb-2">Please ensure:</p>
              <div className="space-y-1 text-sm text-orange-800 ml-2">
                <div className="flex gap-2">
                  <span>✓</span>
                  <span>You have capacity for this client</span>
                </div>
                <div className="flex gap-2">
                  <span>✓</span>
                  <span>Professional boundaries are maintained</span>
                </div>
                <div className="flex gap-2">
                  <span>✓</span>
                  <span>Timely review and response to client submissions</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 mt-4">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">Note:</span> Approving this creates a professional therapeutic relationship with ongoing responsibilities.
              </p>
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowApprovalWarning(false);
                setPendingApprovalConsultation(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleProceedApproval}
              disabled={processingConsultation}
              className="bg-green-600 hover:bg-green-700"
            >
              {processingConsultation ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Approving...
                </>
              ) : (
                'Approve & Create Session'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Client Question Modal */}
      <Dialog open={showEditClientQuestionDialog} onOpenChange={setShowEditClientQuestionDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Client Question</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateClientQuestion} className="space-y-4">
            <div>
              <Label htmlFor="edit-client-question-subject">Subject</Label>
              <Input
                id="edit-client-question-subject"
                value={editClientQuestionSubject}
                onChange={(e) => setEditClientQuestionSubject(e.target.value)}
                placeholder="Brief summary of the question"
                required
              />
            </div>

            <div>
              <Label htmlFor="edit-client-question-message">Question Message</Label>
              <Textarea
                id="edit-client-question-message"
                value={editClientQuestionMessage}
                onChange={(e) => setEditClientQuestionMessage(e.target.value)}
                placeholder="Client's question in detail..."
                rows={6}
                required
              />
            </div>

            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <p className="text-xs text-blue-800">
                <span className="font-semibold">ℹ️ Note:</span> You can edit the client's initial question subject and message. Your own replies can be edited individually within the thread.
              </p>
            </div>

            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowEditClientQuestionDialog(false);
                  setEditingClientQuestion(null);
                  setEditClientQuestionSubject('');
                  setEditClientQuestionMessage('');
                }}
                disabled={updatingClientQuestion}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={updatingClientQuestion}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {updatingClientQuestion ? 'Updating...' : 'Update Question'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      </>
      ) : (
        <Card>
            <CardContent className="py-12 text-center">
              <Clock className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Dashboard Access Restricted</h3>
              <p className="text-gray-600">
                You need admin approval to access the full dashboard. Please wait for approval notification.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
      
      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteConfirmModal} onOpenChange={setShowDeleteConfirmModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-700">Are you sure you want to delete this feedback? This action cannot be undone.</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowDeleteConfirmModal(false);
                setFeedbackToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDeleteFeedback}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* End Chat Confirmation Modal */}
      <Dialog open={showEndChatModal} onOpenChange={setShowEndChatModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>End Chat?</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-700">
              Are you sure you want to end this chat? No more messages can be sent after ending the chat.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => {
              setShowEndChatModal(false);
              setChatToEnd(null);
            }}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleEndChat} 
              disabled={endingChat}
              className="bg-red-600 hover:bg-red-700"
            >
              {endingChat ? 'Ending...' : 'End Chat'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Consent Modal */}
      <ConsentModal 
        isOpen={showConsentModal} 
        onConsentGiven={() => setShowConsentModal(false)} 
      />

      {/* DPDPA Video Call Consent Modal */}
      {/* Transcription modal removed */}

      <DPDPAVideoCallConsent
        isOpen={showVideoConsentModal}
        onAccept={handleVideoConsentAccept}
        onDecline={handleVideoConsentDecline}
      />

      {/* Modal Notification System */}
      {notificationModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              {notificationModal.type === 'success' && (
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-3">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              )}
              {notificationModal.type === 'error' && (
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
              )}
              {notificationModal.type === 'info' && (
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                  <Info className="w-6 h-6 text-blue-600" />
                </div>
              )}
              <h3 className="text-lg font-semibold text-gray-900">
                {notificationModal.type === 'success' && 'Success'}
                {notificationModal.type === 'error' && 'Error'}
                {notificationModal.type === 'info' && 'Information'}
              </h3>
            </div>
            <p className="text-gray-600 mb-6">{notificationModal.message}</p>
            <div className="flex justify-end">
              <button
                onClick={closeModalNotification}
                className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700 transition"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Book Details Modal */}
      {showBookModal && selectedBookForModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-green-600 to-blue-600 text-white p-6 rounded-t-lg">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold mb-2">{selectedBookForModal.title}</h2>
                  <p className="text-green-100">By {selectedBookForModal.author} {selectedBookForModal.year && `(${selectedBookForModal.year})`}</p>
                </div>
                <button
                  onClick={() => setShowBookModal(false)}
                  className="text-white hover:text-gray-200 transition"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              {/* Why Recommended */}
              {selectedBookForModal.why_recommended && (
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-lg mb-2 text-green-900">💡 Why This Book?</h3>
                  <p className="text-gray-700 whitespace-pre-line">{selectedBookForModal.why_recommended}</p>
                </div>
              )}

              {/* 2-Minute Summary */}
              {selectedBookForModal.two_minute_summary && Array.isArray(selectedBookForModal.two_minute_summary) && selectedBookForModal.two_minute_summary.length > 0 && (
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-lg mb-2 text-yellow-900">🗣️ 2-Minute Summary</h3>
                  <div className="space-y-2">
                    {selectedBookForModal.two_minute_summary.map((point, idx) => (
                      <p key={idx} className="text-gray-700">• {point}</p>
                    ))}
                  </div>
                </div>
              )}

              {/* Key Chapters */}
              {selectedBookForModal.key_chapters && selectedBookForModal.key_chapters.length > 0 && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-lg mb-2 text-blue-900">📖 Key Chapters</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    {selectedBookForModal.key_chapters.map((chapter, idx) => (
                      <li key={idx}>{chapter}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Key Themes */}
              {selectedBookForModal.key_themes && selectedBookForModal.key_themes.length > 0 && (
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-gray-900">📚 Key Themes</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedBookForModal.key_themes.map((theme, idx) => (
                      <span key={idx} className="px-3 py-2 bg-blue-100 text-blue-800 rounded-full text-sm">
                        {theme}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Download Links removed as per user request */}
            </div>

            {/* Modal Footer */}
            <div className="p-6 bg-gray-50 rounded-b-lg flex justify-end">
              <button
                onClick={() => setShowBookModal(false)}
                className="px-6 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Client History Modal */}
      {showClientHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Modal Header */}
            <div className="p-6 border-b bg-gradient-to-r from-blue-600 to-indigo-600">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white">Client's Old Analysis & Sessions</h2>
                  <p className="text-blue-100 mt-1">Client: {selectedClientForHistory}</p>
                </div>
                <button
                  onClick={() => setShowClientHistoryModal(false)}
                  className="text-white hover:text-gray-200 transition"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {loadingClientHistory ? (
                <div className="flex justify-center items-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                  <span className="ml-3 text-gray-600">Loading history...</span>
                </div>
              ) : clientHistory.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-lg">No previous sessions found for this client</p>
                  <p className="text-gray-400 text-sm mt-2">This is their first session with you</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {clientHistory.map((session, idx) => (
                    <Card key={session.id} className="border-2 hover:border-blue-300 transition">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3">
                              <h3 className="text-lg font-bold text-gray-900">
                                Session #{clientHistory.length - idx}
                              </h3>
                              {session.expired_at && (
                                <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-semibold">
                                  ⏰ Expired
                                </span>
                              )}
                              <span className="px-3 py-1 bg-green-100 text-green-700 text-xs rounded-full font-semibold">
                                ✓ Completed
                              </span>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div>
                                <span className="text-gray-500">Created:</span>
                                <span className="ml-2 font-medium">
                                  {new Date(session.created_at).toLocaleDateString('en-US', { 
                                    month: 'short', 
                                    day: 'numeric', 
                                    year: 'numeric' 
                                  })}
                                </span>
                              </div>
                              {session.consultation_info?.completed_at && (
                                <div>
                                  <span className="text-gray-500">Consultation Date:</span>
                                  <span className="ml-2 font-medium">
                                    {new Date(session.consultation_info.completed_at).toLocaleDateString('en-US', { 
                                      month: 'short', 
                                      day: 'numeric', 
                                      year: 'numeric' 
                                    })}
                                  </span>
                                </div>
                              )}
                              {session.psychologist_name && (
                                <div>
                                  <span className="text-gray-500">Psychologist:</span>
                                  <span className="ml-2 font-medium">{session.psychologist_name}</span>
                                </div>
                              )}
                              {session.reports_generated_at && (
                                <div>
                                  <span className="text-gray-500">Analysis Generated:</span>
                                  <span className="ml-2 font-medium">
                                    {new Date(session.reports_generated_at).toLocaleDateString('en-US', { 
                                      month: 'short', 
                                      day: 'numeric', 
                                      year: 'numeric' 
                                    })}
                                  </span>
                                </div>
                              )}
                            </div>

                            {session.expiry_reason && (
                              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-sm text-blue-800">
                                  <span className="font-semibold">Expiry Reason:</span> {session.expiry_reason}
                                </p>
                              </div>
                            )}

                            <div className="mt-4 flex items-center gap-3">
                              {session.has_synthesis && (
                                <div className="flex items-center gap-1 text-sm text-green-600">
                                  <CheckCircle className="w-4 h-4" />
                                  <span>14 Points SQIS</span>
                                </div>
                              )}
                              {session.has_ai_reports && (
                                <div className="flex items-center gap-1 text-sm text-blue-600">
                                  <FileText className="w-4 h-4" />
                                  <span>AI Analysis Available</span>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="ml-4">
                            <Button
                              onClick={() => {
                                setShowClientHistoryModal(false);
                                navigate(`/session/${session.id}`);
                              }}
                              className="bg-blue-600 hover:bg-blue-700"
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              View Analysis
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-6 bg-gray-50 border-t flex justify-end">
              <Button
                onClick={() => setShowClientHistoryModal(false)}
                variant="outline"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Thematic Threads Over Time Report Modal */}
      {showThematicThreadsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b p-6 flex items-center justify-between z-10">
              <div>
                <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Thematic Threads Over Time
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Track how a psychological theme evolves across multiple INSIGHT analyses
                </p>
              </div>
              <button
                onClick={handleCloseThematicThreadsModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {!showThematicReportResult ? (
                <div className="space-y-6">
                  {/* Client Selection - Pre-selected from Active Session */}
                  <div>
                    <Label htmlFor="thematic-client" className="text-base font-semibold mb-2 block">
                      Client (From Active Session)
                    </Label>
                    <div className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700">
                      {sessions.find(s => s.id === selectedSessionForFeatures)?.client_name || 'Loading...'}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      <Info className="w-3 h-3 inline mr-1" />
                      Analyzing all completed sessions for this client (minimum 2 required)
                    </p>
                  </div>

                  {/* Theme Selection */}
                  <div>
                    <Label htmlFor="thematic-theme" className="text-base font-semibold mb-2 block">
                      Select Psychological Theme
                    </Label>
                    <Select value={selectedTheme} onValueChange={setSelectedTheme}>
                      <SelectTrigger id="thematic-theme" className="w-full">
                        <SelectValue placeholder="Choose a theme to analyze" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {psychologicalThemes.map((theme) => (
                          <SelectItem key={theme} value={theme}>
                            {theme}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-2">
                      The Analysis will track how this theme evolved across all completed sessions
                    </p>
                  </div>

                  {/* Info Box */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-blue-800">
                        <p className="font-semibold mb-1">About Thematic Threads Analysis:</p>
                        <ul className="list-disc list-inside space-y-1 text-xs">
                          <li>Pure narrative format - no scores or numbers</li>
                          <li>Uses only 14 Points SQIS data (excluding Points 15 & 16)</li>
                          <li>Tracks qualitative evolution: client language, worldview, coping patterns</li>
                          <li>Identifies shifts in meaning-making and emotional tone</li>
                          <li>Provides clinical implications for continued exploration</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Generate Button */}
                  <div className="flex justify-end gap-3 pt-4">
                    <Button
                      onClick={handleCloseThematicThreadsModal}
                      variant="outline"
                      disabled={generatingThematicReport}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleGenerateThematicThreads}
                      disabled={!selectedThematicClient || !selectedTheme || generatingThematicReport}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {generatingThematicReport ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generating Analysis...
                        </>
                      ) : (
                        <>
                          <FileText className="w-4 h-4 mr-2" />
                          Generate Analysis
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Report Content */}
                  <div className="bg-gray-50 border rounded-lg p-6">
                    <div 
                      className="prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{
                        __html: thematicReportContent.replace(/\n/g, '<br/>')
                      }}
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex justify-between items-center pt-4">
                    <Button
                      onClick={() => {
                        setShowThematicReportResult(false);
                        setThematicReportContent('');
                      }}
                      variant="outline"
                    >
                      Generate New Analysis
                    </Button>
                    <div className="flex gap-3">
                      <Button
                        onClick={handleDownloadThematicThreadsPDF}
                        variant="outline"
                        className="border-green-600 text-green-600 hover:bg-green-50"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download PDF
                      </Button>
                      <Button
                        onClick={handleCloseThematicThreadsModal}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        Close
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Persistent Patterns Spotlight Modal */}
      {showPersistentPatternsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-6 flex items-center justify-between z-10">
              <div>
                <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Persistent Patterns Spotlight
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Identify recurring themes and distinguish core challenges from situational stressors
                </p>
              </div>
              <button onClick={handleClosePersistentPatternsModal} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              {!showPersistentReportResult ? (
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="persistent-client" className="text-base font-semibold mb-2 block">
                      Client (From Active Session)
                    </Label>
                    <div className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700">
                      {sessions.find(s => s.id === selectedSessionForFeatures)?.client_name || 'Loading...'}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      <Info className="w-3 h-3 inline mr-1" />
                      Analyzing all completed sessions (Phase 1-4 data + synthesis points 2-14) for persistent patterns (minimum 2 sessions required)
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="persistent-pattern" className="text-base font-semibold mb-2 block">
                      Select Persistent Pattern
                    </Label>
                    <Select value={selectedPersistentPattern} onValueChange={setSelectedPersistentPattern}>
                      <SelectTrigger id="persistent-pattern" className="w-full">
                        <SelectValue placeholder="Choose a pattern to analyze" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {persistentPatterns.map((pattern) => (
                          <SelectItem key={pattern} value={pattern}>
                            {pattern}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-2">
                      The Analysis will identify how this pattern persists and evolves across analyses
                    </p>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-blue-800">
                        <p className="font-semibold mb-1">About Persistent Patterns Spotlight:</p>
                        <ul className="list-disc list-inside space-y-1 text-xs">
                          <li>Uses Phase 1, 2, 3, 4 data + client language + session notes</li>
                          <li>Includes moral reasoning patterns (Phase 2) and value evolution</li>
                          <li>Fallback: Uses 14 Points SQIS Points 2-14 (excludes 1, 15, 16)</li>
                          <li>Session-by-session pattern blocks with specific formatting</li>
                          <li>Distinguishes core challenges from situational stressors</li>
                          <li>Highlights evolution in client's internal stance and values</li>
                          <li>Provides targeted clinical recommendations</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button onClick={handleClosePersistentPatternsModal} variant="outline" disabled={generatingPersistentReport}>
                      Cancel
                    </Button>
                    <Button
                      onClick={handleGeneratePersistentPatterns}
                      disabled={!selectedPersistentPattern || generatingPersistentReport}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {generatingPersistentReport ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generating Analysis...
                        </>
                      ) : (
                        <>
                          <FileText className="w-4 h-4 mr-2" />
                          Generate Analysis
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-gray-50 border rounded-lg p-6">
                    <div 
                      className="prose prose-sm max-w-none font-mono text-xs whitespace-pre-wrap"
                      dangerouslySetInnerHTML={{
                        __html: persistentReportContent.replace(/\n/g, '<br/>')
                      }}
                    />
                  </div>

                  <div className="flex justify-between items-center pt-4">
                    <Button
                      onClick={() => {
                        setShowPersistentReportResult(false);
                        setPersistentReportContent('');
                      }}
                      variant="outline"
                    >
                      Generate New Analysis
                    </Button>
                    <div className="flex gap-3">
                      <Button
                        onClick={handleDownloadPersistentPatternsPDF}
                        variant="outline"
                        className="border-green-600 text-green-600 hover:bg-green-50"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download PDF
                      </Button>
                      <Button onClick={handleClosePersistentPatternsModal} className="bg-blue-600 hover:bg-blue-700">
                        Close
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PsychologistDashboard;
